import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { FaSearch, FaBell, FaUser, FaEllipsisV, FaPlus, FaTrash, FaEdit, FaUsers, FaSchool, FaGraduationCap, FaUserFriends, FaNewspaper, FaCalendarAlt, FaUtensils, FaImage, FaVideo, FaTachometerAlt, FaBuilding, FaUserCog, FaFileAlt, FaDatabase, FaCloudUploadAlt, FaChartPie, FaBus, FaTruck } from 'react-icons/fa';
import { collection, getDocs, getDoc, query, where, orderBy, limit, addDoc, deleteDoc, doc, setDoc, updateDoc, onSnapshot, Timestamp, writeBatch, serverTimestamp, FieldValue, deleteField } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { db, auth, storage } from '../firebase';
import { OrganizationMigration } from '../utils/organizationMigration';
import CloudinaryUpload from './CloudinaryUpload';
import GoogleSheetsTemplateSection from './GoogleSheetsTemplateSection';
import TimetableEditor from './TimetableEditor';
import TimetableManagementScreen from './TimetableManagementScreen';
import CreateOrgWizard, { CreateOrgPayload, OrgKind, LorryModeKind } from './CreateOrgWizard';
import { cloudinaryService } from '../utils/cloudinary';
import { storageNotificationService } from '../utils/storageNotificationService';
import { fetchCancellations, markCancellationDone, resendActivation, CancellationItem } from '../services/paymentsApi';
import { customToast } from '../utils/customToast';
import './Dashboard.css';
import '../admin.css';
import { purgeOrganization } from '../services/orgLifecycle';
import {
  buildBusOrgCode,
  buildLorryOrgCode,
  fleetRolesForProductType,
  isBusTrackingOrg,
  isCargoOrg,
  isLorryOrg,
  isLorryPersonalOrg,
  orgTypeBadgeLabel,
} from '../fleet/fleetConstants';

// Custom Toast Component
const CustomToast = {
  show: (message: string, type: 'success' | 'error' | 'info' | 'warning' = 'info') => {
    const toast = document.createElement('div');
    toast.className = `custom-toast ${type}`;
    toast.textContent = message;

    // Add styles
    toast.style.cssText = `
      position: fixed;
      top: 20px;
      right: 20px;
      background: ${type === 'success' ? '#43BE83' : type === 'error' ? '#EA8F95' : type === 'warning' ? '#FFA726' : '#5041BC'};
      color: white;
      padding: 12px 20px;
      border-radius: 8px;
      font-family: 'Nunito', sans-serif;
      font-weight: 600;
      z-index: 9999;
      box-shadow: 0 4px 12px rgba(0,0,0,0.15);
      transform: translateX(100%);
      transition: transform 0.3s ease;
    `;

    document.body.appendChild(toast);

    // Animate in
    setTimeout(() => {
      toast.style.transform = 'translateX(0)';
    }, 100);

    // Remove after 3 seconds
    setTimeout(() => {
      toast.style.transform = 'translateX(100%)';
      setTimeout(() => {
        document.body.removeChild(toast);
      }, 300);
    }, 3000);
  }
};

// Custom Confirmation Modal Component
const CustomConfirm = {
  show: (message: string, title: string = 'Confirm Action'): Promise<boolean> => {
    return new Promise((resolve) => {
      const modal = document.createElement('div');
      modal.className = 'custom-confirm-overlay';
      modal.style.cssText = `
        position: fixed;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        background: rgba(0, 0, 0, 0.5);
        display: flex;
        align-items: center;
        justify-content: center;
        z-index: 10000;
        font-family: 'Nunito', sans-serif;
      `;

      const modalContent = document.createElement('div');
      modalContent.className = 'custom-confirm-modal';
      modalContent.style.cssText = `
        background: white;
        border-radius: 16px;
        padding: 32px;
        max-width: 400px;
        width: 90%;
        text-align: center;
        box-shadow: 0 20px 40px rgba(0, 0, 0, 0.2);
        transform: scale(0.9);
        opacity: 0;
        transition: all 0.3s ease;
      `;

      const titleEl = document.createElement('h3');
      titleEl.textContent = title;
      titleEl.style.cssText = `
        margin: 0 0 16px 0;
        font-size: 20px;
        font-weight: 700;
        color: #303972;
      `;

      const messageEl = document.createElement('p');
      messageEl.textContent = message;
      messageEl.style.cssText = `
        margin: 0 0 24px 0;
        font-size: 16px;
        color: #4A4C60;
        line-height: 1.5;
      `;

      const buttonContainer = document.createElement('div');
      buttonContainer.style.cssText = `
        display: flex;
        gap: 12px;
        justify-content: center;
      `;

      const cancelBtn = document.createElement('button');
      cancelBtn.textContent = 'Cancel';
      cancelBtn.style.cssText = `
        padding: 12px 24px;
        border: 2px solid #E2DEF9;
        background: white;
        color: #8487A2;
        border-radius: 8px;
        font-size: 14px;
        font-weight: 600;
        cursor: pointer;
        transition: all 0.2s ease;
        font-family: 'Nunito', sans-serif;
      `;

      const confirmBtn = document.createElement('button');
      confirmBtn.textContent = 'Delete';
      confirmBtn.style.cssText = `
        padding: 12px 24px;
        border: none;
        background: #EA8F95;
        color: white;
        border-radius: 8px;
        font-size: 14px;
        font-weight: 600;
        cursor: pointer;
        transition: all 0.2s ease;
        font-family: 'Nunito', sans-serif;
      `;

      // Hover effects
      cancelBtn.onmouseenter = () => {
        cancelBtn.style.background = '#F5F3FF';
        cancelBtn.style.borderColor = '#5041BC';
        cancelBtn.style.color = '#5041BC';
      };
      cancelBtn.onmouseleave = () => {
        cancelBtn.style.background = 'white';
        cancelBtn.style.borderColor = '#E2DEF9';
        cancelBtn.style.color = '#8487A2';
      };

      confirmBtn.onmouseenter = () => {
        confirmBtn.style.background = '#D67A80';
        confirmBtn.style.transform = 'scale(1.05)';
      };
      confirmBtn.onmouseleave = () => {
        confirmBtn.style.background = '#EA8F95';
        confirmBtn.style.transform = 'scale(1)';
      };

      // Event handlers
      const closeModal = (result: boolean) => {
        modalContent.style.transform = 'scale(0.9)';
        modalContent.style.opacity = '0';
        modal.style.background = 'rgba(0, 0, 0, 0)';

        setTimeout(() => {
          document.body.removeChild(modal);
          resolve(result);
        }, 300);
      };

      cancelBtn.onclick = () => closeModal(false);
      confirmBtn.onclick = () => closeModal(true);

      // Close on overlay click
      modal.onclick = (e) => {
        if (e.target === modal) {
          closeModal(false);
        }
      };

      // Escape key
      const handleEscape = (e: KeyboardEvent) => {
        if (e.key === 'Escape') {
          closeModal(false);
          document.removeEventListener('keydown', handleEscape);
        }
      };
      document.addEventListener('keydown', handleEscape);

      // Assemble modal
      buttonContainer.appendChild(cancelBtn);
      buttonContainer.appendChild(confirmBtn);
      modalContent.appendChild(titleEl);
      modalContent.appendChild(messageEl);
      modalContent.appendChild(buttonContainer);
      modal.appendChild(modalContent);
      document.body.appendChild(modal);

      // Animate in
      setTimeout(() => {
        modalContent.style.transform = 'scale(1)';
        modalContent.style.opacity = '1';
      }, 100);
    });
  }
};

interface User {
  id: string;
  displayName: string;
  email: string;
  role: string;
  classGroup?: string;
  classCode?: string;
  institution?: string;
  avatarUrl?: string;
  createdAt: any;
  phone?: string;
  children?: string[];
  childAvatars?: string[];
  organizationCode?: string; // Standardized organization code (school code)
  schoolCode?: string; // Legacy field for backward compatibility
  parentName?: string; // Added for parent-child relationship
  parentId?: string; // Added for parent-child relationship
  associatedClassroomId?: string; // Added for mobile app compatibility
}

interface OrgAddon {
  wanted: boolean;
  status: 'default' | 'connected' | 'off';
  verified?: boolean;
  apiUrl?: string;
  provider?: string;
  apiKey?: string;
  extraFields?: Record<string, string>;
}

interface Organization {
  id: string;
  name: string;
  code: string;
  type: 'school' | 'university' | 'institution';
  productType?: 'FULL_FEATURE' | 'BUS_ONLY' | 'LORRY' | 'SELF_TRACKING' | 'CARGO';
  address?: string;
  phone?: string;
  email?: string;
  createdAt: any;
  studentCount: number;
  teacherCount: number;
  parentCount: number;
  expirationDate?: any;
  isActive?: boolean;
  subscriptionPlan?: 'premium' | 'enterprise' | 'basic';
  maxStudents?: number;
  maxTeachers?: number;
  maxParents?: number;
  subscriptionStatus?: 'active' | 'expired' | 'suspended' | 'canceled' | 'pending_payment';
  hasActiveSubscription?: boolean;
  gracePeriodUntil?: any;
  billingPeriod?: 'term' | 'termly' | 'month' | 'year';
  /** Admin asked to cancel — no further billing; org closes at period end. */
  cancelAtPeriodEnd?: boolean;
  // Self-serve signup fields
  selfServe?: boolean;
  pendingActivation?: boolean;
  adminEmail?: string;
  addons?: Record<string, OrgAddon>;
  plannedChildren?: number;
  plannedBuses?: number;
  // Pilot / billing follow-up surfacing in the owner console.
  isPilot?: boolean;
  pilotEndsAt?: any;
  paymentFailures?: number;
}

interface Post {
  id: string;
  authorId: string;
  authorName: string;
  authorHandle: string;
  authorAvatar: string;
  classId: string;
  textContent: string;
  imageUrl?: string;
  videoUrl?: string;
  likes: string[];
  comments: any[];
  timestamp: any;
  organizationCode?: string;
}

interface MenuItem {
  id: string;
  name: string;
  description: string;
  category: string;
  imageUrl: string;
  rating: number;
  ratingCount: number;
  date: any;
  uploadedBy: string;
  createdAt: any;
  nutritionalInfo: any;
  organizationCode?: string;
}

interface Event {
  id: string;
  title: string;
  description: string;
  date: any;
  time?: string;
  location?: string;
  imageUrl?: string;
  createdBy: string;
  createdAt: any;
  organizationCode?: string;
  status?: 'upcoming' | 'past' | 'cancelled';
  attendees?: string[];
  maxAttendees?: number;
  isPublic?: boolean;
  category?: string;
}

/** Platform-console feed of cancellation requests ("X canceled — follow up").
 *  Backend-gated to platform admins; renders nothing when the feed is empty. */
const ConsoleCancellations: React.FC = () => {
  const [items, setItems] = useState<CancellationItem[]>([]);
  const [busy, setBusy] = useState<string | null>(null);
  const [showDone, setShowDone] = useState(false);
  const [loadFailed, setLoadFailed] = useState(false);

  const load = () => fetchCancellations().then((r) => { setLoadFailed(!r.ok); setItems(r.items); });
  useEffect(() => { load(); }, []);

  const open = items.filter((i) => !i.followUpDone);
  const done = items.filter((i) => i.followUpDone);

  if (loadFailed) {
    return (
      <div style={{ background: '#fff7f7', border: '1px solid #fecaca', borderRadius: 12, padding: '10px 16px', margin: '0 0 18px', fontSize: 13, color: '#b91c1c', display: 'flex', gap: 12, alignItems: 'center' }}>
        <span>Couldn't load cancellation follow-ups.</span>
        <button onClick={load} style={{ background: 'none', border: '1px solid #fca5a5', borderRadius: 6, padding: '4px 10px', fontSize: 12, fontWeight: 700, color: '#b91c1c', cursor: 'pointer' }}>Retry</button>
      </div>
    );
  }
  if (items.length === 0) return null;

  async function markDone(id: string) {
    setBusy(id);
    const ok = await markCancellationDone(id);
    if (ok) setItems((prev) => prev.map((i) => (i.id === id ? { ...i, followUpDone: true } : i)));
    else customToast.show('Could not update — please try again.', 'error');
    setBusy(null);
  }

  const fmtDate = (iso: string | null) =>
    iso ? new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : '—';

  return (
    <div style={{ background: '#fff7f7', border: '1px solid #fecaca', borderRadius: 12, padding: '14px 18px', margin: '0 0 18px' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: open.length ? 10 : 0 }}>
        <strong style={{ fontSize: 14, color: '#991b1b' }}>
          Cancellations {open.length > 0 ? `· ${open.length} to follow up` : '· all followed up'}
        </strong>
        {done.length > 0 && (
          <button onClick={() => setShowDone((s) => !s)}
            style={{ background: 'none', border: 'none', fontSize: 12, color: '#b91c1c', cursor: 'pointer', textDecoration: 'underline' }}>
            {showDone ? 'Hide handled' : `Show handled (${done.length})`}
          </button>
        )}
      </div>
      {(showDone ? items : open).map((c) => (
        <div key={c.id} style={{ display: 'flex', alignItems: 'flex-start', gap: 10, padding: '8px 0', borderTop: '1px solid #fee2e2', opacity: c.followUpDone ? 0.55 : 1 }}>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: '#7f1d1d' }}>
              {c.orgName} <span style={{ fontWeight: 500, color: '#b91c1c' }}>({c.orgCode})</span>
              {c.plan ? <span style={{ fontWeight: 500 }}> · {c.plan}</span> : null}
            </div>
            <div style={{ fontSize: 12, color: '#9f1239', marginTop: 2 }}>
              {c.reason || 'No reason given'} · requested {fmtDate(c.at)}
              {c.requestedBy ? ` by ${c.requestedBy}` : ''} · access until {fmtDate(c.accessUntil)}
            </div>
          </div>
          {!c.followUpDone && (
            <button onClick={() => markDone(c.id)} disabled={busy === c.id}
              style={{ flexShrink: 0, fontSize: 12, fontWeight: 700, color: '#166534', background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 6, padding: '5px 10px', cursor: 'pointer' }}>
              {busy === c.id ? '…' : 'Followed up'}
            </button>
          )}
        </div>
      ))}
    </div>
  );
};

/** Console-only: re-send the sign-in link to a PAID-but-unclaimed (pending) org. */
const PendingResendButton: React.FC<{ orgCode: string }> = ({ orgCode }) => {
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  async function go() {
    setBusy(true);
    const r = await resendActivation(orgCode);
    setBusy(false);
    if (r.ok) { setSent(true); customToast.show(`Invite re-sent${r.email ? ` to ${r.email}` : ''}.`, 'success'); }
    else customToast.show(r.message || 'Could not resend the invite.', 'error');
  }
  return (
    <button onClick={go} disabled={busy || sent}
      style={{ marginTop: 6, fontSize: 11, fontWeight: 700, color: sent ? '#166534' : '#92400e', background: sent ? '#f0fdf4' : '#fffbeb', border: `1px solid ${sent ? '#bbf7d0' : '#fcd34d'}`, borderRadius: 6, padding: '3px 9px', cursor: busy || sent ? 'default' : 'pointer' }}>
      {busy ? 'Sending…' : sent ? '✓ Invite re-sent' : 'Resend invite email'}
    </button>
  );
};

const Dashboard: React.FC = () => {
  const { currentUser, logout, isFleetAdmin, fleetOrgCode } = useAuth();
  const navigate = useNavigate();
  const [users, setUsers] = useState<User[]>([]);
  const [allUsers, setAllUsers] = useState<User[]>([]);
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [posts, setPosts] = useState<Post[]>([]);
  const [allPosts, setAllPosts] = useState<Post[]>([]);
  const [menuItems, setMenuItems] = useState<MenuItem[]>([]);
  const [allMenuItems, setAllMenuItems] = useState<MenuItem[]>([]);
  const [batchMenus, setBatchMenus] = useState<any[]>([]);
  const [allBatchMenus, setAllBatchMenus] = useState<any[]>([]);
  const [events, setEvents] = useState<Event[]>([]);
  const [allEvents, setAllEvents] = useState<Event[]>([]);
  const [batchEvents, setBatchEvents] = useState<any[]>([]);
  const [allBatchEvents, setAllBatchEvents] = useState<any[]>([]);
  const [diaryEntries, setDiaryEntries] = useState<any[]>([]);
  const [allDiaryEntries, setAllDiaryEntries] = useState<any[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedFilter, setSelectedFilter] = useState('All users');
  const [selectedTab, setSelectedTab] = useState('dashboard');
  const [deletedOrganizations, setDeletedOrganizations] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(true);
  const [showAddOrganization, setShowAddOrganization] = useState(false);
  // Seeds the CreateOrgWizard's pre-selected kind when opened from a context-specific button.
  const [wizardInitialKind, setWizardInitialKind] = useState<OrgKind>('FULL_FEATURE');
  const [wizardInitialLorryMode, setWizardInitialLorryMode] = useState<LorryModeKind>('SELF_TRACKING');
  const [showCreatePost, setShowCreatePost] = useState(false);
  const [showCreateEvent, setShowCreateEvent] = useState(false);
  const [eventCreationMode, setEventCreationMode] = useState<'single' | 'batch'>('single');
  const [showCreateMenuItem, setShowCreateMenuItem] = useState(false);
  const [showCreateDiaryEntry, setShowCreateDiaryEntry] = useState(false);
  const [showDiaryClassroomSelection, setShowDiaryClassroomSelection] = useState(false);
  const [isLoadingClasses, setLoadingClasses] = useState(false);
  const [showTimetableModal, setShowTimetableModal] = useState(false);
  const [showEditOrganization, setShowEditOrganization] = useState(false);
  const [editingOrganization, setEditingOrganization] = useState<Organization | null>(null);
  const [selectedOrganization, setSelectedOrganization] = useState<string>('all');
  // State for organization classrooms modal
  const [showClassroomsModal, setShowClassroomsModal] = useState(false);
  const [selectedOrgForClassrooms, setSelectedOrgForClassrooms] = useState<Organization | null>(null);
  const [organizationClassrooms, setOrganizationClassrooms] = useState<any[]>([]);
  const [loadingClassrooms, setLoadingClassrooms] = useState(false);

  // Timetable management state
  const [selectedClassroom, setSelectedClassroom] = useState<any>(null);
  const [showTimetableManagement, setShowTimetableManagement] = useState(false);
  const [showTimetableManagementScreen, setShowTimetableManagementScreen] = useState(false);
  const [classroomEvents, setClassroomEvents] = useState<any[]>([]);
  const [loadingEvents, setLoadingEvents] = useState(false);

  // Timetable upload state
  const [showTimetableUploadModal, setShowTimetableUploadModal] = useState(false);
  const [showTimetableEditor, setShowTimetableEditor] = useState(false);
  const [timetableUploadTarget, setTimetableUploadTarget] = useState<{ type: 'classroom' | 'yearGroup', value: string, classrooms: any[], name?: string } | null>(null);
  const [timetableCSVData, setTimetableCSVData] = useState<any[]>([]);
  const [timetableUploadPreview, setTimetableUploadPreview] = useState<any>(null);
  const [selectedTerm, setSelectedTerm] = useState<string>('');
  const [selectedWeekNumber, setSelectedWeekNumber] = useState<number>(1);
  const [isUploadingTimetable, setIsUploadingTimetable] = useState(false);

  // Holiday management state
  const [holidays, setHolidays] = useState<any[]>([]);
  const [showAddHolidayModal, setShowAddHolidayModal] = useState(false);
  const [newHoliday, setNewHoliday] = useState({ date: '', name: '', type: 'public_holiday' });

  // Weekly summary state
  const [showWeeklySummaryModal, setShowWeeklySummaryModal] = useState(false);
  const [isGeneratingWeeklySummary, setIsGeneratingWeeklySummary] = useState(false);

  // Fleet admin one-time code (PEAR admin generates for BUS_ONLY orgs)
  const [fleetAdminCodeModal, setFleetAdminCodeModal] = useState<{ org: Organization; code: string; expiresAt: number } | null>(null);

  // Add-ons connector modal

  // User menu (PEAR Admin dropdown with logout)
  const [showUserMenu, setShowUserMenu] = useState(false);

  // Add event modal state
  const [showAddEventModal, setShowAddEventModal] = useState(false);
  const [addEventMode, setAddEventMode] = useState<'single' | 'bulk' | 'template'>('single');
  const [isCreatingEvents, setIsCreatingEvents] = useState(false);
  const [newEvent, setNewEvent] = useState({
    title: '',
    description: '',
    startTime: '09:00',
    endTime: '10:00',
    eventType: 'class',
    room: '',
    location: '',
    teacherName: 'Admin', // Default teacher name for admin-created events
    selectedDays: [] as string[],
    startDate: new Date().toISOString().split('T')[0],
    endDate: new Date().toISOString().split('T')[0],
    repeatPattern: 'weekly' as 'daily' | 'weekly' | 'monthly',
    templateName: ''
  });

  // Helper function to get the next weekday (Monday-Friday)
  const getNextWeekday = (date: Date): Date => {
    const nextDay = new Date(date);
    while (nextDay.getDay() === 0 || nextDay.getDay() === 6) { // Sunday or Saturday
      nextDay.setDate(nextDay.getDate() + 1);
    }
    return nextDay;
  };

  // Helper function to format date for input (YYYY-MM-DD)
  const formatDateForInput = (date: Date): string => {
    return date.toISOString().split('T')[0];
  };

  // State for unified menu creation (single item or batch)
  const [menuCreationMode, setMenuCreationMode] = useState('single'); // 'single' or 'batch'
  const [singleMenuItem, setSingleMenuItem] = useState({
    name: '',
    description: '',
    category: '',
    imageFile: null as File | null,
    imageUrl: ''
  });
  const [batchMenuData, setBatchMenuData] = useState({
    organizationCode: '',
    startDate: formatDateForInput(getNextWeekday(new Date())),
    endDate: formatDateForInput(getNextWeekday(new Date(Date.now() + 6 * 24 * 60 * 60 * 1000))),
    periodType: 'week',
    menuItems: [
      { category: 'drinks', name: '', description: '', isEnabled: false, imageFile: null as File | null, imageUrl: '' },
      { category: 'break', name: '', description: '', isEnabled: false, imageFile: null as File | null, imageUrl: '' },
      { category: 'lunch', name: '', description: '', isEnabled: false, imageFile: null as File | null, imageUrl: '' },
      { category: 'snack', name: '', description: '', isEnabled: false, imageFile: null as File | null, imageUrl: '' },
    ]
  });

  // State for batch event creation
  const [batchEventData, setBatchEventData] = useState({
    organizationCode: '',
    startDate: formatDateForInput(getNextWeekday(new Date())),
    endDate: formatDateForInput(getNextWeekday(new Date(Date.now() + 6 * 24 * 60 * 60 * 1000))),
    periodType: 'week',
    events: [
      { title: '', description: '', time: '', location: '', isEnabled: false, imageFile: null as File | null, imageUrl: '', category: 'general' },
      { title: '', description: '', time: '', location: '', isEnabled: false, imageFile: null as File | null, imageUrl: '', category: 'general' },
      { title: '', description: '', time: '', location: '', isEnabled: false, imageFile: null as File | null, imageUrl: '', category: 'general' },
      { title: '', description: '', time: '', location: '', isEnabled: false, imageFile: null as File | null, imageUrl: '', category: 'general' },
      { title: '', description: '', time: '', location: '', isEnabled: false, imageFile: null as File | null, imageUrl: '', category: 'general' },
    ]
  });

  // Operation history state
  const [operationHistory, setOperationHistory] = useState<any[]>([]);

  // Function to log operations to history
  const logOperation = (operation: {
    type: 'create' | 'delete' | 'update';
    contentType: 'post' | 'event' | 'batchEvent' | 'batchMenu' | 'menu' | 'diary' | 'timetable';
    contentId: string;
    contentTitle: string;
    organizationCode: string;
    details?: any;
  }) => {
    const newOperation = {
      id: Date.now().toString(),
      timestamp: serverTimestamp(),
      operatorId: currentUser?.uid || 'admin',
      operatorName: currentUser?.displayName || 'Admin',
      ...operation
    };

    setOperationHistory(prev => [newOperation, ...prev].slice(0, 100)); // Keep last 100 operations

    // Also save to Firestore for persistence
    try {
      addDoc(collection(db, 'operationHistory'), newOperation).catch(err => {
        console.error('Error saving operation to Firestore:', err);
      });
    } catch (err) {
      console.error('Error logging operation:', err);
    }
  };

  // Load operation history from Firestore
  const loadOperationHistory = async () => {
    try {
      const historyQuery = query(
        collection(db, 'operationHistory'),
        orderBy('timestamp', 'desc'),
        limit(100)
      );
      const historySnapshot = await getDocs(historyQuery);
      const history = historySnapshot.docs.map(doc => {
        const data = doc.data();
        return {
          id: doc.id,
          ...data,
          timestamp: data.timestamp,
          organizationCode: data.organizationCode as string | undefined,
        };
      });

      // Filter by selected organization if not 'all'
      const filteredHistory = selectedOrganization === 'all'
        ? history
        : history.filter(op => op.organizationCode === selectedOrganization);

      setOperationHistory(filteredHistory);
    } catch (error) {
      console.error('Error loading operation history:', error);
    }
  };

  useEffect(() => {
    loadData();
    loadOperationHistory();

    // Test Firestore connection
    const testConnection = async () => {
      try {
        console.log('Testing Firestore connection...');
        const testDoc = await getDocs(collection(db, 'users'));
        console.log('Firestore connection successful. Found', testDoc.docs.length, 'users');
      } catch (error) {
        console.error('Firestore connection failed:', error);
        CustomToast.show('Cannot connect to database. Please check your internet connection.', 'error');
      }
    };

    testConnection();

    // Check storage usage and show notifications
    const checkStorageNotifications = async () => {
      try {
        // Wait a bit for data to load
        setTimeout(async () => {
          await storageNotificationService.checkAndNotify();
        }, 3000); // 3 second delay to let data load
      } catch (error) {
        console.error('Error checking storage notifications:', error);
      }
    };

    checkStorageNotifications();

    // Cleanup old diary entries (older than 1.5 months)
    const cleanupOldEntries = async () => {
      try {
        const cutoffDate = new Date();
        cutoffDate.setDate(cutoffDate.getDate() - 45); // 1.5 months = 45 days

        // Delete old diary entries
        const oldEntriesSnapshot = await getDocs(
          query(collection(db, 'diary_entries'), where('timestamp', '<', cutoffDate))
        );

        for (const doc of oldEntriesSnapshot.docs) {
          await deleteDoc(doc.ref);
        }

        // Delete old parent replies
        const oldRepliesSnapshot = await getDocs(
          query(collection(db, 'parent_replies'), where('timestamp', '<', cutoffDate))
        );

        for (const doc of oldRepliesSnapshot.docs) {
          await deleteDoc(doc.ref);
        }

        if (oldEntriesSnapshot.docs.length > 0 || oldRepliesSnapshot.docs.length > 0) {
          console.log(`Cleaned up ${oldEntriesSnapshot.docs.length} old diary entries and ${oldRepliesSnapshot.docs.length} old replies`);
        }
      } catch (error) {
        console.error('Error cleaning up old diary entries:', error);
      }
    };

    cleanupOldEntries();

    // Set up real-time listeners for data changes - with improved consistency
    let timeoutId: NodeJS.Timeout;
    let isReloading = false;
    let pendingReload = false;

    const debouncedLoadData = () => {
      if (isReloading) {
        console.log('Skipping reload - already in progress');
        pendingReload = true;
        return;
      }

      clearTimeout(timeoutId);
      timeoutId = setTimeout(async () => {
        isReloading = true;
        console.log('Debounced reload triggered');
        await loadData();
        isReloading = false;

        // If there was a pending reload while we were loading, trigger another one
        if (pendingReload) {
          pendingReload = false;
          setTimeout(debouncedLoadData, 100);
        }
      }, 2000); // Increased debounce to 2 seconds to reduce flickering
    };

    // Set up listeners for all critical collections without limits for better consistency
    const unsubscribeUsers = onSnapshot(
      collection(db, 'users'),
      debouncedLoadData
    );
    const unsubscribeClasses = onSnapshot(
      collection(db, 'classes'),
      debouncedLoadData
    );
    const unsubscribePosts = onSnapshot(
      collection(db, 'posts'),
      debouncedLoadData
    );
    const unsubscribeMenu = onSnapshot(
      collection(db, 'menu'),
      debouncedLoadData
    );
    const unsubscribeOrganizations = onSnapshot(
      collection(db, 'organizations'),
      debouncedLoadData
    );

    // Cleanup listeners on unmount
    return () => {
      clearTimeout(timeoutId);
      unsubscribeUsers();
      unsubscribeClasses();
      unsubscribePosts();
      unsubscribeMenu();
      unsubscribeOrganizations();
    };
  }, []);

  // Reload operation history when organization changes
  useEffect(() => {
    loadOperationHistory();
  }, [selectedOrganization]);

  // Scope fleet admins to their organization only
  useEffect(() => {
    if (isFleetAdmin && fleetOrgCode) {
      setSelectedOrganization(fleetOrgCode);
    }
  }, [isFleetAdmin, fleetOrgCode]);

  // Handle organization selection changes
  useEffect(() => {
    console.log('Organization selection changed to:', selectedOrganization);
    // The data is already loaded, we just need to ensure the UI reflects the selection
    // The filtering is handled in the getFilteredUsers and other functions
    // Recompute content lists for the selected organization without reloading
    try {
      // Posts
      const filteredPosts = selectedOrganization === 'all'
        ? allPosts
        : allPosts.filter(post => post.organizationCode === selectedOrganization || post.classId === selectedOrganization);
      setPosts(filteredPosts);

      // Menu items
      const filteredMenu = selectedOrganization === 'all'
        ? allMenuItems
        : allMenuItems.filter(item => item.organizationCode === selectedOrganization);
      setMenuItems(filteredMenu);

      // Batch menus
      const filteredBatchMenus = selectedOrganization === 'all'
        ? allBatchMenus
        : allBatchMenus.filter(batch => batch.organizationCode === selectedOrganization);
      setBatchMenus(filteredBatchMenus);

      // Events
      const filteredEvents = selectedOrganization === 'all'
        ? allEvents
        : allEvents.filter(event => event.organizationCode === selectedOrganization);
      setEvents(filteredEvents);

      // Users - filter the display list but keep allUsers for counting
      const filteredUsers = selectedOrganization === 'all'
        ? allUsers
        : allUsers.filter(user => {
          const userOrg = user.organizationCode || user.schoolCode || user.classCode || user.institution || user.associatedClassroomId;
          return userOrg === selectedOrganization;
        });
      setUsers(filteredUsers);

      // Classrooms cache reset for timetable modal UI
      if (selectedOrganization !== 'all') {
        // When organization changes, refresh classrooms list later when modal opens
        setOrganizationClassrooms([]);
      }
    } catch (e) {
      console.log('Error updating filtered content for selected organization:', e);
    }
  }, [selectedOrganization]);

  const loadData = async () => {
    try {
      setLoading(true);

      console.log('Starting to load data...');

      // Load users from 'users' collection - no limits for consistency
      const usersQuery = query(collection(db, 'users'));
      const usersSnapshot = await getDocs(usersQuery);
      let usersData = usersSnapshot.docs
        .filter(doc => {
          const data = doc.data();
          // Filter out deleted users
          return !(data.deleted === true);
        })
        .map(doc => {
          const data = doc.data();
          // Remove excessive logging - only log in debug mode
          // console.log(`User ${doc.id} data:`, data);

          return {
            id: doc.id,
            ...data
          };
        }) as User[];

      // Filter users by selected organization using standardized organization codes
      if (selectedOrganization !== 'all') {
        usersData = usersData.filter(user => {
          // Check all possible organization fields (prioritize organizationCode)
          const userOrg = user.organizationCode || user.schoolCode || user.classCode || user.institution || user.associatedClassroomId;
          // Remove excessive logging
          // console.log(`User ${user.displayName} org check:`, { 
          //   userOrg, 
          //   selectedOrganization, 
          //   organizationCode: user.organizationCode,
          //   schoolCode: user.schoolCode, 
          //   classCode: user.classCode, 
          //   institution: user.institution,
          //   associatedClassroomId: user.associatedClassroomId
          // });
          return userOrg === selectedOrganization;
        });
      }

      console.log('Loaded users:', usersData.length);

      // Load children as students from class documents with limit
      const childrenUsers: User[] = [];

      try {
        const classesQuery = query(collection(db, 'classes'));
        const classesSnapshot = await getDocs(classesQuery);

        for (const classDoc of classesSnapshot.docs) {
          const classData = classDoc.data();
          const classCode = classData.classCode || classData.classId;

          // Filter by selected organization
          if (selectedOrganization !== 'all' && classCode !== selectedOrganization) {
            continue;
          }

          // Remove excessive logging
          // console.log(`Processing class ${classDoc.id} with code ${classCode}`);

          const parents = classData['parents'] as any[] || [];
          // console.log(`Found ${parents.length} parents in class ${classCode}`);

          for (const parent of parents) {
            const children = parent['children'] as any[] || [];
            const childAvatars = parent['childAvatars'] as any[] || [];
            const parentName = parent['parentName'] || 'Unknown Parent';
            const parentId = parent['parentId'] || '';

            // console.log(`Parent ${parentName} has ${children.length} children`);

            for (let i = 0; i < children.length; i++) {
              const childName = children[i] as string;
              const childAvatar = i < childAvatars.length ? childAvatars[i] as string : 'assets/images/cartoon1.png';

              const childUser: User = {
                id: `${parentId}_child_${i}`,
                displayName: childName,
                email: `${childName.toLowerCase().replace(/\s+/g, '.')}@pear.com`,
                role: 'student',
                classCode: classCode,
                parentName: parentName,
                parentId: parentId,
                avatarUrl: childAvatar,
                createdAt: new Date(),
                children: [],
                childAvatars: []
              };

              childrenUsers.push(childUser);
              // console.log(`Created child user: ${childName} with parent ${parentName}`);
            }
          }
        }

        console.log(`Created ${childrenUsers.length} child users from class documents`);
      } catch (error) {
        console.error('Error loading children from class documents:', error);
      }

      // Combine regular users with children users (but don't count children in total)
      const allUsers = [...usersData, ...childrenUsers];
      console.log(`Total users (including children): ${allUsers.length}`);
      console.log(`Regular users: ${usersData.length}`);
      console.log(`Child users: ${childrenUsers.length}`);

      // Debug: Log all users and their organization codes (only in debug mode)
      // console.log('All users with their organization codes:');
      // allUsers.forEach(user => {
      //   const userOrg = user.organizationCode || user.schoolCode || user.classCode || user.institution || user.associatedClassroomId;
      //   console.log(`- ${user.displayName} (${user.role}): ${userOrg}`);
      // });

      // If no users found, log it but don't add test users (causes confusion)
      if (allUsers.length === 0) {
        console.log('No users found in database');
      }

      // Set both filtered and unfiltered user lists
      setUsers(usersData);
      setAllUsers(usersData);

      // Also update the useEffect to handle user filtering when organization changes
      // This ensures the counts are always based on the full dataset

      // Load organizations from 'organizations' collection - ALWAYS load all organizations for selector
      console.log('Loading organizations from Firestore...');
      const orgsQuery = query(collection(db, 'organizations'));
      const orgsSnapshot = await getDocs(orgsQuery);
      console.log('Organizations snapshot:', orgsSnapshot.docs.length, 'documents found');

      let allOrgsData = orgsSnapshot.docs.map(doc => {
        const data = doc.data();

        // Check if organization has active subscription
        const expirationDate = data.expirationDate?.toDate();
        const isActive = data.isActive ?? true;
        const subscriptionStatus = data.subscriptionStatus || 'active';

        // Determine if subscription is active
        let hasActiveSubscription = isActive && subscriptionStatus.toLowerCase() === 'active';
        if (expirationDate) {
          const now = new Date();
          let isExpirationInFuture = false;

          // Handle different date formats
          if (expirationDate instanceof Date) {
            isExpirationInFuture = expirationDate > now;
          } else if (typeof expirationDate === 'string') {
            const parsedDate = new Date(expirationDate);
            isExpirationInFuture = parsedDate > now;
          } else if (expirationDate && typeof expirationDate === 'object' && expirationDate.seconds) {
            // Firestore Timestamp
            const firestoreDate = new Date(expirationDate.seconds * 1000);
            isExpirationInFuture = firestoreDate > now;
          }

          hasActiveSubscription = hasActiveSubscription && isExpirationInFuture;
        }

        // Map the organization data
        return {
          id: doc.id,
          name: data.name || 'Unknown Organization',
          code: data.code || doc.id,
          type: data.type || 'school',
          productType: data.productType || 'FULL_FEATURE',
          address: data.address || '',
          phone: data.phone || '',
          email: data.email || '',
          createdAt: data.createdAt || Timestamp.now(),
          studentCount: 0,
          teacherCount: 0,
          parentCount: 0,
          expirationDate: data.expirationDate || null,
          isActive: isActive,
          subscriptionPlan: data.subscriptionPlan || 'premium',
          maxStudents: data.maxStudents || 100,
          maxTeachers: data.maxTeachers || 10,
          maxParents: data.maxParents || 200,
          subscriptionStatus: subscriptionStatus,
          hasActiveSubscription: hasActiveSubscription,
          isPilot: data.isPilot === true,
          pilotEndsAt: data.pilotEndsAt || null,
          paymentFailures: Number(data.paymentFailures) || 0,
          pendingActivation: data.pendingActivation === true,
          cancelAtPeriodEnd: data.cancelAtPeriodEnd === true,
        };
      }) as Organization[];

      // No hardcoded organizations - only load from database
      const hardcodedOrgs: Organization[] = [];

      // Merge hardcoded organizations with database organizations, excluding deleted ones
      const existingCodes = new Set(allOrgsData.map(org => org.code));
      console.log('Existing organization codes:', Array.from(existingCodes));
      console.log('Deleted organization codes:', Array.from(deletedOrganizations));

      const missingOrgs = hardcodedOrgs.filter(org => {
        const shouldInclude = !existingCodes.has(org.code) && !deletedOrganizations.has(org.code);
        return shouldInclude;
      });

      console.log('Adding missing organizations:', missingOrgs.map(org => org.code));
      allOrgsData = [...allOrgsData, ...missingOrgs];

      // Remove any organizations that are in the deleted list
      allOrgsData = allOrgsData.filter(org => !deletedOrganizations.has(org.code));
      console.log('All organizations after filtering deleted ones:', allOrgsData.map(org => org.code));

      // Set ALL organizations for the selector (never filter this)
      setOrganizations(allOrgsData);

      // For data filtering, use a separate variable
      let orgsData = allOrgsData;

      // Only filter for data calculations, not for the selector
      if (selectedOrganization !== 'all') {
        orgsData = allOrgsData.filter(org => org.code === selectedOrganization);
      }

      console.log('Organizations for data calculations:', orgsData.length);

      // If no organizations found, try to create a default one or show error
      if (orgsData.length === 0) {
        console.log('No organizations found in Firestore. This might be normal if no data exists yet.');
        // Don't set fallback data here - let the user see the real state
      }

      // Calculate real user counts for each organization
      const orgsWithCounts = orgsData.map(org => {
        let studentCount = 0;
        let teacherCount = 0;
        let parentCount = 0;

        // console.log(`Calculating counts for organization: ${org.code}`);
        // console.log(`Total users to check: ${allUsers.length}`);

        // Count users for this organization
        allUsers.forEach(user => {
          // Check all possible organization fields
          const userOrg = user.organizationCode || user.schoolCode || user.classCode || user.institution || user.associatedClassroomId;
          // console.log(`Checking user: ${user.displayName}, role: ${user.role}, userOrg: ${userOrg}, org.code: ${org.code}`);

          // Check if user belongs to this organization
          const belongsToOrg = userOrg === org.code;

          if (belongsToOrg) {
            // console.log(`User ${user.displayName} belongs to organization ${org.code}`);

            switch (user.role) {
              case 'student':
                // Count all students (including children from parents)
                studentCount++;
                // console.log(`Incremented student count for ${org.code}`);
                break;
              case 'teacher':
                teacherCount++;
                // console.log(`Incremented teacher count for ${org.code}`);
                break;
              case 'parent':
                parentCount++;
                // console.log(`Incremented parent count for ${org.code}`);
                // Also count children that this parent has added
                if (user.children && Array.isArray(user.children)) {
                  studentCount += user.children.length;
                  // console.log(`Added ${user.children.length} children to student count for ${org.code}`);
                }
                break;
            }
          }
        });

        // console.log(`Final counts for ${org.code}: Students=${studentCount}, Teachers=${teacherCount}, Parents=${parentCount}`);

        return {
          ...org,
          studentCount,
          teacherCount,
          parentCount
        };
      });

      console.log('Loaded organizations with real counts:', orgsWithCounts.length);

      // Debug: Log all organization codes
      console.log('Organization codes found:', orgsWithCounts.map(org => org.code));

      // Update organizations with real counts but keep all organizations in the list
      setOrganizations(prevOrgs => {
        return prevOrgs.map(org => {
          const orgWithCounts = orgsWithCounts.find(o => o.code === org.code);
          return orgWithCounts ? { ...org, ...orgWithCounts } : org;
        });
      });

      // Now filter users and posts based on active subscriptions
      // Filter users by active subscriptions
      if (selectedOrganization !== 'all') {
        // Get the selected organization to check its subscription status
        const selectedOrg = orgsWithCounts.find(org => org.code === selectedOrganization);
        if (selectedOrg && !selectedOrg.hasActiveSubscription) {
          // If selected organization has no active subscription, show no users
          usersData = [];
          console.log(`No users shown - organization ${selectedOrganization} has no active subscription`);
        }
      } else {
        // For 'all' organizations, only show users from organizations with active subscriptions
        const activeOrgCodes = orgsWithCounts.filter(org => org.hasActiveSubscription).map(org => org.code);
        usersData = usersData.filter(user => {
          const userOrg = user.schoolCode || user.classCode || user.institution || user.associatedClassroomId;
          return activeOrgCodes.includes(userOrg || '');
        });
        console.log(`Filtered users to ${usersData.length} from organizations with active subscriptions`);
      }

      // Load posts - no limits for consistency
      const postsQuery = query(collection(db, 'posts'));
      const postsSnapshot = await getDocs(postsQuery);
      let postsData = postsSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as Post[];
      // store unfiltered for memoized filtering per selectedOrganization
      setAllPosts(postsData);

      console.log('All posts loaded:', postsData.length);
      console.log('Selected organization:', selectedOrganization);
      // console.log('Sample posts:', postsData.slice(0, 3).map(post => ({
      //   id: post.id,
      //   classId: post.classId,
      //   organizationCode: post.organizationCode,
      //   authorName: post.authorName,
      //   textContent: post.textContent?.substring(0, 50) + '...'
      // })));

      // Log all organization codes found in posts
      const allOrgCodes = Array.from(new Set(postsData.map(post => post.organizationCode).filter(code => code)));
      console.log('All organization codes found in posts:', allOrgCodes);

      // Filter posts by selected organization AND active subscription
      if (selectedOrganization !== 'all') {
        // Get the selected organization to check its subscription status
        const selectedOrg = orgsWithCounts.find(org => org.code === selectedOrganization);
        if (selectedOrg && !selectedOrg.hasActiveSubscription) {
          // If selected organization has no active subscription, show no posts
          postsData = [];
          console.log(`No posts shown - organization ${selectedOrganization} has no active subscription`);
        } else {
          console.log(`Filtering posts for organization: ${selectedOrganization}`);
          // console.log('Available classIds in posts:', Array.from(new Set(postsData.map(post => post.classId))));
          // console.log('Available organizationCodes in posts:', Array.from(new Set(postsData.map(post => post.organizationCode).filter(code => code))));

          postsData = postsData.filter(post => {
            const matchesClassId = post.classId === selectedOrganization;
            const matchesOrgCode = post.organizationCode === selectedOrganization;
            // console.log(`Post filtering: post.classId=${post.classId}, post.organizationCode=${post.organizationCode}, selectedOrganization=${selectedOrganization}, matchesClassId=${matchesClassId}, matchesOrgCode=${matchesOrgCode}`);
            // Check if post belongs to selected organization (try both classId and organizationCode)
            return matchesClassId || matchesOrgCode;
          });

          console.log(`Posts after filtering: ${postsData.length}`);
        }
      } else {
        // For 'all' organizations, only show posts from organizations with active subscriptions
        const activeOrgCodes = orgsWithCounts.filter(org => org.hasActiveSubscription).map(org => org.code);
        console.log('Active organization codes:', activeOrgCodes);
        postsData = postsData.filter(post =>
          activeOrgCodes.includes(post.classId) ||
          activeOrgCodes.includes(post.organizationCode || '')
        );
        console.log(`Filtered posts to ${postsData.length} from organizations with active subscriptions`);
      }

      console.log('Loaded posts:', postsData.length);
      // Default view: if an org is selected, show that org's posts only
      setPosts(selectedOrganization === 'all' ? postsData : postsData.filter(p => p.organizationCode === selectedOrganization || p.classId === selectedOrganization));

      // Load menu items - no limits for consistency
      const menuQuery = query(collection(db, 'menu'));
      const menuSnapshot = await getDocs(menuQuery);
      let menuData = menuSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as MenuItem[];
      setAllMenuItems(menuData);

      // Filter menu items by selected organization
      if (selectedOrganization !== 'all') {
        menuData = menuData.filter(item => {
          // Check if menu item has organizationCode field
          if (item.organizationCode === selectedOrganization) {
            return true;
          }

          // Find the user who uploaded this item
          const uploader = allUsers.find(user => user.id === item.uploadedBy);
          return uploader && (
            uploader.schoolCode === selectedOrganization ||
            uploader.classCode === selectedOrganization
          );
        });
      }

      console.log('Loaded menu items:', menuData.length);

      // Load batch menus separately - filter out deleted ones
      const batchMenuQuery = query(collection(db, 'batchMenus'));
      const batchMenuSnapshot = await getDocs(batchMenuQuery);
      let batchMenuData = batchMenuSnapshot.docs
        .filter(doc => doc.data().status !== 'deleted') // Filter out deleted batch menus
        .map(doc => ({
          id: doc.id,
          ...doc.data()
        })) as any[];
      setAllBatchMenus(batchMenuData);

      // Filter batch menus by selected organization
      if (selectedOrganization !== 'all') {
        batchMenuData = batchMenuData.filter(batchMenu =>
          batchMenu.organizationCode === selectedOrganization
        );
      }

      console.log('Loaded batch menus:', batchMenuData.length);
      setBatchMenus(selectedOrganization === 'all' ? batchMenuData : batchMenuData.filter(b => b.organizationCode === selectedOrganization));

      // Extract menu items from batch menus and add them to menu items list
      const batchMenuItems: MenuItem[] = [];
      batchMenuData.forEach((batchMenu: any) => {
        if (batchMenu.menuItems && Array.isArray(batchMenu.menuItems)) {
          batchMenu.menuItems.forEach((item: any, index: number) => {
            // Convert batch menu item to individual menu item format
            const batchMenuItem: MenuItem = {
              id: `${batchMenu.id}_${item.category}_${index}`, // Unique ID for batch menu items
              name: item.name || '',
              description: item.description || '',
              category: item.category || '',
              imageUrl: item.imageUrl || '',
              rating: 0,
              ratingCount: 0,
              date: batchMenu.startDate || new Date(),
              uploadedBy: batchMenu.createdBy || '',
              createdAt: batchMenu.createdAt || new Date(),
              nutritionalInfo: item.nutritionalInfo || {},
              organizationCode: batchMenu.organizationCode,
            };
            batchMenuItems.push(batchMenuItem);
          });
        }
      });

      // Merge individual menu items with batch menu items
      const allMenuItemsCombined = [...menuData, ...batchMenuItems];

      // Sort by creation date (newest first)
      allMenuItemsCombined.sort((a, b) => {
        const dateA = a.createdAt?.toDate ? a.createdAt.toDate() : new Date(a.createdAt);
        const dateB = b.createdAt?.toDate ? b.createdAt.toDate() : new Date(b.createdAt);
        return dateB.getTime() - dateA.getTime();
      });

      console.log('Total menu items (including batch):', allMenuItemsCombined.length);
      console.log('  - Individual items:', menuData.length);
      console.log('  - Batch menu items:', batchMenuItems.length);

      setAllMenuItems(allMenuItemsCombined);
      setMenuItems(allMenuItemsCombined);

      // Load events from dedicated events collection - no limits for consistency
      const eventsQuery = query(
        collection(db, 'events'),
        orderBy('eventDate', 'desc')
      );
      const eventsSnapshot = await getDocs(eventsQuery);
      let eventsData = eventsSnapshot.docs.map(doc => ({
        id: doc.id,
        title: doc.data().title,
        description: doc.data().description,
        date: doc.data().eventDate,
        time: doc.data().time,
        location: doc.data().location,
        imageUrl: doc.data().imageUrl,
        createdBy: doc.data().createdBy,
        createdAt: doc.data().createdAt,
        organizationCode: doc.data().organizationCode,
        status: doc.data().status
      })) as Event[];
      setAllEvents(eventsData);

      // Filter events by selected organization
      if (selectedOrganization !== 'all') {
        eventsData = eventsData.filter(event =>
          event.organizationCode === selectedOrganization
        );
      }

      console.log('Loaded events:', eventsData.length);
      setEvents(selectedOrganization === 'all' ? eventsData : eventsData.filter(e => e.organizationCode === selectedOrganization));

      // Load batch events separately - filter out deleted ones
      const batchEventsQuery = query(collection(db, 'batchEvents'));
      const batchEventsSnapshot = await getDocs(batchEventsQuery);
      let batchEventsData = batchEventsSnapshot.docs
        .filter(doc => doc.data().status !== 'deleted') // Filter out deleted batch events
        .map(doc => ({
          id: doc.id,
          organizationCode: doc.data().organizationCode,
          startDate: doc.data().startDate,
          endDate: doc.data().endDate,
          periodType: doc.data().periodType,
          events: doc.data().events,
          createdBy: doc.data().createdBy,
          createdAt: doc.data().createdAt,
          status: doc.data().status || 'active',
          type: 'batch'
        })) as any[];
      setAllBatchEvents(batchEventsData);

      // Filter batch events by selected organization
      if (selectedOrganization !== 'all') {
        batchEventsData = batchEventsData.filter(batchEvent =>
          batchEvent.organizationCode === selectedOrganization
        );
      }

      console.log('Loaded batch events:', batchEventsData.length);
      setBatchEvents(batchEventsData);

      // Load diary entries - no limits for consistency
      const diaryQuery = query(collection(db, 'diary_entries'));
      const diarySnapshot = await getDocs(diaryQuery);
      let diaryData = diarySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as any[];
      setAllDiaryEntries(diaryData);

      // Filter diary entries by selected organization
      if (selectedOrganization !== 'all') {
        diaryData = diaryData.filter(entry => entry.classId === selectedOrganization);
      }

      console.log('Loaded diary entries:', diaryData.length);
      setDiaryEntries(selectedOrganization === 'all' ? diaryData : diaryData.filter(e => e.classId === selectedOrganization));

    } catch (error) {
      console.error('Error loading data:', error);

      // Reset organization selection to 'all' when there's an error
      setSelectedOrganization('all');

      // If there's an error, show some basic data to prevent the app from being completely empty
      console.log('Setting fallback data due to error...');

      // Add some basic fallback data so the app doesn't look completely broken
      setUsers([
        {
          id: 'fallback1',
          displayName: 'Loading...',
          email: 'loading@pear.com',
          role: 'teacher',
          schoolCode: 'loading',
          classCode: 'loading',
          createdAt: new Date(),
          phone: '+1234567890'
        }
      ]);
      setOrganizations([]); // Don't set fallback organizations - let it show empty state
      setPosts([]);
      setMenuItems([]);
      setBatchMenus([]);
      setEvents([]);

      // Show error toast
      CustomToast.show('Error loading data. Please check your connection.', 'error');
    } finally {
      setLoading(false);
    }
  };

  const getFilteredUsers = () => {
    let filteredUsers = users.filter(user => {
      // Check if user belongs to selected organization
      const userOrg = user.organizationCode || user.schoolCode || user.classCode || user.institution || user.associatedClassroomId;
      const belongsToSelectedOrg = selectedOrganization === 'all' || userOrg === selectedOrganization;

      // Check search query
      const matchesSearch = user.displayName?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        user.email?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        user.institution?.toLowerCase().includes(searchQuery.toLowerCase());

      // Check role filter
      let matchesRole = true;
      if (selectedFilter === 'Teachers') matchesRole = user.role === 'teacher';
      if (selectedFilter === 'Parents') matchesRole = user.role === 'parent';
      if (selectedFilter === 'Students') matchesRole = user.role === 'student';

      return belongsToSelectedOrg && matchesSearch && matchesRole;
    });

    // If showing Students, also include children from parents
    if (selectedFilter === 'Students') {
      const parentUsers = users.filter(user => {
        const userOrg = user.organizationCode || user.schoolCode || user.classCode || user.institution || user.associatedClassroomId;
        const belongsToSelectedOrg = selectedOrganization === 'all' || userOrg === selectedOrganization;
        return user.role === 'parent' && belongsToSelectedOrg;
      });

      // Create virtual student entries from parent children
      const virtualStudents: User[] = [];
      parentUsers.forEach(parent => {
        if (parent.children && parent.children.length > 0) {
          parent.children.forEach((childName: string, index: number) => {
            const childAvatar = parent.childAvatars && parent.childAvatars[index]
              ? parent.childAvatars[index]
              : 'assets/images/cartoon1.png';

            virtualStudents.push({
              id: `${parent.id}_child_${index}`,
              displayName: childName,
              email: `${childName.toLowerCase().replace(/\s+/g, '.')}@student.local`,
              role: 'student',
              parentId: parent.id,
              parentName: parent.displayName,
              classGroup: parent.classGroup,
              classCode: parent.classCode,
              schoolCode: parent.schoolCode,
              institution: parent.institution,
              avatarUrl: childAvatar,
              createdAt: parent.createdAt,
              children: [],
              childAvatars: [],
            });
          });
        }
      });

      // Add virtual students to filtered results
      filteredUsers = [...filteredUsers, ...virtualStudents];
    }

    return filteredUsers;
  };

  const getStudentCount = () => {
    // Use allUsers (unfiltered) for counting, but filter by selected organization
    const orgUsers = allUsers.filter(user => {
      const userOrg = user.organizationCode || user.schoolCode || user.classCode || user.institution || user.associatedClassroomId;
      return selectedOrganization === 'all' || userOrg === selectedOrganization;
    });

    // Count actual student users
    const actualStudents = orgUsers.filter(user => user.role === 'student').length;

    // Count children from all parents in this organization
    const childrenFromParents = orgUsers
      .filter(user => user.role === 'parent')
      .reduce((total, parent) => total + (parent.children?.length || 0), 0);

    return actualStudents + childrenFromParents;
  };

  const getParentCount = () => {
    const orgUsers = allUsers.filter(user => {
      if (selectedOrganization === 'all') return true;

      // For parents, check their organizationCode first, then schoolCode, then classCode
      if (user.role === 'parent') {
        return user.organizationCode === selectedOrganization || user.schoolCode === selectedOrganization || user.classCode === selectedOrganization;
      }

      // For other users, check all possible organization fields
      const userOrg = user.organizationCode || user.schoolCode || user.classCode || user.institution || user.associatedClassroomId;
      return userOrg === selectedOrganization;
    });
    return orgUsers.filter(user => user.role === 'parent').length;
  };

  const getTeacherCount = () => {
    const orgUsers = allUsers.filter(user => {
      if (selectedOrganization === 'all') return true;

      // Check all possible organization fields
      const userOrg = user.organizationCode || user.schoolCode || user.classCode || user.institution || user.associatedClassroomId;
      return userOrg === selectedOrganization;
    });
    return orgUsers.filter(user => user.role === 'teacher').length;
  };

  const handleLogout = async () => {
    try {
      await logout();
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  const getStatusColor = (role: string) => {
    switch (role) {
      case 'teacher': return '#F7941D';
      case 'parent': return '#43BE83';
      case 'student': return '#EA8F95';
      default: return '#797D8C';
    }
  };

  const getStatusText = (role: string) => {
    switch (role) {
      case 'teacher': return 'Teacher';
      case 'parent': return 'Parent';
      case 'student': return 'Student';
      default: return role;
    }
  };

  const handleAddOrganization = async (orgData: Partial<Organization>) => {
    try {
      console.log('=== ADDING ORGANIZATION ===');
      console.log('Received orgData:', orgData);

      // Set subscription limits based on plan
      let maxStudents = 100;
      let maxTeachers = 10;
      let maxParents = 200;

      switch (orgData.subscriptionPlan) {
        case 'premium':
          maxStudents = 500;
          maxTeachers = 25;
          maxParents = 1000;
          break;
        case 'enterprise':
          maxStudents = 999999; // Unlimited
          maxTeachers = 999999; // Unlimited
          maxParents = 999999; // Unlimited
          break;
        default:
          maxStudents = 500;
          maxTeachers = 25;
          maxParents = 1000;
      }

      // Check if expiration date is in the past
      const expirationDate = orgData.expirationDate;
      let subscriptionStatus = orgData.isActive ? 'active' : 'suspended';
      let hasActiveSubscription = orgData.isActive ?? true;

      if (expirationDate && expirationDate instanceof Date) {
        const now = new Date();
        if (expirationDate < now) {
          subscriptionStatus = 'expired';
          hasActiveSubscription = false;
          console.log('Organization expiration date is in the past, setting status to expired');
        }
      }

      const newOrg = {
        ...orgData,
        productType: orgData.productType ?? 'FULL_FEATURE',
        createdAt: new Date(),
        studentCount: 0,
        teacherCount: 0,
        parentCount: 0,
        isActive: orgData.isActive ?? true,
        subscriptionStatus: subscriptionStatus,
        hasActiveSubscription: hasActiveSubscription,
        maxStudents: maxStudents,
        maxTeachers: maxTeachers,
        maxParents: maxParents,
        updatedAt: new Date()
      };

      console.log('Processed newOrg:', newOrg);
      console.log('About to add to organizations collection...');

      // Use the organization code as the document ID
      const orgCode = newOrg.code;
      if (!orgCode) {
        throw new Error('Organization code is required');
      }

      const docRef = doc(db, 'organizations', orgCode);
      await setDoc(docRef, newOrg);
      const orgId = orgCode; // Use the code as the ID
      console.log('Organization added with ID:', orgId);

      // If this is a new organization with a code that was previously deleted, remove it from deleted list
      if (newOrg.code && deletedOrganizations.has(newOrg.code)) {
        setDeletedOrganizations(prev => {
          const newSet = new Set(Array.from(prev));
          newSet.delete(newOrg.code!);
          return newSet;
        });
        console.log(`Removed ${newOrg.code} from deleted organizations list`);
      }

      // Send welcome email to organization admin
      if (newOrg.email) {
        try {
          await sendOrganizationWelcomeEmail(newOrg.email, newOrg.name || 'Unknown Organization', newOrg.code || 'Unknown');
          console.log('Welcome email sent to organization admin');
        } catch (emailError) {
          console.error('Error sending welcome email:', emailError);
        }
      }

      // Update local state immediately for instant UI feedback
      const newOrgWithCounts: Organization = {
        id: orgId, // Use the document ID from Firestore
        name: newOrg.name || 'Unknown Organization',
        code: newOrg.code || orgId,
        type: newOrg.type || 'school',
        productType: newOrg.productType || 'FULL_FEATURE',
        address: newOrg.address || '',
        phone: newOrg.phone || '',
        email: newOrg.email || '',
        createdAt: newOrg.createdAt || new Date(),
        studentCount: 0,
        teacherCount: 0,
        parentCount: 0,
        expirationDate: newOrg.expirationDate || null,
        isActive: newOrg.isActive ?? true,
        subscriptionPlan: newOrg.subscriptionPlan || 'premium',
        maxStudents: newOrg.maxStudents || 100,
        maxTeachers: newOrg.maxTeachers || 10,
        maxParents: newOrg.maxParents || 200,
        subscriptionStatus: 'active' as const,
        hasActiveSubscription: newOrg.hasActiveSubscription ?? true
      };

      setOrganizations(prev => [...prev, newOrgWithCounts]);
      CustomToast.show('Organization added successfully!', 'success');

      // Note: Real-time listeners will automatically update the data, no need for manual reload
      return true;
    } catch (error) {
      console.error('Error adding organization:', error);
      CustomToast.show('Failed to add organization', 'error');
      return false;
    }
  };

  // Maps the new Create-Org wizard payload onto the existing create logic.
  const submitWizardOrg = async (
    p: CreateOrgPayload
  ): Promise<{ ok: boolean; fullCode?: string; error?: string }> => {
    let code = (p.code || '').trim();
    let resolvedProductType: Organization['productType'] = 'FULL_FEATURE';

    if (p.kind === 'BUS_TRACKING') {
      const built = buildBusOrgCode(code);
      if (built.error) {
        CustomToast.show(built.error, 'error');
        return { ok: false, error: built.error };
      }
      code = built.code;
      resolvedProductType = 'BUS_ONLY';
    } else if (p.kind === 'LORRY') {
      const built = buildLorryOrgCode(code, p.lorryMode);
      if (built.error) {
        CustomToast.show(built.error, 'error');
        return { ok: false, error: built.error };
      }
      code = built.code;
      resolvedProductType = p.lorryMode;
    }

    const ok = await handleAddOrganization({
      name: p.name,
      code,
      type: 'school' as const,
      productType: resolvedProductType,
      address: p.address,
      phone: p.phone,
      email: p.email,
      expirationDate: p.expiry ? new Date(p.expiry) : null,
      isActive: p.active,
      subscriptionPlan: p.plan,
      subscriptionStatus: p.active ? ('active' as const) : ('suspended' as const),
    });

    return { ok: ok !== false, fullCode: code };
  };

  /* Permanent delete. The routine itself lives in services/orgLifecycle so the
     console and this dashboard share ONE copy — the old inline version filtered
     component state for posts/menu/events, which meant deleting org B while
     viewing org A left B's content orphaned (Known bug 24). Since 2026-09-30
     the delete itself runs on the server and keeps no snapshot. */
  const handleDeleteOrganization = async (orgId: string) => {
    try {
      const orgToDelete = organizations.find(org => org.id === orgId);
      if (!orgToDelete) {
        console.error('Organization not found');
        return;
      }

      const confirmed = window.confirm(
        `\u26a0\ufe0f WARNING: This will permanently delete "${orgToDelete.name}" (${orgToDelete.code}) and ALL associated data:\n\n` +
        `\u2022 All users (teachers, parents, students) — including their logins\n` +
        `\u2022 All posts, menu items, events and class documents\n` +
        `\u2022 All conversations, messages, notifications and badges\n` +
        `\u2022 All buses, lorries, trips, attendance, refuels and alerts\n\n` +
        `Only payment records are kept. No copy is saved — to keep one, archive instead.\n\n` +
        `Are you sure you want to continue?`
      );
      if (!confirmed) return;

      const userInput = prompt('Type "DELETE" to confirm permanent deletion:');
      if (userInput !== 'DELETE') {
        CustomToast.show('Deletion cancelled - incorrect confirmation', 'error');
        return;
      }

      // The delete runs on the server — logins included. Throws, with nothing
      // deleted, when the server does not do it.
      const result = await purgeOrganization(orgToDelete.code, orgId);
      const authOk = result.authFailed === 0;
      if (!authOk) {
        console.error(`[delete] ${result.authFailed} login account(s) could not be removed`);
      }

      if (selectedOrganization === orgToDelete.code) setSelectedOrganization('all');
      setDeletedOrganizations(prev => new Set(Array.from(prev).concat(orgToDelete.code)));
      setOrganizations([]);
      setUsers([]);
      setPosts([]);
      setMenuItems([]);
      setEvents([]);

      CustomToast.show(
        authOk
          ? 'Organization and all associated data deleted successfully!'
          : `Organization deleted, but ${result.authFailed} login account(s) could not be removed — see console.`,
        authOk ? 'success' : 'error',
      );
    } catch (error) {
      console.error('Error deleting organization:', error);
      // purgeOrganization says whether anything was deleted; show its words.
      CustomToast.show(error instanceof Error && error.message ? error.message : 'Failed to delete organization', 'error');
    }
  };

  const handleEditOrganization = async (orgId: string, updates: Partial<Organization>) => {
    try {
      // Get the current organization data to check if status is changing
      const currentOrg = organizations.find(org => org.id === orgId);
      if (!currentOrg) {
        console.error('Organization not found');
        return;
      }

      // Check if organization is being set to inactive
      const isBeingDeactivated = currentOrg.isActive && updates.isActive === false;
      const isBeingActivated = !currentOrg.isActive && updates.isActive === true;

      if (isBeingDeactivated) {
        console.log(`Organization ${currentOrg.name} is being deactivated. This will log out all users.`);

        // Find all users associated with this organization
        const usersToLogout = users.filter(user =>
          user.organizationCode === currentOrg.code || user.schoolCode === currentOrg.code || user.classCode === currentOrg.code
        );

        console.log(`Will log out ${usersToLogout.length} users from organization ${currentOrg.code}`);

        // Update user documents to mark them as logged out
        for (const user of usersToLogout) {
          try {
            await updateDoc(doc(db, 'users', user.id), {
              isLoggedOut: true,
              logoutReason: 'Organization deactivated by admin',
              logoutTimestamp: new Date(),
              updatedAt: new Date()
            });
            console.log(`Marked user ${user.id} as logged out`);
          } catch (error) {
            console.error(`Error marking user ${user.id} as logged out:`, error);
          }
        }
      }

      // Check if organization is being reactivated
      if (isBeingActivated) {
        console.log(`Organization ${currentOrg.name} is being reactivated. This will allow users to log in again.`);

        // Find all users associated with this organization
        const usersToReactivate = users.filter(user =>
          user.organizationCode === currentOrg.code || user.schoolCode === currentOrg.code || user.classCode === currentOrg.code
        );

        console.log(`Will reactivate ${usersToReactivate.length} users for organization ${currentOrg.code}`);

        // Update user documents to allow them to log in again
        for (const user of usersToReactivate) {
          try {
            await updateDoc(doc(db, 'users', user.id), {
              isLoggedOut: false,
              logoutReason: null,
              logoutTimestamp: null,
              updatedAt: new Date()
            });
            console.log(`Reactivated user ${user.id}`);
          } catch (error) {
            console.error(`Error reactivating user ${user.id}:`, error);
          }
        }
      }

      // Check if expiration date is being updated
      if (updates.expirationDate) {
        const expirationDate = updates.expirationDate;
        let subscriptionStatus = updates.subscriptionStatus || currentOrg.subscriptionStatus || 'active';
        let hasActiveSubscription = updates.isActive ?? currentOrg.isActive ?? true;

        if (expirationDate instanceof Date) {
          const now = new Date();
          if (expirationDate < now) {
            subscriptionStatus = 'expired';
            hasActiveSubscription = false;
            console.log('Organization expiration date is in the past, setting status to expired');
          } else {
            subscriptionStatus = 'active';
            hasActiveSubscription = hasActiveSubscription && true;
            console.log('Organization expiration date is in the future, setting status to active');
          }
        }

        // Update the subscription status based on expiration date
        updates.subscriptionStatus = subscriptionStatus;
        updates.hasActiveSubscription = hasActiveSubscription;
      }

      // If organization is being set to active, ensure subscription status is also active
      if (updates.isActive === true) {
        updates.subscriptionStatus = 'active';
        updates.hasActiveSubscription = true;
        console.log('Organization is being set to active, ensuring subscription status is active');
      }

      // If subscription status is being set to active, ensure hasActiveSubscription is true
      if (updates.subscriptionStatus === 'active') {
        updates.hasActiveSubscription = true;
        console.log('Subscription status is being set to active, ensuring hasActiveSubscription is true');
      }

      // If subscription status is being set to expired, ensure hasActiveSubscription is false
      if (updates.subscriptionStatus === 'expired') {
        updates.hasActiveSubscription = false;
        console.log('Subscription status is being set to expired, ensuring hasActiveSubscription is false');
      }

      // Final calculation of hasActiveSubscription based on all factors
      if (updates.subscriptionStatus === 'active' && updates.isActive !== false) {
        let finalHasActiveSubscription = true;

        // Check expiration date if provided
        if (updates.expirationDate) {
          const now = new Date();
          let isExpirationInFuture = false;

          if (updates.expirationDate instanceof Date) {
            isExpirationInFuture = updates.expirationDate > now;
          } else if (typeof updates.expirationDate === 'string') {
            const parsedDate = new Date(updates.expirationDate);
            isExpirationInFuture = parsedDate > now;
          }

          finalHasActiveSubscription = finalHasActiveSubscription && isExpirationInFuture;
        }

        updates.hasActiveSubscription = finalHasActiveSubscription;
      }

      // If subscription is being renewed (status changed to active or expiration date extended), clear grace period
      const orgDoc = await getDoc(doc(db, 'organizations', orgId));
      if (orgDoc.exists()) {
        const currentData = orgDoc.data();
        const wasExpired = currentData.subscriptionStatus === 'expired' || !currentData.isActive;
        const isNowActive = (updates.subscriptionStatus === 'active' || updates.isActive === true) &&
          (!updates.subscriptionStatus || updates.subscriptionStatus === 'active');

        // If renewing subscription, restore archived data and reset user logout status
        if (wasExpired && isNowActive) {
          console.log(`Renewing subscription for organization: ${orgId}`);
          const orgCode = currentData.code || orgId;

          // Restore archived data if organization was archived
          if (currentData.archived === true) {
            try {
              console.log(`Restoring archived data for organization: ${orgCode}`);
              await restoreOrganizationData(orgCode);
              CustomToast.show(`Organization ${orgCode} data restored successfully!`, 'success');
            } catch (restoreError) {
              console.error(`Error restoring organization data:`, restoreError);
              CustomToast.show(`Warning: Could not restore archived data. Please restore manually.`, 'warning');
            }
          }

          // Clear grace period
          updates.gracePeriodUntil = null;

          // Reset logout status for all users in this organization
          const usersQuery = query(
            collection(db, 'users'),
            where('schoolCode', '==', orgCode)
          );
          const usersSnapshot = await getDocs(usersQuery);

          const batch = writeBatch(db);
          usersSnapshot.docs.forEach((userDoc) => {
            batch.update(userDoc.ref, {
              isLoggedOut: false,
              logoutReason: null,
              logoutTimestamp: null
            });
          });

          // Also check class code users
          const classUsersQuery = query(
            collection(db, 'users'),
            where('classCode', '==', orgCode)
          );
          const classUsersSnapshot = await getDocs(classUsersQuery);
          classUsersSnapshot.docs.forEach((userDoc) => {
            batch.update(userDoc.ref, {
              isLoggedOut: false,
              logoutReason: null,
              logoutTimestamp: null
            });
          });

          await batch.commit();
          console.log(`Reset logout status for users in organization: ${orgCode}`);
        }
      }

      // Update the organization in the correct collection
      await updateDoc(doc(db, 'organizations', orgId), {
        ...updates,
        updatedAt: new Date()
      });
      console.log('Organization updated:', orgId);

      // Immediately update the local state to reflect changes
      setOrganizations(prevOrgs =>
        prevOrgs.map(org =>
          org.id === orgId
            ? { ...org, ...updates, hasActiveSubscription: updates.hasActiveSubscription ?? org.hasActiveSubscription }
            : org
        )
      );

      // Real-time listeners will automatically update the data
      CustomToast.show('Organization updated successfully! Users can now log in again.', 'success');
    } catch (error) {
      console.error('Error updating organization:', error);
      CustomToast.show('Failed to update organization', 'error');
    }
  };

  const handleCreatePost = async (postData: { textContent: string; imageUrl?: string }) => {
    try {
      const newPost = {
        authorId: currentUser?.uid || 'admin',
        authorName: 'PEAR Admin',
        authorHandle: '@pear_admin',
        authorAvatar: 'assets/images/pear.png',
        classId: selectedOrganization === 'all' ? 'general' : selectedOrganization,
        organizationCode: selectedOrganization === 'all' ? 'general' : selectedOrganization,
        textContent: postData.textContent,
        imageUrl: postData.imageUrl,
        likes: [],
        comments: [],
        timestamp: new Date()
      };

      const docRef = await addDoc(collection(db, 'posts'), newPost);
      console.log('Post created with ID:', docRef.id);

      // Log operation
      logOperation({
        type: 'create',
        contentType: 'post',
        contentId: docRef.id,
        contentTitle: postData.textContent.substring(0, 50),
        organizationCode: selectedOrganization === 'all' ? 'general' : selectedOrganization,
      });

      // Reload data
      await loadData();
      setShowCreatePost(false);
      CustomToast.show('Post created successfully!', 'success');
    } catch (error) {
      console.error('Error creating post:', error);
      CustomToast.show('Failed to create post', 'error');
    }
  };

  const handleCreateEvent = async (eventData: { title: string; description: string; date: Date; time?: string; location?: string; imageUrl?: string; imageFile?: File }) => {
    try {
      // Validate organization selection
      if (selectedOrganization === 'all') {
        CustomToast.show('Please select an organization first', 'error');
        return;
      }

      // Validate required fields
      if (!eventData.title || !eventData.description || !eventData.date) {
        CustomToast.show('Please fill in all required fields', 'error');
        return;
      }

      setLoading(true);
      let imageUrl = eventData.imageUrl;

      // Handle image upload if file is provided
      if (eventData.imageFile) {
        try {
          const storageRef = ref(storage, `events/${selectedOrganization}/${Date.now()}_${eventData.imageFile.name}`);
          const uploadResult = await uploadBytes(storageRef, eventData.imageFile);
          imageUrl = await getDownloadURL(uploadResult.ref);
          console.log('Image uploaded successfully:', imageUrl);
        } catch (uploadError) {
          console.error('Error uploading image:', uploadError);
          CustomToast.show('Failed to upload image, but event will be created', 'warning');
        }
      }

      // Create event in dedicated events collection
      const newEvent = {
        title: eventData.title,
        description: eventData.description,
        eventDate: eventData.date,
        time: eventData.time,
        location: eventData.location,
        imageUrl: imageUrl,
        createdBy: currentUser?.uid || 'admin',
        createdAt: new Date(),
        organizationCode: selectedOrganization,
        status: 'upcoming', // Will be updated by mobile app based on date
        attendees: [],
        maxAttendees: null,
        isPublic: true,
        category: 'general'
      };

      const docRef = await addDoc(collection(db, 'events'), newEvent);
      console.log('Event created with ID:', docRef.id);

      // Log operation
      logOperation({
        type: 'create',
        contentType: 'event',
        contentId: docRef.id,
        contentTitle: eventData.title,
        organizationCode: selectedOrganization,
      });

      // Send notifications to all users in the organization
      try {
        // Query users by organizationCode, schoolCode, or classCode to catch all users
        const usersQuery1 = query(
          collection(db, 'users'),
          where('organizationCode', '==', selectedOrganization)
        );
        const usersQuery2 = query(
          collection(db, 'users'),
          where('schoolCode', '==', selectedOrganization)
        );
        const usersQuery3 = query(
          collection(db, 'users'),
          where('classCode', '==', selectedOrganization)
        );

        const [snapshot1, snapshot2, snapshot3] = await Promise.all([
          getDocs(usersQuery1),
          getDocs(usersQuery2),
          getDocs(usersQuery3)
        ]);

        // Combine all users and deduplicate by user ID
        const allUsers = new Map<string, any>();
        [snapshot1, snapshot2, snapshot3].forEach(snapshot => {
          snapshot.forEach((doc) => {
            if (!allUsers.has(doc.id)) {
              allUsers.set(doc.id, { id: doc.id, data: doc.data() });
            }
          });
        });

        // Create individual notification documents per user
        const batches: ReturnType<typeof writeBatch>[] = [writeBatch(db)];
        let currentBatchIndex = 0;
        let notificationCount = 0;
        const maxBatchSize = 500;

        for (const [userId, userInfo] of allUsers) {
          // Check for duplicate notification
          const existingNotificationsQuery = query(
            collection(db, 'notifications'),
            where('recipientId', '==', userId),
            where('type', '==', 'event'),
            where('eventId', '==', docRef.id),
            where('read', '==', false),
            limit(1)
          );
          const existingNotifications = await getDocs(existingNotificationsQuery);

          if (existingNotifications.empty) {
            // Check if current batch is full
            if (notificationCount > 0 && notificationCount % maxBatchSize === 0) {
              currentBatchIndex++;
              batches.push(writeBatch(db));
            }

            const notificationRef = doc(collection(db, 'notifications'));
            batches[currentBatchIndex].set(notificationRef, {
              recipientId: userId,
              senderId: currentUser?.uid || 'admin',
              eventId: docRef.id,
              title: `New Event: ${eventData.title}`,
              body: eventData.description?.length > 100
                ? `${eventData.description.substring(0, 100)}...`
                : eventData.description || 'New event has been added',
              type: 'event',
              timestamp: serverTimestamp(),
              read: false,
              processed: false, // Will be set to true when notification is shown
              organizationCode: selectedOrganization,
              eventDate: Timestamp.fromDate(eventData.date),
              location: eventData.location,
              imageUrl: eventData.imageUrl,
            });
            notificationCount++;
          }
        }

        // Commit all batches
        if (notificationCount > 0) {
          await Promise.all(batches.map(b => b.commit()));
          console.log(`Created ${notificationCount} event notifications`);
        }
      } catch (notifError) {
        console.error('Error sending event notifications:', notifError);
        // Don't fail event creation if notifications fail
      }

      // Reload data
      await loadData();
      setShowCreateEvent(false);
      CustomToast.show('Event created successfully!', 'success');
    } catch (error) {
      console.error('Error creating event:', error);
      CustomToast.show('Failed to create event', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateMenuItem = async (menuData: { name: string; description: string; category: string }) => {
    try {
      const newMenuItem = {
        name: menuData.name,
        description: menuData.description,
        category: menuData.category,
        imageUrl: 'assets/images/food.svg',
        rating: 0,
        ratingCount: 0,
        date: new Date(),
        uploadedBy: currentUser?.uid || 'admin',
        createdAt: new Date(),
        nutritionalInfo: {}
      };

      const docRef = await addDoc(collection(db, 'menu'), newMenuItem);
      console.log('Menu item created with ID:', docRef.id);

      // Reload data
      await loadData();
      setShowCreateMenuItem(false);
      CustomToast.show('Menu item created successfully!', 'success');
    } catch (error) {
      console.error('Error creating menu item:', error);
      CustomToast.show('Failed to create menu item', 'error');
    }
  };

  // Delete functions
  const handleDeletePost = async (postId: string) => {
    try {
      // Get post data before deletion for history
      const postDoc = await getDoc(doc(db, 'posts', postId));
      const postData = postDoc.data();

      await deleteDoc(doc(db, 'posts', postId));
      console.log('Post deleted:', postId);

      // Log operation
      if (postData) {
        logOperation({
          type: 'delete',
          contentType: 'post',
          contentId: postId,
          contentTitle: postData.textContent?.substring(0, 50) || 'Post',
          organizationCode: postData.organizationCode || selectedOrganization,
        });
      }

      // Reload data
      await loadData();
      CustomToast.show('Post deleted successfully!', 'success');
    } catch (error) {
      console.error('Error deleting post:', error);
      CustomToast.show('Failed to delete post', 'error');
    }
  };

  const handleDeleteEvent = async (eventId: string) => {
    try {
      // Get event data before deletion
      const eventDoc = await getDoc(doc(db, 'events', eventId));
      const eventData = eventDoc.data();

      await deleteDoc(doc(db, 'events', eventId));
      console.log('Event deleted:', eventId);

      // Log operation
      if (eventData) {
        logOperation({
          type: 'delete',
          contentType: 'event',
          contentId: eventId,
          contentTitle: eventData.title || 'Event',
          organizationCode: eventData.organizationCode || selectedOrganization,
        });
      }

      // Reload data
      await loadData();
      CustomToast.show('Event deleted successfully!', 'success');
    } catch (error) {
      console.error('Error deleting event:', error);
      CustomToast.show('Failed to delete event', 'error');
    }
  };

  const handleDeleteMenuItem = async (menuItemId: string, menuItemName?: string, menuItemCategory?: string) => {
    try {
      // Check if this is a batch menu item (format: batchMenuId_category_index)
      // Batch menu items have IDs like: "abc123_drinks_0"
      if (menuItemId.includes('_') && menuItemId.split('_').length >= 3) {
        const parts = menuItemId.split('_');
        // Last part is index, second to last is category, everything before is batchMenuId
        const index = parts.pop();
        const category = parts.pop();
        const batchMenuId = parts.join('_'); // Rejoin in case batchMenuId has underscores

        // Get the batch menu document
        const batchMenuDoc = await getDoc(doc(db, 'batchMenus', batchMenuId));
        if (!batchMenuDoc.exists()) {
          CustomToast.show('Batch menu not found', 'error');
          return;
        }

        const batchMenuData = batchMenuDoc.data();
        const menuItems = batchMenuData.menuItems || [];

        // Find and remove the menu item with matching category
        const updatedMenuItems = menuItems.filter((item: any) => item.category !== category);

        if (updatedMenuItems.length === menuItems.length) {
          CustomToast.show('Menu item not found in batch menu', 'error');
          return;
        }

        // Update the batch menu with the filtered items
        await updateDoc(doc(db, 'batchMenus', batchMenuId), {
          menuItems: updatedMenuItems,
          updatedAt: serverTimestamp(),
        });

        console.log('Menu item removed from batch menu:', menuItemId);

        // Log operation
        logOperation({
          type: 'delete',
          contentType: 'menu',
          contentId: menuItemId,
          contentTitle: `${menuItemName || 'Menu Item'} (${category})`,
          organizationCode: batchMenuData.organizationCode || selectedOrganization,
          details: {
            category: category,
            batchMenuId: batchMenuId,
          },
        });

        CustomToast.show('Menu item deleted from batch menu successfully!', 'success');
      } else {
        // Regular individual menu item - delete from 'menu' collection
        const menuItemDoc = await getDoc(doc(db, 'menu', menuItemId));
        if (!menuItemDoc.exists()) {
          CustomToast.show('Menu item not found', 'error');
          return;
        }

        const menuItemData = menuItemDoc.data();

        await deleteDoc(doc(db, 'menu', menuItemId));
        console.log('Menu item deleted:', menuItemId);

        // Log operation
        logOperation({
          type: 'delete',
          contentType: 'menu',
          contentId: menuItemId,
          contentTitle: `${menuItemData.name || 'Menu Item'} (${menuItemData.category || ''})`,
          organizationCode: menuItemData.organizationCode || selectedOrganization,
          details: {
            category: menuItemData.category,
          },
        });

        CustomToast.show('Menu item deleted successfully!', 'success');
      }

      // Reload data
      await loadData();
    } catch (error) {
      console.error('Error deleting menu item:', error);
      CustomToast.show('Failed to delete menu item', 'error');
    }
  };

  // Delete all menu items from Firebase (including batch menus)
  const handleDeleteAllMenuItems = async () => {
    try {
      // Get all menu items for the selected organization
      let itemsToDelete = menuItems;
      let batchMenusToDelete = batchMenus;

      if (selectedOrganization === 'all') {
        // If 'all' is selected, get all menu items and batch menus
        itemsToDelete = allMenuItems;
        batchMenusToDelete = allBatchMenus;
      }

      if (itemsToDelete.length === 0 && batchMenusToDelete.length === 0) {
        CustomToast.show('No menu items to delete', 'info');
        return;
      }

      // Separate individual menu items from batch menu items
      const individualItems = itemsToDelete.filter(item => !item.id.includes('_') || item.id.split('_').length < 3);

      let deletedCount = 0;
      let deletedBatchMenus = 0;

      // Delete individual menu items
      for (const item of individualItems) {
        try {
          await deleteDoc(doc(db, 'menu', item.id));
          deletedCount++;
        } catch (error) {
          console.error(`Error deleting menu item ${item.id}:`, error);
        }
      }

      // Delete all batch menus for the selected organization
      for (const batchMenu of batchMenusToDelete) {
        try {
          // Mark batch menu as deleted (same approach as handleDeleteBatchMenu)
          await updateDoc(doc(db, 'batchMenus', batchMenu.id), {
            status: 'deleted',
            deletedAt: serverTimestamp(),
            deletedBy: currentUser?.uid || 'admin',
          });

          // Count the items in this batch menu
          const menuItemsCount = batchMenu.menuItems?.length || 0;
          deletedCount += menuItemsCount;
          deletedBatchMenus++;
        } catch (error) {
          console.error(`Error deleting batch menu ${batchMenu.id}:`, error);
        }
      }

      // Log operation
      logOperation({
        type: 'delete',
        contentType: 'menu',
        contentId: 'all',
        contentTitle: `All Menu Items (${deletedCount} items, ${deletedBatchMenus} batch menus)`,
        organizationCode: selectedOrganization,
        details: {
          deletedCount: deletedCount,
          individualItems: individualItems.length,
          batchMenus: deletedBatchMenus,
        },
      });

      // Reload data
      await loadData();

      CustomToast.show(`Successfully deleted ${deletedCount} menu item(s) from ${deletedBatchMenus} batch menu(s)!`, 'success');
    } catch (error) {
      console.error('Error deleting all menu items:', error);
      CustomToast.show('Failed to delete all menu items', 'error');
    }
  };

  const handleDeleteBatchMenu = async (batchMenuId: string) => {
    try {
      // Get batch menu data before deletion
      const batchMenuDoc = await getDoc(doc(db, 'batchMenus', batchMenuId));
      const batchMenuData = batchMenuDoc.data();

      // Instead of deleting, mark as deleted with status flag
      // This ensures it won't show on mobile app (which filters by status === 'active')
      await updateDoc(doc(db, 'batchMenus', batchMenuId), {
        status: 'deleted',
        deletedAt: serverTimestamp(),
        deletedBy: currentUser?.uid || 'admin',
      });

      console.log('Batch menu marked as deleted:', batchMenuId);

      // Log operation
      if (batchMenuData) {
        logOperation({
          type: 'delete',
          contentType: 'batchMenu',
          contentId: batchMenuId,
          contentTitle: `Batch Menu (${batchMenuData.menuItems?.length || 0} items)`,
          organizationCode: batchMenuData.organizationCode || selectedOrganization,
          details: {
            startDate: batchMenuData.startDate,
            endDate: batchMenuData.endDate,
            itemCount: batchMenuData.menuItems?.length || 0,
          },
        });
      }

      // Reload data
      await loadData();
      CustomToast.show('Batch menu deleted successfully!', 'success');
    } catch (error) {
      console.error('Error deleting batch menu:', error);
      CustomToast.show('Failed to delete batch menu', 'error');
    }
  };

  const handleDeleteDiaryEntry = async (entryId: string) => {
    try {
      // Get the entry first to find associated replies
      const entryDoc = await getDoc(doc(db, 'diary_entries', entryId));
      if (!entryDoc.exists()) {
        CustomToast.show('Diary entry not found', 'error');
        return;
      }

      const entryData = entryDoc.data();

      // Delete associated replies
      const repliesSnapshot = await getDocs(query(collection(db, 'parent_replies'), where('diaryEntryId', '==', entryId)));
      for (const replyDoc of repliesSnapshot.docs) {
        await deleteDoc(replyDoc.ref);
      }

      // Delete the diary entry
      await deleteDoc(doc(db, 'diary_entries', entryId));
      console.log('Diary entry deleted:', entryId);

      // Log operation
      logOperation({
        type: 'delete',
        contentType: 'diary',
        contentId: entryId,
        contentTitle: entryData.type === 'personal'
          ? `Personal Note for ${entryData.studentName || 'Student'}`
          : 'General Note',
        organizationCode: entryData.organizationCode || selectedOrganization,
        details: {
          studentName: entryData.studentName,
          mood: entryData.mood,
          type: entryData.type,
        },
      });

      // Reload data
      await loadData();
      CustomToast.show('Diary entry deleted successfully!', 'success');
    } catch (error) {
      console.error('Error deleting diary entry:', error);
      CustomToast.show('Failed to delete diary entry', 'error');
    }
  };

  const handleCreateDiaryEntry = async (diaryData: {
    content: string;
    mood: string;
    type: string;
    studentName?: string;
    classroomId?: string;
    classroomName?: string;
  }) => {
    try {
      if (selectedOrganization === 'all') {
        CustomToast.show('Please select an organization first', 'error');
        return;
      }

      // Get a teacher from the selected classroom or organization
      let teacher;
      if (diaryData.classroomId) {
        // Find teacher for the specific classroom
        teacher = users.find(user =>
          user.role === 'teacher' &&
          (user.id === diaryData.classroomId || user.classGroup === diaryData.classroomId)
        );
      }

      if (!teacher) {
        // Fallback to any teacher in the organization
        teacher = users.find(user =>
          user.role === 'teacher' &&
          (user.organizationCode === selectedOrganization || user.schoolCode === selectedOrganization)
        );
      }

      if (!teacher) {
        CustomToast.show('No teacher found for this classroom', 'error');
        return;
      }

      const newDiaryEntry = {
        teacherId: teacher.id,
        teacherName: teacher.displayName,
        classId: diaryData.classroomId || selectedOrganization,
        content: diaryData.content,
        mood: diaryData.mood,
        type: diaryData.type,
        studentId: diaryData.type === 'personal' ? `student_${Date.now()}` : null,
        studentName: diaryData.studentName || null,
        timestamp: new Date(),
        parentReplies: [],
        isRead: false,
      };

      const docRef = await addDoc(collection(db, 'diary_entries'), newDiaryEntry);
      console.log('Diary entry created with ID:', docRef.id);

      // Log operation
      logOperation({
        type: 'create',
        contentType: 'diary',
        contentId: docRef.id,
        contentTitle: diaryData.type === 'personal'
          ? `Personal Note for ${diaryData.studentName || 'Student'}`
          : 'General Note',
        organizationCode: selectedOrganization,
        details: {
          studentName: diaryData.studentName,
          mood: diaryData.mood,
          type: diaryData.type,
        },
      });

      // Send notifications to parents in the classroom
      try {
        const classId = diaryData.classroomId || selectedOrganization;

        // Get classroom document to find parents
        const classDoc = await getDoc(doc(db, 'classes', classId));
        let parentIds: string[] = [];

        if (classDoc.exists()) {
          const classData = classDoc.data();
          // Get parents from class document
          const parents = classData?.parents || [];
          parentIds = parents.map((p: any) => typeof p === 'string' ? p : p.parentId || p.id).filter(Boolean);
        } else {
          // Fallback: Query users by organizationCode/classCode with role='parent'
          const parentsQuery1 = query(
            collection(db, 'users'),
            where('role', '==', 'parent'),
            where('organizationCode', '==', selectedOrganization)
          );
          const parentsQuery2 = query(
            collection(db, 'users'),
            where('role', '==', 'parent'),
            where('schoolCode', '==', selectedOrganization)
          );
          const parentsQuery3 = query(
            collection(db, 'users'),
            where('role', '==', 'parent'),
            where('classCode', '==', selectedOrganization)
          );

          const [snapshot1, snapshot2, snapshot3] = await Promise.all([
            getDocs(parentsQuery1),
            getDocs(parentsQuery2),
            getDocs(parentsQuery3)
          ]);

          const allParents = new Set<string>();
          [snapshot1, snapshot2, snapshot3].forEach(snapshot => {
            snapshot.forEach((doc) => {
              allParents.add(doc.id);
            });
          });
          parentIds = Array.from(allParents);
        }

        // For personal notes, only send to the specific student's parent
        if (diaryData.type === 'personal' && diaryData.studentName) {
          // Filter parents to only those who have this student
          // This is a simplified check - in production, you'd check the student's parentId
          // For now, send to all parents and let the mobile app filter
        }

        // Create individual notification documents per parent
        const batches: ReturnType<typeof writeBatch>[] = [writeBatch(db)];
        let currentBatchIndex = 0;
        let notificationCount = 0;
        const maxBatchSize = 500;

        const diaryTitle = diaryData.type === 'personal'
          ? `Personal note for ${diaryData.studentName || 'student'}`
          : `New diary entry from ${teacher.displayName}`;
        const diaryBody = diaryData.content.length > 100
          ? `${diaryData.content.substring(0, 100)}...`
          : diaryData.content;

        for (const parentId of parentIds) {
          // Check for duplicate notification
          const existingNotificationsQuery = query(
            collection(db, 'notifications'),
            where('recipientId', '==', parentId),
            where('type', '==', 'diary'),
            where('diaryEntryId', '==', docRef.id),
            where('read', '==', false),
            limit(1)
          );
          const existingNotifications = await getDocs(existingNotificationsQuery);

          if (existingNotifications.empty) {
            // Check if current batch is full
            if (notificationCount > 0 && notificationCount % maxBatchSize === 0) {
              currentBatchIndex++;
              batches.push(writeBatch(db));
            }

            const notificationRef = doc(collection(db, 'notifications'));
            batches[currentBatchIndex].set(notificationRef, {
              recipientId: parentId,
              senderId: teacher.id,
              title: diaryTitle,
              body: diaryBody,
              type: 'diary',
              diaryEntryId: docRef.id,
              studentId: diaryData.type === 'personal' ? `student_${Date.now()}` : null,
              studentName: diaryData.studentName || null,
              mood: diaryData.mood,
              timestamp: serverTimestamp(),
              read: false,
              processed: false, // Will be set to true when notification is shown
              showModal: true, // Auto-open modal when parent opens app
              organizationCode: selectedOrganization,
            });
            notificationCount++;
          }
        }

        // Commit all batches
        if (notificationCount > 0) {
          await Promise.all(batches.map(b => b.commit()));
          console.log(`Created ${notificationCount} diary notifications`);
        }
      } catch (notifError) {
        console.error('Error sending diary notifications:', notifError);
        // Don't fail diary creation if notifications fail
      }

      // Reload data
      await loadData();
      setShowCreateDiaryEntry(false);
      localStorage.removeItem('selectedDiaryClassroom');
      CustomToast.show(`Diary entry created successfully for ${diaryData.classroomName || 'classroom'}!`, 'success');
    } catch (error) {
      console.error('Error creating diary entry:', error);
      CustomToast.show('Failed to create diary entry', 'error');
    }
  };

  // Delete user function - Complete Firestore cleanup
  const handleDeleteUser = async (userId: string, userName: string) => {
    try {
      console.log(`[Admin] Starting deletion of user: ${userName} (ID: ${userId})`);

      // Get user data before deletion
      const userDoc = await getDoc(doc(db, 'users', userId));
      if (!userDoc.exists()) {
        CustomToast.show('User not found', 'error');
        return;
      }

      const userData = userDoc.data();
      const userEmail = userData.email;
      const userRole = userData.role;
      const classCode = userData.classGroup || userData.classCode;

      console.log(`[Admin] Deleting user ${userEmail} (${userRole})`);

      let totalDeleted = 0;
      const maxBatchSize = 500; // Firestore batch limit

      // ============================================================================
      // BATCH 1: Delete user-generated content
      // ============================================================================
      let batch = writeBatch(db);
      let batchCount = 0;

      // 1. Delete all posts by this user
      const postsSnapshot = await getDocs(query(collection(db, 'posts'), where('authorId', '==', userId)));
      for (const postDoc of postsSnapshot.docs) {
        if (batchCount >= maxBatchSize) {
          await batch.commit();
          batch = writeBatch(db);
          batchCount = 0;
        }
        batch.delete(postDoc.ref);
        batchCount++;
        totalDeleted++;
      }
      console.log(`[Admin] Queued ${postsSnapshot.docs.length} posts for deletion`);

      // 2. Delete all messages by this user
      const messagesSnapshot = await getDocs(query(collection(db, 'messages'), where('senderId', '==', userId)));
      for (const msgDoc of messagesSnapshot.docs) {
        if (batchCount >= maxBatchSize) {
          await batch.commit();
          batch = writeBatch(db);
          batchCount = 0;
        }
        batch.delete(msgDoc.ref);
        batchCount++;
        totalDeleted++;
      }
      console.log(`[Admin] Queued ${messagesSnapshot.docs.length} messages for deletion`);

      // 3. Remove user from conversations (don't delete conversations, just remove participant)
      const conversationsSnapshot = await getDocs(query(collection(db, 'conversations'), where('participants', 'array-contains', userId)));
      for (const convDoc of conversationsSnapshot.docs) {
        if (batchCount >= maxBatchSize) {
          await batch.commit();
          batch = writeBatch(db);
          batchCount = 0;
        }
        const convData = convDoc.data();
        const participants = convData.participants || [];
        const updatedParticipants = participants.filter((id: string) => id !== userId);

        // If only one participant left and it's a group, delete the group
        // Otherwise just remove the user
        if (updatedParticipants.length === 0 || (convData.isGroup && updatedParticipants.length === 1)) {
          batch.delete(convDoc.ref);
        } else {
          batch.update(convDoc.ref, {
            participants: updatedParticipants,
            updatedAt: Timestamp.now(),
          });
        }
        batchCount++;
        totalDeleted++;
      }
      console.log(`[Admin] Processed ${conversationsSnapshot.docs.length} conversations`);

      // 4. Delete all badges awarded to this user
      const badgesSnapshot = await getDocs(query(collection(db, 'badges'), where('userId', '==', userId)));
      for (const badgeDoc of badgesSnapshot.docs) {
        if (batchCount >= maxBatchSize) {
          await batch.commit();
          batch = writeBatch(db);
          batchCount = 0;
        }
        batch.delete(badgeDoc.ref);
        batchCount++;
        totalDeleted++;
      }
      console.log(`[Admin] Queued ${badgesSnapshot.docs.length} badges for deletion`);

      // 5. Delete all notifications for this user (both received and sent)
      const notificationsReceived = await getDocs(query(collection(db, 'notifications'), where('recipientId', '==', userId)));
      for (const notifDoc of notificationsReceived.docs) {
        if (batchCount >= maxBatchSize) {
          await batch.commit();
          batch = writeBatch(db);
          batchCount = 0;
        }
        batch.delete(notifDoc.ref);
        batchCount++;
        totalDeleted++;
      }

      const notificationsSent = await getDocs(query(collection(db, 'notifications'), where('senderId', '==', userId)));
      for (const notifDoc of notificationsSent.docs) {
        if (batchCount >= maxBatchSize) {
          await batch.commit();
          batch = writeBatch(db);
          batchCount = 0;
        }
        batch.delete(notifDoc.ref);
        batchCount++;
        totalDeleted++;
      }
      console.log(`[Admin] Queued ${notificationsReceived.docs.length + notificationsSent.docs.length} notifications for deletion`);

      // 6. Delete diary entries created by this user
      const diaryEntriesSnapshot = await getDocs(query(collection(db, 'diary_entries'), where('teacherId', '==', userId)));
      for (const diaryDoc of diaryEntriesSnapshot.docs) {
        if (batchCount >= maxBatchSize) {
          await batch.commit();
          batch = writeBatch(db);
          batchCount = 0;
        }
        batch.delete(diaryDoc.ref);
        batchCount++;
        totalDeleted++;
      }
      console.log(`[Admin] Queued ${diaryEntriesSnapshot.docs.length} diary entries for deletion`);

      // 7. Delete events created by this user
      const eventsSnapshot = await getDocs(query(collection(db, 'events'), where('createdBy', '==', userId)));
      for (const eventDoc of eventsSnapshot.docs) {
        if (batchCount >= maxBatchSize) {
          await batch.commit();
          batch = writeBatch(db);
          batchCount = 0;
        }
        batch.delete(eventDoc.ref);
        batchCount++;
        totalDeleted++;
      }
      console.log(`[Admin] Queued ${eventsSnapshot.docs.length} events for deletion`);

      // 8. Delete menu items uploaded by this user
      const menuItemsSnapshot = await getDocs(query(collection(db, 'menuItems'), where('uploadedBy', '==', userId)));
      for (const menuDoc of menuItemsSnapshot.docs) {
        if (batchCount >= maxBatchSize) {
          await batch.commit();
          batch = writeBatch(db);
          batchCount = 0;
        }
        batch.delete(menuDoc.ref);
        batchCount++;
        totalDeleted++;
      }
      console.log(`[Admin] Queued ${menuItemsSnapshot.docs.length} menu items for deletion`);

      // 9. Delete reports involving this user
      const reportsSnapshot = await getDocs(query(collection(db, 'reports'), where('reportedUserId', '==', userId)));
      for (const reportDoc of reportsSnapshot.docs) {
        if (batchCount >= maxBatchSize) {
          await batch.commit();
          batch = writeBatch(db);
          batchCount = 0;
        }
        batch.delete(reportDoc.ref);
        batchCount++;
        totalDeleted++;
      }

      const reportsByUser = await getDocs(query(collection(db, 'reports'), where('reporterId', '==', userId)));
      for (const reportDoc of reportsByUser.docs) {
        if (batchCount >= maxBatchSize) {
          await batch.commit();
          batch = writeBatch(db);
          batchCount = 0;
        }
        batch.delete(reportDoc.ref);
        batchCount++;
        totalDeleted++;
      }
      console.log(`[Admin] Queued ${reportsSnapshot.docs.length + reportsByUser.docs.length} reports for deletion`);

      // Commit first batch if there are operations
      if (batchCount > 0) {
        await batch.commit();
        console.log(`[Admin] Committed first batch: ${batchCount} operations`);
      }

      // ============================================================================
      // BATCH 2: Update related documents (classes, organizations)
      // ============================================================================
      batch = writeBatch(db);
      batchCount = 0;

      // 10. Remove user from class documents if they're a parent
      if (userRole === 'parent' && classCode) {
        const classesSnapshot = await getDocs(query(collection(db, 'classes'), where('classCode', '==', classCode)));
        for (const classDoc of classesSnapshot.docs) {
          if (batchCount >= maxBatchSize) {
            await batch.commit();
            batch = writeBatch(db);
            batchCount = 0;
          }
          const classData = classDoc.data();
          const parents = classData.parents || [];
          const updatedParents = parents.filter((parent: any) => parent.parentId !== userId);

          batch.update(classDoc.ref, {
            parents: updatedParents,
            updatedAt: Timestamp.now(),
          });
          batchCount++;
        }
        console.log(`[Admin] Removed user from ${classesSnapshot.docs.length} class documents`);
      }

      // 11. Remove user from organization counts
      if (userRole === 'teacher' && userData.schoolCode) {
        const orgSnapshot = await getDocs(query(collection(db, 'organizations'), where('code', '==', userData.schoolCode)));
        if (orgSnapshot.docs.length > 0) {
          const orgDoc = orgSnapshot.docs[0];
          const orgData = orgDoc.data();
          const currentTeacherCount = orgData.teacherCount || 0;

          batch.update(orgDoc.ref, {
            teacherCount: Math.max(0, currentTeacherCount - 1),
            updatedAt: Timestamp.now(),
          });
          batchCount++;
          console.log(`[Admin] Updated organization teacher count`);
        }
      } else if (userRole === 'parent' && classCode) {
        // For parents, find their teacher's organization
        const classesSnapshot = await getDocs(query(collection(db, 'classes'), where('classCode', '==', classCode)));
        if (classesSnapshot.docs.length > 0) {
          const classData = classesSnapshot.docs[0].data();
          const teacherId = classData.teacherId;

          if (teacherId) {
            const teacherDoc = await getDoc(doc(db, 'users', teacherId));
            if (teacherDoc.exists()) {
              const teacherData = teacherDoc.data();
              const schoolCode = teacherData.schoolCode;

              if (schoolCode) {
                const orgSnapshot = await getDocs(query(collection(db, 'organizations'), where('code', '==', schoolCode)));
                if (orgSnapshot.docs.length > 0) {
                  const orgDoc = orgSnapshot.docs[0];
                  const orgData = orgDoc.data();
                  const currentParentCount = orgData.parentCount || 0;

                  batch.update(orgDoc.ref, {
                    parentCount: Math.max(0, currentParentCount - 1),
                    updatedAt: Timestamp.now(),
                  });
                  batchCount++;
                  console.log(`[Admin] Updated organization parent count`);
                }
              }
            }
          }
        }
      }

      // 12. Mark user as deleted (don't actually delete - allows for recovery if needed)
      // Set deleted flag and clear sensitive data
      const adminUserId = currentUser?.uid || 'admin-dashboard';
      batch.update(doc(db, 'users', userId), {
        deleted: true,
        deletedAt: Timestamp.now(),
        deletedBy: adminUserId,
        email: `deleted_${Date.now()}@deleted.local`,
        displayName: '[Deleted User]',
        profilePicUrl: null,
        isLoggedOut: true,
        logoutReason: 'Account deleted by administrator',
        logoutTimestamp: Timestamp.now(),
        updatedAt: Timestamp.now(),
      });
      batchCount++;

      // Commit second batch
      if (batchCount > 0) {
        await batch.commit();
        console.log(`[Admin] Committed second batch: ${batchCount} operations`);
      }

      console.log(`[Admin] Successfully deleted user ${userEmail} and ${totalDeleted} associated records`);

      // Reload data
      await loadData();
      CustomToast.show(`User ${userName} and all associated data deleted successfully! (${totalDeleted} records)`, 'success');
    } catch (error) {
      console.error('[Admin] Error deleting user:', error);
      CustomToast.show(`Failed to delete user ${userName}: ${error instanceof Error ? error.message : 'Unknown error'}`, 'error');
    }
  };

  // Helper function to get institution name from code
  const getInstitutionName = (code: string) => {
    const org = organizations.find(org => org.code === code);
    return org ? org.name : code;
  };

  // Show user details modal
  const _showUserDetails = (user: User) => {
    // Get the organization code for this user
    const userOrgCode = user.organizationCode || user.schoolCode || user.classCode || user.institution || user.associatedClassroomId;
    const institutionName = getInstitutionName(userOrgCode || '');

    const modal = document.createElement('div');
    modal.className = 'custom-confirm-overlay';
    modal.style.cssText = `
      position: fixed;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      background: rgba(0, 0, 0, 0.5);
      display: flex;
      align-items: center;
      justify-content: center;
      z-index: 10000;
      font-family: 'Nunito', sans-serif;
    `;

    const modalContent = document.createElement('div');
    modalContent.className = 'custom-confirm-modal';
    modalContent.style.cssText = `
      background: white;
      border-radius: 16px;
      padding: 32px;
      max-width: 500px;
      width: 90%;
      text-align: left;
      box-shadow: 0 20px 40px rgba(0, 0, 0, 0.2);
      transform: scale(0.9);
      opacity: 0;
      transition: all 0.3s ease;
    `;

    const titleEl = document.createElement('h3');
    titleEl.textContent = 'User Details';
    titleEl.style.cssText = `
      margin: 0 0 24px 0;
      font-size: 20px;
      font-weight: 700;
      color: #303972;
      text-align: center;
    `;

    const detailsHtml = `
      <div style="margin-bottom: 16px;">
        <strong style="color: #8487A2; font-size: 14px;">Name:</strong>
        <div style="color: #303972; font-size: 16px; margin-top: 4px;">${user.displayName || 'Unknown User'}</div>
      </div>
      <div style="margin-bottom: 16px;">
        <strong style="color: #8487A2; font-size: 14px;">Email:</strong>
        <div style="color: #303972; font-size: 16px; margin-top: 4px;">${user.email || 'N/A'}</div>
      </div>
      <div style="margin-bottom: 16px;">
        <strong style="color: #8487A2; font-size: 14px;">Phone:</strong>
        <div style="color: #303972; font-size: 16px; margin-top: 4px;">${user.phone || 'N/A'}</div>
      </div>
      <div style="margin-bottom: 16px;">
        <strong style="color: #8487A2; font-size: 14px;">Role:</strong>
        <div style="color: #303972; font-size: 16px; margin-top: 4px;">${getStatusText(user.role)}</div>
      </div>
      <div style="margin-bottom: 16px;">
        <strong style="color: #8487A2; font-size: 14px;">Organization Code:</strong>
        <div style="color: #303972; font-size: 16px; margin-top: 4px;">${userOrgCode || 'N/A'}</div>
      </div>
      <div style="margin-bottom: 16px;">
        <strong style="color: #8487A2; font-size: 14px;">Institution:</strong>
        <div style="color: #303972; font-size: 16px; margin-top: 4px;">${institutionName}</div>
      </div>
      ${user.role === 'parent' && user.children && user.children.length > 0 ? `
      <div style="margin-bottom: 16px;">
        <strong style="color: #8487A2; font-size: 14px;">Children:</strong>
        <div style="color: #303972; font-size: 16px; margin-top: 4px;">${user.children.join(', ')}</div>
      </div>
      ` : ''}
      ${user.role === 'student' && user.parentName ? `
      <div style="margin-bottom: 16px;">
        <strong style="color: #8487A2; font-size: 14px;">Parent:</strong>
        <div style="color: #303972; font-size: 16px; margin-top: 4px;">${user.parentName}</div>
      </div>
      ` : ''}
      <div style="margin-bottom: 16px;">
        <strong style="color: #8487A2; font-size: 14px;">Joined:</strong>
        <div style="color: #303972; font-size: 16px; margin-top: 4px;">
          ${(() => {
        try {
          if (!user.createdAt) return 'N/A';

          // Handle different date formats
          if (user.createdAt.toDate && typeof user.createdAt.toDate === 'function') {
            // Firestore Timestamp
            return new Date(user.createdAt.toDate()).toLocaleDateString();
          } else if (user.createdAt instanceof Date) {
            // JavaScript Date
            return user.createdAt.toLocaleDateString();
          } else if (typeof user.createdAt === 'string') {
            // String date
            return new Date(user.createdAt).toLocaleDateString();
          } else if (user.createdAt.seconds) {
            // Firestore Timestamp with seconds
            return new Date(user.createdAt.seconds * 1000).toLocaleDateString();
          } else {
            // Fallback
            return 'N/A';
          }
        } catch (error) {
          console.error('Error formatting date:', error);
          return 'N/A';
        }
      })()}
        </div>
      </div>
    `;

    const detailsEl = document.createElement('div');
    detailsEl.innerHTML = detailsHtml;

    const buttonContainer = document.createElement('div');
    buttonContainer.style.cssText = `
      display: flex;
      justify-content: center;
      margin-top: 24px;
    `;

    const closeBtn = document.createElement('button');
    closeBtn.textContent = 'Close';
    closeBtn.style.cssText = `
      padding: 12px 24px;
      border: none;
      background: #5041BC;
      color: white;
      border-radius: 8px;
      font-size: 14px;
      font-weight: 600;
      cursor: pointer;
      transition: all 0.2s ease;
      font-family: 'Nunito', sans-serif;
    `;

    const deleteBtn = document.createElement('button');
    deleteBtn.textContent = 'Delete User';
    deleteBtn.style.cssText = `
      padding: 12px 24px;
      border: none;
      background: #EA8F95;
      color: white;
      border-radius: 8px;
      font-size: 14px;
      font-weight: 600;
      cursor: pointer;
      transition: all 0.2s ease;
      font-family: 'Nunito', sans-serif;
    `;

    // Hover effects
    closeBtn.onmouseenter = () => {
      closeBtn.style.background = '#3D2F9A';
      closeBtn.style.transform = 'scale(1.05)';
    };
    closeBtn.onmouseleave = () => {
      closeBtn.style.background = '#5041BC';
      closeBtn.style.transform = 'scale(1)';
    };

    deleteBtn.onmouseenter = () => {
      deleteBtn.style.background = '#D67A80';
      deleteBtn.style.transform = 'scale(1.05)';
    };
    deleteBtn.onmouseleave = () => {
      deleteBtn.style.background = '#EA8F95';
      deleteBtn.style.transform = 'scale(1)';
    };

    // Event handlers
    const closeModal = () => {
      modalContent.style.transform = 'scale(0.9)';
      modalContent.style.opacity = '0';
      modal.style.background = 'rgba(0, 0, 0, 0)';

      setTimeout(() => {
        document.body.removeChild(modal);
      }, 300);
    };

    closeBtn.onclick = closeModal;
    deleteBtn.onclick = async () => {
      const confirmed = await CustomConfirm.show(
        `Are you sure you want to delete user ${user.displayName}? This action cannot be undone.`,
        'Delete User'
      );
      if (confirmed) {
        handleDeleteUser(user.id, user.displayName);
      }
    };

    // Close on overlay click
    modal.onclick = (e) => {
      if (e.target === modal) {
        closeModal();
      }
    };

    // Escape key
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        closeModal();
        document.removeEventListener('keydown', handleEscape);
      }
    };
    document.addEventListener('keydown', handleEscape);

    // Assemble modal
    buttonContainer.appendChild(closeBtn);
    buttonContainer.appendChild(deleteBtn);
    modalContent.appendChild(titleEl);
    modalContent.appendChild(detailsEl);
    modalContent.appendChild(buttonContainer);
    modal.appendChild(modalContent);
    document.body.appendChild(modal);

    // Animate in
    setTimeout(() => {
      modalContent.style.transform = 'scale(1)';
      modalContent.style.opacity = '1';
    }, 100);
  };

  // Delete all test users function
  const handleDeleteAllTestUsers = async () => {
    try {
      const confirmed = await CustomConfirm.show(
        'Are you sure you want to delete ALL test users? This action cannot be undone and will affect all test accounts.',
        'Delete All Test Users'
      );

      if (!confirmed) return;

      console.log('Starting bulk deletion of test users...');

      // Get all users
      const usersQuery = query(collection(db, 'users'));
      const usersSnapshot = await getDocs(usersQuery);

      let deletedCount = 0;
      let errorCount = 0;

      for (const userDoc of usersSnapshot.docs) {
        const userData = userDoc.data();
        const userName = userData.displayName || userData.email || userDoc.id;

        // Check if this is a test user (you can customize this logic)
        const isTestUser =
          userName.toLowerCase().includes('test') ||
          userName.toLowerCase().includes('demo') ||
          userName.toLowerCase().includes('sample') ||
          userData.email?.toLowerCase().includes('test') ||
          userData.email?.toLowerCase().includes('demo') ||
          userData.email?.toLowerCase().includes('sample') ||
          userDoc.id.includes('test') ||
          userDoc.id.includes('demo');

        if (isTestUser) {
          try {
            await deleteDoc(doc(db, 'users', userDoc.id));
            console.log(`Deleted test user: ${userName}`);
            deletedCount++;
          } catch (error) {
            console.error(`Error deleting test user ${userName}:`, error);
            errorCount++;
          }
        }
      }

      // Reload data
      await loadData();

      if (errorCount > 0) {
        CustomToast.show(`Deleted ${deletedCount} test users. ${errorCount} errors occurred.`, 'info');
      } else {
        CustomToast.show(`Successfully deleted ${deletedCount} test users!`, 'success');
      }

    } catch (error) {
      console.error('Error in bulk deletion:', error);
      CustomToast.show('Failed to delete test users', 'error');
    }
  };

  const handleRunMigration = async () => {
    try {
      const confirmed = await CustomConfirm.show(
        'This will migrate all data to ensure proper organization structure. Continue?',
        'Run Organization Migration'
      );

      if (confirmed) {
        console.log('Starting organization migration...');
        const results = await OrganizationMigration.runCompleteMigration();

        if (results.users.success && results.posts.success && results.classes.success) {
          await loadData(); // Reload data after migration
          CustomToast.show('Migration completed successfully!', 'success');
        } else {
          CustomToast.show('Migration completed with some issues. Check console for details.', 'error');
        }
      }
    } catch (error) {
      console.error('Migration failed:', error);
      CustomToast.show('Migration failed. Check console for details.', 'error');
    }
  };

  const handleValidateStructure = async () => {
    try {
      console.log('Validating organization structure...');
      const result = await OrganizationMigration.validateOrgStructure();

      if (result.success) {
        CustomToast.show('✅ Organization structure is valid!', 'success');
      } else {
        CustomToast.show(`❌ Found ${result.issues?.length || 0} issues. Check console for details.`, 'error');
      }
    } catch (error) {
      console.error('Validation failed:', error);
      CustomToast.show('Validation failed. Check console for details.', 'error');
    }
  };

  const handleCleanupOrphanedClasses = async () => {
    try {
      console.log('Cleaning up orphaned class codes...');

      // Get all class documents
      const classesQuery = query(collection(db, 'classes'));
      const classesSnapshot = await getDocs(classesQuery);

      let deletedCount = 0;

      for (const classDoc of classesSnapshot.docs) {
        const classData = classDoc.data();

        // If this is a class code (has classCode but no schoolCode), check if its school exists
        if (classData.classCode && !classData.schoolCode) {
          // Check if the school (PEAR001) exists
          const schoolExists = organizations.some(org => org.code === 'PEAR001');

          if (!schoolExists) {
            // School doesn't exist, delete this orphaned class
            try {
              await deleteDoc(doc(db, 'classes', classDoc.id));
              console.log(`Deleted orphaned class: ${classData.classCode}`);
              deletedCount++;
            } catch (error) {
              console.error(`Error deleting orphaned class ${classData.classCode}:`, error);
            }
          }
        }
      }

      if (deletedCount > 0) {
        await loadData(); // Reload data after cleanup
        CustomToast.show(`Cleaned up ${deletedCount} orphaned class codes`, 'success');
      } else {
        CustomToast.show('No orphaned class codes found', 'info');
      }
    } catch (error) {
      console.error('Cleanup failed:', error);
      CustomToast.show('Cleanup failed. Check console for details.', 'error');
    }
  };

  // Function to migrate existing class documents to include organization codes
  const handleMigrateClassOrganizationCodes = async () => {
    try {
      console.log('=== MIGRATE CLASS ORGANIZATION CODES ===');

      // Get all class documents
      const classesSnapshot = await getDocs(collection(db, 'classes'));
      console.log(`Found ${classesSnapshot.docs.length} total class documents`);

      let migratedCount = 0;
      let skippedCount = 0;

      for (const classDoc of classesSnapshot.docs) {
        const classData = classDoc.data();
        const teacherId = classData.teacherId;

        if (!teacherId) {
          console.log(`Class ${classDoc.id} has no teacherId, skipping`);
          skippedCount++;
          continue;
        }

        // Check if class already has organization code
        if (classData.organizationCode) {
          console.log(`Class ${classDoc.id} already has organizationCode: ${classData.organizationCode}, skipping`);
          skippedCount++;
          continue;
        }

        // Get teacher data to find organization code
        const teacherDoc = await getDoc(doc(db, 'users', teacherId));
        if (!teacherDoc.exists()) {
          console.log(`Teacher ${teacherId} for class ${classDoc.id} doesn't exist, skipping`);
          skippedCount++;
          continue;
        }

        const teacherData = teacherDoc.data();
        if (!teacherData.schoolCode) {
          console.log(`Teacher ${teacherId} has no schoolCode, skipping`);
          skippedCount++;
          continue;
        }

        // Add organization code to class document
        console.log(`Adding organizationCode ${teacherData.schoolCode} to class ${classDoc.id}`);
        await updateDoc(classDoc.ref, {
          organizationCode: teacherData.schoolCode,
          updatedAt: serverTimestamp()
        });
        migratedCount++;
      }

      console.log(`Migration complete: ${migratedCount} classes migrated, ${skippedCount} classes skipped`);
      CustomToast.show(`Migration complete: ${migratedCount} classes migrated, ${skippedCount} classes skipped`, 'success');

    } catch (error) {
      console.error('Error migrating class organization codes:', error);
      CustomToast.show('Error migrating class organization codes', 'error');
    }
  };

  // Function to migrate existing parents to have correct schoolCode
  const handleMigrateParentSchoolCodes = async () => {
    try {
      console.log('=== MIGRATE PARENT SCHOOL CODES ===');

      // Get all parent users
      const usersSnapshot = await getDocs(collection(db, 'users'));
      console.log(`Found ${usersSnapshot.docs.length} total users`);

      let migratedCount = 0;
      let skippedCount = 0;

      for (const userDoc of usersSnapshot.docs) {
        const userData = userDoc.data();

        // Only process parent users
        if (userData.role !== 'parent') {
          skippedCount++;
          continue;
        }

        const currentSchoolCode = userData.schoolCode;
        const classCode = userData.classCode;

        console.log(`Processing parent ${userDoc.id}: current schoolCode=${currentSchoolCode}, classCode=${classCode}`);

        // Skip if already has correct schoolCode (matches organization code)
        if (currentSchoolCode && currentSchoolCode !== 'UNASSIGNED' && currentSchoolCode !== 'test1234') {
          // Check if this schoolCode matches any organization
          const matchingOrg = organizations.find(org => org.code === currentSchoolCode);
          if (matchingOrg) {
            console.log(`Parent ${userDoc.id} already has correct schoolCode: ${currentSchoolCode}, skipping`);
            skippedCount++;
            continue;
          } else {
            console.log(`Parent ${userDoc.id} has schoolCode ${currentSchoolCode} but no matching organization found, will migrate`);
          }
        }

        // Find the teacher's organization code from class document
        if (classCode) {
          try {
            console.log(`Looking for class with classCode: ${classCode}`);

            // Try multiple ways to find the class
            let classQuery = await getDocs(
              query(collection(db, 'classes'), where('classCode', '==', classCode))
            );

            // If not found, try with teacherId
            if (classQuery.empty) {
              console.log(`No class found with classCode ${classCode}, trying teacherId...`);
              classQuery = await getDocs(
                query(collection(db, 'classes'), where('teacherId', '==', userDoc.id))
              );
            }

            // If still not found, try with joinCode (old field name)
            if (classQuery.empty) {
              console.log(`No class found with teacherId, trying joinCode...`);
              classQuery = await getDocs(
                query(collection(db, 'classes'), where('joinCode', '==', classCode))
              );
            }

            if (!classQuery.empty) {
              const classData = classQuery.docs[0].data();
              console.log(`Found class document:`, classData);

              const organizationCode = classData.organizationCode;
              console.log(`Class organizationCode: ${organizationCode}`);

              if (organizationCode && organizationCode !== currentSchoolCode) {
                console.log(`Updating parent ${userDoc.id} schoolCode from ${currentSchoolCode} to ${organizationCode}`);

                await updateDoc(userDoc.ref, {
                  schoolCode: organizationCode,
                  updatedAt: serverTimestamp()
                });
                migratedCount++;
              } else {
                console.log(`No organization code found for class ${classCode} or already correct, skipping`);
                skippedCount++;
              }
            } else {
              console.log(`No class document found for classCode ${classCode} after trying all methods, skipping`);
              skippedCount++;
            }
          } catch (e) {
            console.log(`Error processing parent ${userDoc.id}: ${e}`);
            skippedCount++;
          }
        } else {
          console.log(`Parent ${userDoc.id} has no classCode, skipping`);
          skippedCount++;
        }
      }

      console.log(`Migration complete: ${migratedCount} parents migrated, ${skippedCount} parents skipped`);
      CustomToast.show(`Migration complete: ${migratedCount} parents migrated, ${skippedCount} parents skipped`, 'success');

      // Reload data to reflect changes
      await loadData();

    } catch (error) {
      console.error('Error migrating parent school codes:', error);
      CustomToast.show('Error migrating parent school codes', 'error');
    }
  };

  // Function to migrate existing conversations and notifications to include organization codes
  const handleMigrateConversationsAndNotifications = async () => {
    try {
      console.log('=== MIGRATE CONVERSATIONS AND NOTIFICATIONS ===');

      // Migrate conversations
      const conversationsSnapshot = await getDocs(collection(db, 'conversations'));
      console.log(`Found ${conversationsSnapshot.docs.length} total conversation documents`);

      let conversationsMigrated = 0;
      let conversationsSkipped = 0;

      for (const convDoc of conversationsSnapshot.docs) {
        const convData = convDoc.data();

        // Check if conversation already has organization code
        if (convData.organizationCode) {
          console.log(`Conversation ${convDoc.id} already has organizationCode: ${convData.organizationCode}, skipping`);
          conversationsSkipped++;
          continue;
        }

        // Get organization code from participants
        let organizationCode = null;
        if (convData.participants && convData.participants.length > 0) {
          for (const participantId of convData.participants) {
            try {
              const userDoc = await getDoc(doc(db, 'users', participantId));
              if (userDoc.exists()) {
                const userData = userDoc.data();
                if (userData) {
                  organizationCode = userData.schoolCode ?? userData.classCode ?? null;
                  if (organizationCode) break;
                }
              }
            } catch (e) {
              console.log(`Error getting user data for ${participantId}: ${e}`);
            }
          }
        }

        if (organizationCode) {
          console.log(`Adding organizationCode ${organizationCode} to conversation ${convDoc.id}`);
          await updateDoc(convDoc.ref, {
            organizationCode: organizationCode,
            updatedAt: serverTimestamp()
          });
          conversationsMigrated++;
        } else {
          console.log(`Could not find organization code for conversation ${convDoc.id}, skipping`);
          conversationsSkipped++;
        }
      }

      // Migrate notifications
      const notificationsSnapshot = await getDocs(collection(db, 'notifications'));
      console.log(`Found ${notificationsSnapshot.docs.length} total notification documents`);

      let notificationsMigrated = 0;
      let notificationsSkipped = 0;

      for (const notifDoc of notificationsSnapshot.docs) {
        const notifData = notifDoc.data();

        // Check if notification already has organization code
        if (notifData.organizationCode) {
          console.log(`Notification ${notifDoc.id} already has organizationCode: ${notifData.organizationCode}, skipping`);
          notificationsSkipped++;
          continue;
        }

        // Get organization code from recipient
        let organizationCode = null;
        if (notifData.recipientId) {
          try {
            const userDoc = await getDoc(doc(db, 'users', notifData.recipientId));
            if (userDoc.exists()) {
              const userData = userDoc.data();
              if (userData) {
                organizationCode = userData.schoolCode ?? userData.classCode ?? null;
              }
            }
          } catch (e) {
            console.log(`Error getting user data for notification ${notifDoc.id}: ${e}`);
          }
        }

        if (organizationCode) {
          console.log(`Adding organizationCode ${organizationCode} to notification ${notifDoc.id}`);
          await updateDoc(notifDoc.ref, {
            organizationCode: organizationCode,
            updatedAt: serverTimestamp()
          });
          notificationsMigrated++;
        } else {
          console.log(`Could not find organization code for notification ${notifDoc.id}, skipping`);
          notificationsSkipped++;
        }
      }

      console.log(`Migration complete:`);
      console.log(`- Conversations: ${conversationsMigrated} migrated, ${conversationsSkipped} skipped`);
      console.log(`- Notifications: ${notificationsMigrated} migrated, ${notificationsSkipped} skipped`);

      CustomToast.show(
        `Migration complete: ${conversationsMigrated} conversations, ${notificationsMigrated} notifications migrated`,
        'success'
      );

    } catch (error) {
      console.error('Error migrating conversations and notifications:', error);
      CustomToast.show('Error migrating conversations and notifications', 'error');
    }
  };

  const handleShowDeletedOrgs = () => {
    console.log('Currently deleted organizations:', Array.from(deletedOrganizations));
    CustomToast.show(`Deleted organizations: ${Array.from(deletedOrganizations).join(', ') || 'None'}`, 'info');
  };

  const handleForceClearDeletedOrgs = () => {
    setDeletedOrganizations(new Set());
    console.log('Force cleared deleted organizations list');
    CustomToast.show('Deleted organizations list cleared', 'info');
    loadData(); // Reload data after clearing
  };

  const handleTestDeletion = async () => {
    try {
      console.log('=== TESTING DELETION PROCESS ===');
      console.log('Current organizations:', organizations.map(org => ({ id: org.id, code: org.code, name: org.name })));
      console.log('Current deleted organizations:', Array.from(deletedOrganizations));
      console.log('Current selected organization:', selectedOrganization);

      // Find PEAR001 organization
      const pearOrg = organizations.find(org => org.code === 'PEAR001');
      if (pearOrg) {
        console.log('Found PEAR001 organization, testing deletion...');
        await handleDeleteOrganization(pearOrg.id);
      } else {
        console.log('PEAR001 organization not found in current organizations list');
      }
    } catch (error) {
      console.error('Test deletion failed:', error);
    }
  };

  const handleDeleteEverything = async () => {
    try {
      const confirmed = await CustomConfirm.show(
        '⚠️ WARNING: This will delete ALL data from the database!\n\n' +
        'This will permanently delete:\n' +
        '• All organizations\n' +
        '• All users\n' +
        '• All posts\n' +
        '• All events\n' +
        '• All menu items\n' +
        '• All classes\n\n' +
        'This action cannot be undone. Are you sure?',
        'Delete Everything'
      );

      if (!confirmed) return;

      console.log('=== DELETING EVERYTHING FROM DATABASE ===');

      // Clear all state immediately
      setOrganizations([]);
      setUsers([]);
      setPosts([]);
      setMenuItems([]);
      setEvents([]);
      setDeletedOrganizations(new Set());
      setSelectedOrganization('all');

      // Delete all organizations
      const orgsQuery = query(collection(db, 'organizations'));
      const orgsSnapshot = await getDocs(orgsQuery);
      console.log(`Deleting ${orgsSnapshot.docs.length} organizations...`);
      for (const doc of orgsSnapshot.docs) {
        await deleteDoc(doc.ref);
        console.log(`Deleted organization: ${doc.data().name || doc.id}`);
      }

      // Delete all users
      const usersQuery = query(collection(db, 'users'));
      const usersSnapshot = await getDocs(usersQuery);
      console.log(`Deleting ${usersSnapshot.docs.length} users...`);
      for (const doc of usersSnapshot.docs) {
        await deleteDoc(doc.ref);
        console.log(`Deleted user: ${doc.data().displayName || doc.id}`);
      }

      // Delete all posts
      const postsQuery = query(collection(db, 'posts'));
      const postsSnapshot = await getDocs(postsQuery);
      console.log(`Deleting ${postsSnapshot.docs.length} posts...`);
      for (const doc of postsSnapshot.docs) {
        await deleteDoc(doc.ref);
        console.log(`Deleted post: ${doc.id}`);
      }

      // Delete all events
      const eventsQuery = query(collection(db, 'events'));
      const eventsSnapshot = await getDocs(eventsQuery);
      console.log(`Deleting ${eventsSnapshot.docs.length} events...`);
      for (const doc of eventsSnapshot.docs) {
        await deleteDoc(doc.ref);
        console.log(`Deleted event: ${doc.id}`);
      }

      // Delete all menu items
      const menuQuery = query(collection(db, 'menu'));
      const menuSnapshot = await getDocs(menuQuery);
      console.log(`Deleting ${menuSnapshot.docs.length} menu items...`);
      for (const doc of menuSnapshot.docs) {
        await deleteDoc(doc.ref);
        console.log(`Deleted menu item: ${doc.id}`);
      }

      // Delete all classes
      const classesQuery = query(collection(db, 'classes'));
      const classesSnapshot = await getDocs(classesQuery);
      console.log(`Deleting ${classesSnapshot.docs.length} classes...`);
      for (const doc of classesSnapshot.docs) {
        await deleteDoc(doc.ref);
        console.log(`Deleted class: ${doc.data().classCode || doc.id}`);
      }

      console.log('=== DATABASE COMPLETELY CLEARED ===');
      CustomToast.show('Database completely cleared! All data deleted.', 'success');

      // Reload data to confirm everything is gone
      await loadData();

    } catch (error) {
      console.error('Error deleting everything:', error);
      CustomToast.show('Error deleting data. Check console for details.', 'error');
    }
  };

  // Subscription Management Functions
  const checkSubscriptionExpiration = async () => {
    try {
      console.log('=== CHECKING SUBSCRIPTION EXPIRATIONS ===');

      const orgsQuery = query(collection(db, 'organizations'));
      const orgsSnapshot = await getDocs(orgsQuery);

      for (const doc of orgsSnapshot.docs) {
        const orgData = doc.data();
        const expirationDate = orgData.expirationDate?.toDate();
        const email = orgData.email;
        const name = orgData.name;
        const code = orgData.code;

        if (!expirationDate || !email) continue;

        const now = new Date();
        const daysUntilExpiration = Math.ceil((expirationDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));

        console.log(`Organization ${code} (${name}): ${daysUntilExpiration} days until expiration`);

        // If expired, set grace period instead of immediate logout
        if (daysUntilExpiration <= 0 && orgData.isActive) {
          console.log(`Subscription expired for organization: ${code}`);

          // Check if grace period is already set
          const gracePeriodUntil = orgData.gracePeriodUntil?.toDate();
          const now = new Date();

          if (!gracePeriodUntil || now > gracePeriodUntil) {
            // Set grace period (7 days from expiration date)
            const gracePeriodEnd = new Date(expirationDate);
            gracePeriodEnd.setDate(gracePeriodEnd.getDate() + 7);

            console.log(`Setting grace period for organization: ${code} until ${gracePeriodEnd}`);
            await updateDoc(doc.ref, {
              isActive: false,
              subscriptionStatus: 'expired',
              gracePeriodUntil: gracePeriodEnd
            });

            // Send expiration email with grace period info
            await sendSubscriptionExpiredEmail(email, name, code);
          } else {
            // Grace period already set, check if it has expired
            if (now > gracePeriodUntil) {
              console.log(`Grace period expired for organization: ${code}, archiving and logging out users`);

              // Archive organization data before logging out users
              try {
                await archiveOrganizationData(code);
                console.log(`Organization ${code} data archived successfully`);
              } catch (archiveError) {
                console.error(`Error archiving organization ${code}:`, archiveError);
                // Continue with logout even if archiving fails
              }

              await updateDoc(doc.ref, {
                isActive: false,
                subscriptionStatus: 'expired'
              });

              // Log out all users from this organization
              await logoutUsersFromOrganization(code);
            } else {
              console.log(`Organization ${code} is in grace period until ${gracePeriodUntil}`);
            }
          }

        } else if (daysUntilExpiration <= 7 && daysUntilExpiration > 0) {
          // Send warning email 1 week before expiration
          console.log(`Sending warning email for organization: ${code}`);
          await sendSubscriptionWarningEmail(email, name, code, daysUntilExpiration);
        }
      }

      console.log('=== SUBSCRIPTION EXPIRATION CHECK COMPLETE ===');
    } catch (error) {
      console.error('Error checking subscription expiration:', error);
    }
  };

  const sendSubscriptionWarningEmail = async (email: string, orgName: string, orgCode: string, daysLeft: number) => {
    try {
      // This would integrate with your email service (Firebase Functions, SendGrid, etc.)
      console.log(`Sending warning email to ${email} for ${orgName} (${orgCode}) - ${daysLeft} days left`);

      // For now, just log the email that would be sent
      const emailContent = {
        to: email,
        subject: `Subscription Expiring Soon - ${orgName}`,
        body: `Dear ${orgName} Administrator,

Your PEAR subscription for ${orgName} (${orgCode}) will expire in ${daysLeft} days.

To ensure uninterrupted service, please renew your subscription before the expiration date.

If you have any questions, please contact our support team.

Best regards,
PEAR Team`
      };

      console.log('Email content:', emailContent);
      // TODO: Integrate with actual email service

    } catch (error) {
      console.error('Error sending warning email:', error);
    }
  };

  const sendSubscriptionExpiredEmail = async (email: string, orgName: string, orgCode: string) => {
    try {
      console.log(`Sending expiration email to ${email} for ${orgName} (${orgCode})`);

      const emailContent = {
        to: email,
        subject: `Subscription Expired - ${orgName}`,
        body: `Dear ${orgName} Administrator,

Your PEAR subscription for ${orgName} (${orgCode}) has expired.

Your organization has been placed in a 7-day grace period. During this time, users can still access the platform, but you must renew your subscription before the grace period ends to avoid service interruption.

After the grace period expires, all users will be logged out and access will be restricted until renewal.

To restore full access, please renew your subscription as soon as possible.

If you have any questions, please contact our support team.

Best regards,
PEAR Team`
      };

      console.log('Email content:', emailContent);
      // TODO: Integrate with actual email service

    } catch (error) {
      console.error('Error sending expiration email:', error);
    }
  };

  const sendOrganizationWelcomeEmail = async (email: string, orgName: string, orgCode: string) => {
    try {
      console.log(`Sending welcome email to ${email} for ${orgName} (${orgCode})`);

      const emailContent = {
        to: email,
        subject: `Welcome to PEAR - ${orgName}`,
        body: `Dear ${orgName} Administrator,

Welcome to PEAR! Your organization "${orgName}" (${orgCode}) has been successfully created.

Your organization code is: ${orgCode}

Please share this code with your teachers so they can sign up and create their classrooms. Teachers will then provide class codes to parents for registration.

Important Information:
- Your subscription is currently active
- You can manage your organization through the admin dashboard
- Contact support if you need any assistance

Best regards,
PEAR Team`
      };

      console.log('Welcome email content:', emailContent);
      // TODO: Integrate with actual email service

    } catch (error) {
      console.error('Error sending welcome email:', error);
    }
  };

  // Archive organization data before expiration
  const archiveOrganizationData = async (orgCode: string) => {
    try {
      console.log(`[Archive] Starting archive for organization: ${orgCode}`);
      const archiveTimestamp = Timestamp.now();
      const archiveId = `archive_${orgCode}_${archiveTimestamp.toMillis()}`;

      const archiveData: any = {
        organizationCode: orgCode,
        archivedAt: archiveTimestamp,
        archivedBy: currentUser?.uid || 'admin-dashboard',
        version: '1.0',
      };

      // 1. Archive organization document
      const orgDoc = await getDoc(doc(db, 'organizations', orgCode));
      if (orgDoc.exists()) {
        archiveData.organization = orgDoc.data();
        console.log(`[Archive] Organization document archived`);
      }

      // 2. Archive users
      const usersSnapshot = await getDocs(query(
        collection(db, 'users'),
        where('schoolCode', '==', orgCode)
      ));
      archiveData.users = usersSnapshot.docs.map(userDoc => ({
        id: userDoc.id,
        data: userDoc.data(),
      }));
      console.log(`[Archive] Archived ${archiveData.users.length} users`);

      // 3. Archive classes
      const classesSnapshot = await getDocs(query(
        collection(db, 'classes'),
        where('organizationCode', '==', orgCode)
      ));
      archiveData.classes = classesSnapshot.docs.map(classDoc => ({
        id: classDoc.id,
        data: classDoc.data(),
      }));
      console.log(`[Archive] Archived ${archiveData.classes.length} classes`);

      // 4. Archive conversations
      const conversationsSnapshot = await getDocs(query(
        collection(db, 'conversations'),
        where('organizationCode', '==', orgCode)
      ));
      archiveData.conversations = conversationsSnapshot.docs.map(convDoc => ({
        id: convDoc.id,
        data: convDoc.data(),
      }));
      console.log(`[Archive] Archived ${archiveData.conversations.length} conversations`);

      // 5. Archive posts
      const postsSnapshot = await getDocs(query(
        collection(db, 'posts'),
        where('organizationCode', '==', orgCode)
      ));
      archiveData.posts = postsSnapshot.docs.map(postDoc => ({
        id: postDoc.id,
        data: postDoc.data(),
      }));
      console.log(`[Archive] Archived ${archiveData.posts.length} posts`);

      // 6. Archive notifications
      const notificationsSnapshot = await getDocs(query(
        collection(db, 'notifications'),
        where('organizationCode', '==', orgCode)
      ));
      archiveData.notifications = notificationsSnapshot.docs.map(notifDoc => ({
        id: notifDoc.id,
        data: notifDoc.data(),
      }));
      console.log(`[Archive] Archived ${archiveData.notifications.length} notifications`);

      // Save archive to Firestore
      await setDoc(doc(db, 'organization_archives', archiveId), archiveData);

      // Mark organization as archived
      if (orgDoc.exists()) {
        await updateDoc(doc(db, 'organizations', orgCode), {
          archived: true,
          archivedAt: archiveTimestamp,
          archiveId: archiveId,
          updatedAt: Timestamp.now(),
        });
      }

      console.log(`[Archive] Organization ${orgCode} archived successfully! Archive ID: ${archiveId}`);
      return archiveId;
    } catch (error) {
      console.error(`[Archive] Error archiving organization ${orgCode}:`, error);
      throw error;
    }
  };

  // Restore organization data when subscription is renewed
  const restoreOrganizationData = async (orgCode: string) => {
    try {
      console.log(`[Restore] Starting restore for organization: ${orgCode}`);

      // Find the latest archive for this organization
      const archivesQuery = query(
        collection(db, 'organization_archives'),
        where('organizationCode', '==', orgCode),
        orderBy('archivedAt', 'desc'),
        limit(1)
      );
      const archivesSnapshot = await getDocs(archivesQuery);

      if (archivesSnapshot.empty) {
        console.log(`[Restore] No archive found for organization: ${orgCode}`);
        return false;
      }

      const archiveDoc = archivesSnapshot.docs[0];
      const archiveData = archiveDoc.data();
      console.log(`[Restore] Found archive: ${archiveDoc.id}`);

      const batch = writeBatch(db);
      let restoreCount = 0;

      // 1. Restore organization document
      if (archiveData.organization) {
        const orgRef = doc(db, 'organizations', orgCode);
        const orgData = { ...archiveData.organization };
        delete orgData.archived;
        delete orgData.archivedAt;
        delete orgData.archiveId;
        orgData.updatedAt = Timestamp.now();
        batch.set(orgRef, orgData, { merge: true });
        restoreCount++;
      }

      // 2. Restore users (in batches if needed)
      if (archiveData.users && archiveData.users.length > 0) {
        for (const user of archiveData.users) {
          if (restoreCount >= 500) {
            await batch.commit();
            restoreCount = 0;
          }
          const userRef = doc(db, 'users', user.id);
          const userData = { ...user.data };
          delete userData.isLoggedOut;
          delete userData.logoutReason;
          delete userData.logoutTimestamp;
          userData.updatedAt = Timestamp.now();
          batch.set(userRef, userData, { merge: true });
          restoreCount++;
        }
        console.log(`[Restore] Queued ${archiveData.users.length} users for restoration`);
      }

      // 3. Restore classes
      if (archiveData.classes && archiveData.classes.length > 0) {
        for (const classItem of archiveData.classes) {
          if (restoreCount >= 500) {
            await batch.commit();
            restoreCount = 0;
          }
          const classRef = doc(db, 'classes', classItem.id);
          const classData = { ...classItem.data };
          classData.updatedAt = Timestamp.now();
          batch.set(classRef, classData, { merge: true });
          restoreCount++;
        }
        console.log(`[Restore] Queued ${archiveData.classes.length} classes for restoration`);
      }

      // 4. Restore conversations
      if (archiveData.conversations && archiveData.conversations.length > 0) {
        for (const conv of archiveData.conversations) {
          if (restoreCount >= 500) {
            await batch.commit();
            restoreCount = 0;
          }
          const convRef = doc(db, 'conversations', conv.id);
          const convData = { ...conv.data };
          convData.updatedAt = Timestamp.now();
          batch.set(convRef, convData, { merge: true });
          restoreCount++;
        }
        console.log(`[Restore] Queued ${archiveData.conversations.length} conversations for restoration`);
      }

      // 5. Restore posts
      if (archiveData.posts && archiveData.posts.length > 0) {
        for (const post of archiveData.posts) {
          if (restoreCount >= 500) {
            await batch.commit();
            restoreCount = 0;
          }
          const postRef = doc(db, 'posts', post.id);
          const postData = { ...post.data };
          postData.updatedAt = Timestamp.now();
          batch.set(postRef, postData, { merge: true });
          restoreCount++;
        }
        console.log(`[Restore] Queued ${archiveData.posts.length} posts for restoration`);
      }

      // 6. Restore notifications
      if (archiveData.notifications && archiveData.notifications.length > 0) {
        for (const notif of archiveData.notifications) {
          if (restoreCount >= 500) {
            await batch.commit();
            restoreCount = 0;
          }
          const notifRef = doc(db, 'notifications', notif.id);
          const notifData = { ...notif.data };
          notifData.updatedAt = Timestamp.now();
          batch.set(notifRef, notifData, { merge: true });
          restoreCount++;
        }
        console.log(`[Restore] Queued ${archiveData.notifications.length} notifications for restoration`);
      }

      // Commit final batch
      if (restoreCount > 0) {
        await batch.commit();
      }

      // Clear archive flag
      await updateDoc(doc(db, 'organizations', orgCode), {
        archived: deleteField(),
        archivedAt: deleteField(),
        archiveId: deleteField(),
        updatedAt: Timestamp.now(),
      });

      console.log(`[Restore] Organization ${orgCode} restored successfully!`);
      return true;
    } catch (error) {
      console.error(`[Restore] Error restoring organization ${orgCode}:`, error);
      throw error;
    }
  };

  const logoutUsersFromOrganization = async (orgCode: string) => {
    try {
      console.log(`Logging out users from organization: ${orgCode}`);

      const logoutReason = 'Your organization subscription grace period has ended. Please contact your administrator to renew.';

      // Find all users belonging to this organization directly
      const usersQuery = query(
        collection(db, 'users'),
        where('schoolCode', '==', orgCode)
      );
      const usersSnapshot = await getDocs(usersQuery);

      const batch = writeBatch(db);
      let logoutCount = 0;

      // Log out direct organization users
      for (const doc of usersSnapshot.docs) {
        batch.update(doc.ref, {
          isLoggedOut: true,
          logoutReason: logoutReason,
          logoutTimestamp: Timestamp.now()
        });
        logoutCount++;
        console.log(`Queued logout for user: ${doc.data().displayName}`);
      }

      // Find all classrooms under this organization
      const classesQuery = query(collection(db, 'classes'));
      const classesSnapshot = await getDocs(classesQuery);
      const classroomCodes = new Set<string>();

      for (const classDoc of classesSnapshot.docs) {
        const classData = classDoc.data();
        const classOrgCode = classData.organizationCode || classData.schoolCode;
        const teacherId = classData.teacherId;

        // Check if classroom belongs to this organization
        if (classOrgCode === orgCode) {
          classroomCodes.add(classData.classCode);
        } else if (teacherId) {
          // Check teacher's organization
          try {
            const teacherDoc = await getDoc(doc(db, 'users', teacherId));
            if (teacherDoc.exists()) {
              const teacherData = teacherDoc.data();
              if (teacherData.schoolCode === orgCode || teacherData.organizationCode === orgCode) {
                classroomCodes.add(classData.classCode);
              }
            }
          } catch (e) {
            console.error(`Error checking teacher ${teacherId}:`, e);
          }
        }
      }

      console.log(`Found ${classroomCodes.size} classrooms under organization ${orgCode}`);

      // Log out all parents and students in these classrooms
      if (classroomCodes.size > 0) {
        const allUsersQuery = query(collection(db, 'users'));
        const allUsersSnapshot = await getDocs(allUsersQuery);

        for (const userDoc of allUsersSnapshot.docs) {
          const userData = userDoc.data();
          const userClassCode = userData.classCode;

          // Check if user belongs to any classroom under this organization
          if (userClassCode && classroomCodes.has(userClassCode)) {
            batch.update(userDoc.ref, {
              isLoggedOut: true,
              logoutReason: logoutReason,
              logoutTimestamp: Timestamp.now()
            });
            logoutCount++;
            console.log(`Queued logout for classroom user: ${userData.displayName} (classroom: ${userClassCode})`);
          }
        }
      }

      // Commit all logout operations
      await batch.commit();
      console.log(`Successfully logged out ${logoutCount} users from organization: ${orgCode}`);


    } catch (error) {
      console.error('Error logging out users:', error);
    }
  };

  // Run subscription check on component mount
  useEffect(() => {
    checkSubscriptionExpiration();

    // Check every hour
    const interval = setInterval(checkSubscriptionExpiration, 60 * 60 * 1000);

    return () => clearInterval(interval);
  }, []);

  // Function to load classrooms for an organization
  const loadOrganizationClassrooms = async (orgCode: string) => {
    setLoadingClassrooms(true);
    try {
      console.log(`[Admin Dashboard] Loading classrooms for organization: ${orgCode}`);

      const classrooms: any[] = [];

      // Method 1: Get all classes and filter by organization
      try {
        const classesQuery = query(collection(db, 'classes'));
        const classesSnapshot = await getDocs(classesQuery);

        console.log(`[Admin Dashboard] Found ${classesSnapshot.docs.length} total classes`);

        for (const classDoc of classesSnapshot.docs) {
          const classData = classDoc.data();
          const teacherId = classData.teacherId;

          // Get teacher details
          let teacherData = null;
          if (teacherId) {
            try {
              const teacherDoc = await getDoc(doc(db, 'users', teacherId));
              if (teacherDoc.exists()) {
                teacherData = teacherDoc.data();
              }
            } catch (error) {
              console.error(`Error fetching teacher data for ${teacherId}:`, error);
            }
          }

          const parents = classData.parents || [];
          const students = classData.students || [];

          // Count total students from all parents
          let totalStudents = 0;
          for (const parent of parents) {
            const parentChildren = parent.children || [];
            totalStudents += parentChildren.length;
          }

          // Check if this class belongs to the organization
          const classOrgCode = classData.organizationCode || classData.schoolCode || classData.code;
          // STRICT filtering: only include if organization code matches exactly
          if (classOrgCode === orgCode) {
            classrooms.push({
              id: classDoc.id,
              className: classData.name || classData.className || `Class ${classDoc.id.substring(0, 8)}`,
              classCode: classData.joinCode || classData.code || classData.classCode || classDoc.id,
              teacherName: teacherData?.displayName || classData.teacherName || 'Unknown Teacher',
              teacherEmail: teacherData?.email || 'No Email',
              teacherPhone: teacherData?.phone || 'No Phone',
              parentCount: parents.length,
              studentCount: totalStudents,
              createdAt: classData.createdAt,
              isActive: classData.isActive !== false, // Default to true if not specified
              parents: parents,
              students: students,
              organizationCode: classData.organizationCode || orgCode,
              yearGroup: classData.yearGroup || null, // Include year group for grouping
            });
          }
        }

        console.log(`[Admin Dashboard] Found ${classrooms.length} classes for organization: ${orgCode}`);
      } catch (error) {
        console.error(`Error fetching classes:`, error);
      }

      // Method 2: If no classes found, try teacher-based approach
      if (classrooms.length === 0) {
        console.log(`[Admin Dashboard] No classes found by direct approach, trying teacher-based approach`);

        try {
          // Get all teachers in this organization (try multiple field names)
          const teachersQuery = query(
            collection(db, 'users'),
            where('role', '==', 'teacher')
          );
          const teachersSnapshot = await getDocs(teachersQuery);

          console.log(`[Admin Dashboard] Found ${teachersSnapshot.docs.length} total teachers`);

          for (const teacherDoc of teachersSnapshot.docs) {
            const teacherData = teacherDoc.data();
            const teacherId = teacherDoc.id;

            // Check if this teacher belongs to the organization
            const teacherOrgCode = teacherData.schoolCode || teacherData.organizationCode || teacherData.classCode;
            // STRICT filtering: only include if teacher's organization code matches exactly
            if (teacherOrgCode === orgCode) {
              console.log(`[Admin Dashboard] Teacher ${teacherData.displayName} belongs to organization ${orgCode}`);

              // Get classes for this teacher
              const teacherClassesQuery = query(
                collection(db, 'classes'),
                where('teacherId', '==', teacherId)
              );
              const teacherClassesSnapshot = await getDocs(teacherClassesQuery);

              for (const classDoc of teacherClassesSnapshot.docs) {
                const classData = classDoc.data();
                const parents = classData.parents || [];
                const students = classData.students || [];

                // Count total students from all parents
                let totalStudents = 0;
                for (const parent of parents) {
                  const parentChildren = parent.children || [];
                  totalStudents += parentChildren.length;
                }

                classrooms.push({
                  id: classDoc.id,
                  className: classData.name || classData.className || `Class ${classDoc.id.substring(0, 8)}`,
                  classCode: classData.joinCode || classData.code || classData.classCode || classDoc.id,
                  teacherName: teacherData.displayName || 'Unknown Teacher',
                  teacherEmail: teacherData.email || 'No Email',
                  teacherPhone: teacherData.phone || 'No Phone',
                  parentCount: parents.length,
                  studentCount: totalStudents,
                  createdAt: classData.createdAt,
                  isActive: classData.isActive !== false,
                  parents: parents,
                  students: students,
                  organizationCode: classData.organizationCode || orgCode,
                  yearGroup: classData.yearGroup || null, // Include year group for grouping
                });
              }
            }
          }

          console.log(`[Admin Dashboard] Found ${classrooms.length} classes via teacher approach`);
        } catch (error) {
          console.error(`Error fetching classes by teacher approach:`, error);
        }
      }

      // Method 3: Try calendar events to find classrooms
      if (classrooms.length === 0) {
        console.log(`[Admin Dashboard] No classes found by teacher approach, trying calendar events`);

        try {
          const calendarEventsQuery = query(
            collection(db, 'calendar_events'),
            where('organizationId', '==', orgCode)
          );
          const calendarEventsSnapshot = await getDocs(calendarEventsQuery);

          console.log(`[Admin Dashboard] Found ${calendarEventsSnapshot.docs.length} calendar events for organization: ${orgCode}`);

          // Group events by classroomId
          const classroomEvents = new Map();
          for (const eventDoc of calendarEventsSnapshot.docs) {
            const eventData = eventDoc.data();
            const classroomId = eventData.classroomId;

            if (classroomId && !classroomEvents.has(classroomId)) {
              classroomEvents.set(classroomId, {
                id: classroomId,
                className: `Class ${classroomId.substring(0, 8)}`,
                classCode: classroomId,
                teacherName: 'Unknown Teacher',
                teacherEmail: 'No Email',
                teacherPhone: 'No Phone',
                parentCount: 0,
                studentCount: 0,
                createdAt: eventData.createdAt,
                isActive: true,
                parents: [],
                students: [],
                organizationCode: orgCode,
                eventCount: 1,
              });
            } else if (classroomId) {
              const existing = classroomEvents.get(classroomId);
              existing.eventCount = (existing.eventCount || 0) + 1;
            }
          }

          // Convert to array
          classroomEvents.forEach((classroom, classroomId) => {
            classrooms.push(classroom);
          });
        } catch (error) {
          console.error(`Error fetching calendar events:`, error);
        }
      }

      console.log(`[Admin Dashboard] Total classrooms loaded: ${classrooms.length}`);
      console.log(`[Admin Dashboard] Classrooms:`, classrooms.map(c => ({
        id: c.id,
        name: c.className,
        code: c.classCode,
        teacher: c.teacherName,
        students: c.studentCount,
        parents: c.parentCount
      })));

      setOrganizationClassrooms(classrooms);

      // Debug: Check if classrooms are being set
      console.log(`[Admin Dashboard] Setting organizationClassrooms to:`, classrooms.length, 'classrooms');
    } catch (error) {
      console.error('Error loading organization classrooms:', error);
      CustomToast.show('Failed to load classrooms', 'error');
    } finally {
      setLoadingClassrooms(false);
    }
  };

  // Function to load holidays for an organization
  const loadHolidays = async (orgCode: string) => {
    try {
      console.log(`[Admin Dashboard] Loading holidays for organization: ${orgCode}`);
      const calendarRef = doc(db, 'academic_calendar', `${orgCode}_${new Date().getFullYear()}`);
      const calendarSnap = await getDoc(calendarRef);

      if (calendarSnap.exists()) {
        const data = calendarSnap.data();
        setHolidays(data.holidays || []);
        console.log(`[Admin Dashboard] Loaded ${data.holidays?.length || 0} holidays`);
      } else {
        setHolidays([]);
        console.log(`[Admin Dashboard] No holidays found for organization`);
      }
    } catch (error) {
      console.error('Error loading holidays:', error);
      setHolidays([]);
    }
  };

  // Function to show classrooms modal
  const showClassroomsForOrganization = async (org: Organization) => {
    setSelectedOrgForClassrooms(org);
    setShowClassroomsModal(true);
    await loadOrganizationClassrooms(org.code);
  };

  // Function to open timetable management for a classroom
  const openTimetableManagement = async (classroom: any) => {
    setSelectedClassroom(classroom);
    setShowTimetableManagement(true);
    setShowTimetableModal(false);
    await loadClassroomEvents(classroom.id);
  };

  // Function to load events for a specific classroom
  const loadClassroomEvents = async (classroomId: string) => {
    setLoadingEvents(true);
    try {
      console.log(`[Admin Dashboard] Loading events for classroom: ${classroomId}`);

      const eventsQuery = query(
        collection(db, 'calendar_events'),
        where('classroomId', '==', classroomId)
      );
      const eventsSnapshot = await getDocs(eventsQuery);

      const events = eventsSnapshot.docs.map(doc => {
        const data = doc.data();
        return {
          id: doc.id,
          title: data.title,
          description: data.description,
          startTime: data.startTime?.toDate(),
          endTime: data.endTime?.toDate(),
          eventType: data.eventType,
          room: data.room,
          location: data.location,
          createdBy: data.createdBy,
          createdAt: data.createdAt?.toDate(),
        };
      });

      // Sort events by start time
      events.sort((a, b) => a.startTime - b.startTime);

      console.log(`[Admin Dashboard] Loaded ${events.length} events for classroom ${classroomId}`);
      setClassroomEvents(events);
    } catch (error) {
      console.error('Error loading classroom events:', error);
      CustomToast.show('Failed to load classroom events', 'error');
    } finally {
      setLoadingEvents(false);
    }
  };

  // Function to edit an event
  const editEvent = (event: any) => {
    // TODO: Implement edit event functionality
    CustomToast.show(`Edit event "${event.title}" functionality coming soon!`, 'info');
  };

  // Function to delete an event
  const deleteEvent = async (eventId: string) => {
    // Use window.confirm to avoid ESLint error
    if (window.confirm('Are you sure you want to delete this event?')) {
      try {
        await deleteDoc(doc(db, 'calendar_events', eventId));
        CustomToast.show('Event deleted successfully', 'success');
        // Reload events
        if (selectedClassroom) {
          await loadClassroomEvents(selectedClassroom.id);
        }
      } catch (error) {
        console.error('Error deleting event:', error);
        CustomToast.show('Failed to delete event', 'error');
      }
    }
  };

  // Function to create a single event
  const createSingleEvent = async () => {
    if (!selectedClassroom || !newEvent.title) {
      CustomToast.show('Please fill in all required fields', 'error');
      return;
    }

    setIsCreatingEvents(true);
    try {
      console.log('[Admin Dashboard] Creating single event for classroom:', selectedClassroom.id);
      console.log('[Admin Dashboard] Event details:', newEvent);

      const startDateTime = new Date(`${newEvent.startDate}T${newEvent.startTime}`);
      const endDateTime = new Date(`${newEvent.startDate}T${newEvent.endTime}`);

      console.log('[Admin Dashboard] Start DateTime:', startDateTime);
      console.log('[Admin Dashboard] End DateTime:', endDateTime);

      const eventData = {
        title: newEvent.title,
        description: newEvent.description,
        startTime: startDateTime,
        endTime: endDateTime,
        classroomId: selectedClassroom.id,
        organizationId: selectedClassroom.organizationCode || 'pear1234',
        createdBy: 'admin', // Admin created
        createdAt: new Date(),
        updatedAt: new Date(),
        eventType: newEvent.eventType,
        participants: [],
        room: newEvent.room,
        location: newEvent.location,
        teacherName: newEvent.teacherName,
      };

      console.log('[Admin Dashboard] Event data to save:', eventData);

      const docRef = await addDoc(collection(db, 'calendar_events'), eventData);
      console.log('[Admin Dashboard] Event created with ID:', docRef.id);

      CustomToast.show('Event created successfully!', 'success');
      setShowAddEventModal(false);
      resetNewEvent();
      await loadClassroomEvents(selectedClassroom.id);
    } catch (error) {
      console.error('Error creating event:', error);
      CustomToast.show('Failed to create event', 'error');
    } finally {
      setIsCreatingEvents(false);
    }
  };

  // Function to create bulk events
  const createBulkEvents = async () => {
    if (!selectedClassroom || !newEvent.title || newEvent.selectedDays.length === 0) {
      CustomToast.show('Please fill in all required fields and select at least one day', 'error');
      return;
    }

    setIsCreatingEvents(true);
    try {
      console.log('[Admin Dashboard] Creating bulk events for classroom:', selectedClassroom.id);
      console.log('[Admin Dashboard] Bulk event details:', newEvent);

      const startDate = new Date(newEvent.startDate);
      const endDate = new Date(newEvent.endDate);
      const eventsCreated = [];

      console.log('[Admin Dashboard] Date range:', startDate, 'to', endDate);
      console.log('[Admin Dashboard] Selected days:', newEvent.selectedDays);
      console.log('[Admin Dashboard] Repeat pattern:', newEvent.repeatPattern);

      // Generate dates based on selected days and date range
      const dates = generateDatesForPattern(startDate, endDate, newEvent.selectedDays, newEvent.repeatPattern);

      console.log('[Admin Dashboard] Generated dates:', dates);

      for (const date of dates) {
        const startDateTime = new Date(`${date}T${newEvent.startTime}`);
        const endDateTime = new Date(`${date}T${newEvent.endTime}`);

        const eventData = {
          title: newEvent.title,
          description: newEvent.description,
          startTime: startDateTime,
          endTime: endDateTime,
          classroomId: selectedClassroom.id,
          organizationId: selectedClassroom.organizationCode || 'pear1234',
          createdBy: 'admin',
          createdAt: new Date(),
          updatedAt: new Date(),
          eventType: newEvent.eventType,
          participants: [],
          room: newEvent.room,
          location: newEvent.location,
          teacherName: newEvent.teacherName,
        };

        console.log('[Admin Dashboard] Creating event for date:', date, 'with data:', eventData);

        const docRef = await addDoc(collection(db, 'calendar_events'), eventData);
        console.log('[Admin Dashboard] Event created with ID:', docRef.id);
        eventsCreated.push(date);
      }

      console.log('[Admin Dashboard] Total events created:', eventsCreated.length);

      CustomToast.show(`Successfully created ${eventsCreated.length} events!`, 'success');
      setShowAddEventModal(false);
      resetNewEvent();
      await loadClassroomEvents(selectedClassroom.id);
    } catch (error) {
      console.error('Error creating bulk events:', error);
      CustomToast.show('Failed to create bulk events', 'error');
    } finally {
      setIsCreatingEvents(false);
    }
  };

  // Function to generate dates for bulk scheduling
  const generateDatesForPattern = (startDate: Date, endDate: Date, selectedDays: string[], pattern: string): string[] => {
    const dates: string[] = [];
    const currentDate = new Date(startDate);

    console.log('[Admin Dashboard] Generating dates from:', currentDate, 'to:', endDate);
    console.log('[Admin Dashboard] Pattern:', pattern, 'Selected days:', selectedDays);

    // For daily pattern, just check each day
    if (pattern === 'daily') {
      while (currentDate <= endDate) {
        const dayOfWeek = currentDate.toLocaleDateString('en-US', { weekday: 'long' }).toLowerCase();
        if (selectedDays.includes(dayOfWeek)) {
          dates.push(currentDate.toISOString().split('T')[0]);
        }
        currentDate.setDate(currentDate.getDate() + 1);
      }
    }
    // For weekly pattern, check each week
    else if (pattern === 'weekly') {
      while (currentDate <= endDate) {
        const dayOfWeek = currentDate.toLocaleDateString('en-US', { weekday: 'long' }).toLowerCase();
        if (selectedDays.includes(dayOfWeek)) {
          dates.push(currentDate.toISOString().split('T')[0]);
        }
        currentDate.setDate(currentDate.getDate() + 1);
      }
    }
    // For monthly pattern, check each month
    else if (pattern === 'monthly') {
      while (currentDate <= endDate) {
        const dayOfWeek = currentDate.toLocaleDateString('en-US', { weekday: 'long' }).toLowerCase();
        if (selectedDays.includes(dayOfWeek)) {
          dates.push(currentDate.toISOString().split('T')[0]);
        }
        currentDate.setDate(currentDate.getDate() + 1);
      }
    }

    console.log('[Admin Dashboard] Generated dates:', dates);
    return dates;
  };

  // Function to reset new event form
  const resetNewEvent = () => {
    setNewEvent({
      title: '',
      description: '',
      startTime: '09:00',
      endTime: '10:00',
      eventType: 'class',
      room: '',
      location: '',
      teacherName: 'Admin', // Default teacher name for admin-created events
      selectedDays: [],
      startDate: new Date().toISOString().split('T')[0],
      endDate: new Date().toISOString().split('T')[0],
      repeatPattern: 'weekly',
      templateName: ''
    });
  };

  // Function to handle day selection for bulk scheduling
  const toggleDaySelection = (day: string) => {
    setNewEvent(prev => ({
      ...prev,
      selectedDays: prev.selectedDays.includes(day)
        ? prev.selectedDays.filter(d => d !== day)
        : [...prev.selectedDays, day]
    }));
  };

  // Function to close classrooms modal
  const closeClassroomsModal = () => {
    setShowClassroomsModal(false);
    setSelectedOrgForClassrooms(null);
    setOrganizationClassrooms([]);
  };

  // Image upload functions
  const handleImageUpload = async (file: File): Promise<string> => {
    try {
      const storageRef = ref(storage, `menu/${selectedOrganization}/${Date.now()}_${file.name}`);
      const uploadResult = await uploadBytes(storageRef, file);
      const downloadURL = await getDownloadURL(uploadResult.ref);
      return downloadURL;
    } catch (error) {
      console.error('Error uploading image:', error);
      throw new Error('Failed to upload image');
    }
  };

  const handleSingleItemImageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      setSingleMenuItem(prev => ({ ...prev, imageFile: file }));
    }
  };

  const handleBatchItemImageChange = (index: number, event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      const newMenuItems = [...batchMenuData.menuItems];
      newMenuItems[index].imageFile = file;
      setBatchMenuData(prev => ({ ...prev, menuItems: newMenuItems }));
    }
  };

  const removeSingleItemImage = () => {
    setSingleMenuItem(prev => ({ ...prev, imageFile: null, imageUrl: '' }));
  };

  const removeBatchItemImage = (index: number) => {
    const newMenuItems = [...batchMenuData.menuItems];
    newMenuItems[index].imageFile = null;
    newMenuItems[index].imageUrl = '';
    setBatchMenuData(prev => ({ ...prev, menuItems: newMenuItems }));
  };

  // Handle single menu item creation with instant feedback
  const handleCreateSingleMenuItem = async () => {
    try {
      if (!singleMenuItem.name || !singleMenuItem.description || !singleMenuItem.category) {
        CustomToast.show('Please fill in all required fields', 'error');
        return;
      }

      if (selectedOrganization === 'all') {
        CustomToast.show('Please select an organization first', 'error');
        return;
      }

      // Show immediate feedback
      setLoading(true);
      CustomToast.show('Creating menu item...', 'info');

      // Upload image using Cloudinary
      let imageUrl = 'assets/images/food.svg'; // Default image
      if (singleMenuItem.imageFile) {
        CustomToast.show('Uploading image...', 'info');
        try {
          const uploadResult = await cloudinaryService.uploadImage(singleMenuItem.imageFile, 'pear_menu_items');
          if (uploadResult.success && uploadResult.url) {
            imageUrl = uploadResult.url;
            CustomToast.show('Image uploaded successfully!', 'success');
          } else {
            CustomToast.show('Failed to upload image, using default', 'warning');
          }
        } catch (error) {
          CustomToast.show('Failed to upload image, using default', 'warning');
        }
      }

      // Normalize date to start of day for consistent date matching
      const normalizedDate = new Date();
      normalizedDate.setHours(0, 0, 0, 0);

      const newMenuItem = {
        name: singleMenuItem.name,
        description: singleMenuItem.description,
        category: singleMenuItem.category,
        imageUrl: imageUrl,
        rating: 0,
        ratingCount: 0,
        date: normalizedDate,
        uploadedBy: currentUser?.uid || 'admin',
        createdAt: new Date(),
        nutritionalInfo: {},
        organizationCode: selectedOrganization,
        expiresAt: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000), // Expires in 7 days
        isActive: true,
        type: 'single'
      };

      const docRef = await addDoc(collection(db, 'menu'), newMenuItem);
      console.log('Single menu item created with ID:', docRef.id);
      console.log('Menu item organizationCode:', selectedOrganization);
      console.log('Menu item date:', normalizedDate);
      console.log('Menu item expiresAt:', newMenuItem.expiresAt);

      // Log operation to history
      logOperation({
        type: 'create',
        contentType: 'menu',
        contentId: docRef.id,
        contentTitle: `${newMenuItem.name} (${newMenuItem.category})`,
        organizationCode: selectedOrganization,
        details: {
          category: newMenuItem.category,
          date: normalizedDate,
          expiresAt: newMenuItem.expiresAt,
        },
      });

      // Reset form
      setSingleMenuItem({
        name: '',
        description: '',
        category: '',
        imageFile: null,
        imageUrl: ''
      });

      // Reload data
      await loadData();
      setShowCreateMenuItem(false);
      CustomToast.show('✅ Menu item created successfully!', 'success');
    } catch (error) {
      console.error('Error creating single menu item:', error);
      CustomToast.show('❌ Failed to create menu item', 'error');
    } finally {
      setLoading(false);
    }
  };

  // Handle batch menu creation with instant feedback
  const handleCreateBatchMenu = async (batchMenuData: any) => {
    try {
      // Validate inputs
      const enabledItems = batchMenuData.menuItems.filter((item: any) => item.isEnabled);
      if (enabledItems.length < 2) {
        CustomToast.show('Please enable at least 2 categories', 'error');
        return;
      }

      if (enabledItems.length > 4) {
        CustomToast.show('Maximum 4 categories allowed', 'error');
        return;
      }

      // Validate that all enabled items have required fields
      for (const item of enabledItems) {
        if (!item.name.trim()) {
          CustomToast.show(`Please enter a name for ${item.category}`, 'error');
          return;
        }
        if (!item.description.trim()) {
          CustomToast.show(`Please enter a description for ${item.category}`, 'error');
          return;
        }
      }

      // Use selected organization
      const organizationCode = selectedOrganization;
      console.log('Creating batch menu for organization:', organizationCode);
      if (organizationCode === 'all') {
        CustomToast.show('Please select an organization first', 'error');
        return;
      }

      // Calculate end date based on period type (Monday-Friday only)
      const startDate = new Date(batchMenuData.startDate);

      // Validate that start date is a weekday (Monday-Friday)
      const startDayOfWeek = startDate.getDay();
      if (startDayOfWeek === 0 || startDayOfWeek === 6) { // Sunday or Saturday
        CustomToast.show('Start date must be a weekday (Monday-Friday)', 'error');
        return;
      }

      let endDate;
      if (batchMenuData.periodType === 'week') {
        // For week, calculate end date to include 5 weekdays (Monday-Friday)
        endDate = new Date(startDate);
        let weekdaysAdded = 0;
        while (weekdaysAdded < 4) { // 4 more days to get 5 total weekdays
          endDate.setDate(endDate.getDate() + 1);
          const dayOfWeek = endDate.getDay();
          if (dayOfWeek >= 1 && dayOfWeek <= 5) { // Monday-Friday
            weekdaysAdded++;
          }
        }
      } else {
        // For month, calculate end date to last weekday of the month
        endDate = new Date(startDate.getFullYear(), startDate.getMonth() + 1, 0); // Last day of next month
        // Adjust to last weekday if it's a weekend
        while (endDate.getDay() === 0 || endDate.getDay() === 6) {
          endDate.setDate(endDate.getDate() - 1);
        }
      }

      // Show immediate feedback
      setLoading(true);
      CustomToast.show(`Creating batch menu with ${enabledItems.length} items...`, 'info');

      // Create batch menu items with Cloudinary image uploads with progress
      const batchMenuItems = [];
      for (let i = 0; i < enabledItems.length; i++) {
        const item = enabledItems[i];
        CustomToast.show(`Processing ${item.category} (${i + 1}/${enabledItems.length})...`, 'info');

        let imageUrl = 'assets/images/food.svg'; // Default image
        if (item.imageFile) {
          try {
            const uploadResult = await cloudinaryService.uploadImage(item.imageFile, 'pear_menu_items');
            if (uploadResult.success && uploadResult.url) {
              imageUrl = uploadResult.url;
              CustomToast.show(`✅ ${item.category} image uploaded!`, 'success');
            } else {
              CustomToast.show(`⚠️ ${item.category} image failed, using default`, 'warning');
            }
          } catch (error) {
            console.error(`Error uploading image for ${item.category}:`, error);
            CustomToast.show(`⚠️ ${item.category} image failed, using default`, 'warning');
          }
        }

        batchMenuItems.push({
          name: item.name.trim(),
          description: item.description.trim(),
          category: item.category,
          imageUrl: imageUrl,
          nutritionalInfo: {}
        });
      }

      // Normalize dates to start of day (midnight) for consistent date comparisons
      const normalizedStartDate = new Date(startDate);
      normalizedStartDate.setHours(0, 0, 0, 0);
      const normalizedEndDate = new Date(endDate);
      normalizedEndDate.setHours(23, 59, 59, 999); // End of day

      // Create batch menu document with Firestore Timestamps
      const batchMenu = {
        organizationCode: organizationCode,
        startDate: Timestamp.fromDate(normalizedStartDate),
        endDate: Timestamp.fromDate(normalizedEndDate),
        menuItems: batchMenuItems,
        periodType: batchMenuData.periodType,
        createdBy: currentUser?.uid || 'admin',
        createdAt: serverTimestamp(),
        status: 'active',
        expiresAt: Timestamp.fromDate(normalizedEndDate),
        isActive: true,
        type: 'batch'
      };

      console.log('Creating batch menu with data:', {
        organizationCode: batchMenu.organizationCode,
        startDate: batchMenu.startDate,
        endDate: batchMenu.endDate,
        menuItemsCount: batchMenu.menuItems.length,
        periodType: batchMenu.periodType,
        status: batchMenu.status
      });

      CustomToast.show('Saving batch menu...', 'info');
      const docRef = await addDoc(collection(db, 'batchMenus'), batchMenu);
      console.log('Batch menu created with ID:', docRef.id);

      // Log operation
      logOperation({
        type: 'create',
        contentType: 'batchMenu',
        contentId: docRef.id,
        contentTitle: `Batch Menu (${enabledItems.length} items)`,
        organizationCode: organizationCode,
        details: {
          startDate: startDate,
          endDate: endDate,
          itemCount: enabledItems.length,
        },
      });

      // Send notifications to all users in the organization
      try {
        // Query users by organizationCode, schoolCode, or classCode to catch all users
        const usersQuery1 = query(
          collection(db, 'users'),
          where('organizationCode', '==', organizationCode)
        );
        const usersQuery2 = query(
          collection(db, 'users'),
          where('schoolCode', '==', organizationCode)
        );
        const usersQuery3 = query(
          collection(db, 'users'),
          where('classCode', '==', organizationCode)
        );

        const [snapshot1, snapshot2, snapshot3] = await Promise.all([
          getDocs(usersQuery1),
          getDocs(usersQuery2),
          getDocs(usersQuery3)
        ]);

        // Combine all users and deduplicate by user ID
        const allUsers = new Map<string, any>();
        [snapshot1, snapshot2, snapshot3].forEach(snapshot => {
          snapshot.forEach((doc) => {
            if (!allUsers.has(doc.id)) {
              allUsers.set(doc.id, { id: doc.id, data: doc.data() });
            }
          });
        });

        // Create individual notification documents per user
        const batches: ReturnType<typeof writeBatch>[] = [writeBatch(db)];
        let currentBatchIndex = 0;
        let notificationCount = 0;
        const maxBatchSize = 500;

        const menuTitle = `New Menu Available`;
        const menuDescription = `New menu items from ${startDate.toLocaleDateString()} to ${endDate.toLocaleDateString()}`;

        for (const [userId, userInfo] of allUsers) {
          // Check for duplicate notification (check by startDate, endDate, and recipientId)
          const existingNotificationsQuery = query(
            collection(db, 'notifications'),
            where('recipientId', '==', userId),
            where('type', '==', 'menu'),
            where('startDate', '==', Timestamp.fromDate(startDate)),
            where('endDate', '==', Timestamp.fromDate(endDate)),
            where('read', '==', false),
            limit(1)
          );
          const existingNotifications = await getDocs(existingNotificationsQuery);

          if (existingNotifications.empty) {
            // Check if current batch is full
            if (notificationCount > 0 && notificationCount % maxBatchSize === 0) {
              currentBatchIndex++;
              batches.push(writeBatch(db));
            }

            const notificationRef = doc(collection(db, 'notifications'));
            batches[currentBatchIndex].set(notificationRef, {
              recipientId: userId,
              senderId: currentUser?.uid || 'admin',
              title: menuTitle,
              body: menuDescription,
              type: 'menu',
              timestamp: serverTimestamp(),
              read: false,
              processed: false, // Will be set to true when notification is shown
              organizationCode: organizationCode,
              startDate: Timestamp.fromDate(startDate),
              endDate: Timestamp.fromDate(endDate),
            });
            notificationCount++;
          }
        }

        // Commit all batches
        if (notificationCount > 0) {
          await Promise.all(batches.map(b => b.commit()));
          console.log(`Created ${notificationCount} menu notifications for batch menu`);
        }
      } catch (notifError) {
        console.error('Error sending batch menu notifications:', notifError);
        // Don't fail menu creation if notifications fail
      }

      // Reload data
      await loadData();
      setShowCreateMenuItem(false);
      CustomToast.show('✅ Batch menu created successfully!', 'success');
    } catch (error) {
      console.error('Error creating batch menu:', error);
      CustomToast.show('❌ Failed to create batch menu', 'error');
    } finally {
      setLoading(false);
    }
  };

  // Handle batch event creation
  const handleCreateBatchEvent = async (batchEventData: any) => {
    try {
      // Validate inputs
      const enabledEvents = batchEventData.events.filter((event: any) => event.isEnabled);
      if (enabledEvents.length < 1) {
        CustomToast.show('Please enable at least 1 event', 'error');
        return;
      }

      // Validate that all enabled events have required fields
      for (const event of enabledEvents) {
        if (!event.title.trim()) {
          CustomToast.show('Please enter a title for all enabled events', 'error');
          return;
        }
        if (!event.description.trim()) {
          CustomToast.show('Please enter a description for all enabled events', 'error');
          return;
        }
      }

      // Use selected organization
      const organizationCode = selectedOrganization;
      console.log('Creating batch event for organization:', organizationCode);
      if (organizationCode === 'all') {
        CustomToast.show('Please select an organization first', 'error');
        return;
      }

      // Calculate end date based on period type
      const startDate = new Date(batchEventData.startDate);

      // Validate that start date is a weekday (Monday-Friday)
      const startDayOfWeek = startDate.getDay();
      if (startDayOfWeek === 0 || startDayOfWeek === 6) {
        CustomToast.show('Start date must be a weekday (Monday-Friday)', 'error');
        return;
      }

      let endDate;
      if (batchEventData.periodType === 'week') {
        // For week, calculate end date to include 5 weekdays (Monday-Friday)
        endDate = new Date(startDate);
        let weekdaysAdded = 0;
        while (weekdaysAdded < 4) {
          endDate.setDate(endDate.getDate() + 1);
          const dayOfWeek = endDate.getDay();
          if (dayOfWeek >= 1 && dayOfWeek <= 5) {
            weekdaysAdded++;
          }
        }
      } else {
        // For month, calculate end date to last weekday of the month
        endDate = new Date(startDate.getFullYear(), startDate.getMonth() + 1, 0);
        while (endDate.getDay() === 0 || endDate.getDay() === 6) {
          endDate.setDate(endDate.getDate() - 1);
        }
      }

      // Show immediate feedback
      setLoading(true);
      CustomToast.show(`Creating batch event with ${enabledEvents.length} events...`, 'info');

      // Create batch event items with Cloudinary image uploads
      const batchEventItems = [];
      for (let i = 0; i < enabledEvents.length; i++) {
        const event = enabledEvents[i];
        CustomToast.show(`Processing ${event.title || `Event ${i + 1}`} (${i + 1}/${enabledEvents.length})...`, 'info');

        let imageUrl = '';
        if (event.imageFile) {
          try {
            const uploadResult = await cloudinaryService.uploadImage(event.imageFile, 'pear_events');
            if (uploadResult.success && uploadResult.url) {
              imageUrl = uploadResult.url;
              CustomToast.show(`✅ ${event.title} image uploaded!`, 'success');
            } else {
              CustomToast.show(`⚠️ ${event.title} image failed`, 'warning');
            }
          } catch (error) {
            console.error(`Error uploading image for ${event.title}:`, error);
            CustomToast.show(`⚠️ ${event.title} image failed`, 'warning');
          }
        }

        batchEventItems.push({
          title: event.title.trim(),
          description: event.description.trim(),
          time: event.time.trim() || '',
          location: event.location.trim() || '',
          imageUrl: imageUrl,
          category: event.category || 'general'
        });
      }

      // Normalize dates to start of day (midnight) for consistent date comparisons
      const normalizedStartDate = new Date(startDate);
      normalizedStartDate.setHours(0, 0, 0, 0);
      const normalizedEndDate = new Date(endDate);
      normalizedEndDate.setHours(23, 59, 59, 999); // End of day

      // Create batch event document with Firestore Timestamps
      const batchEvent = {
        organizationCode: organizationCode,
        startDate: Timestamp.fromDate(normalizedStartDate),
        endDate: Timestamp.fromDate(normalizedEndDate),
        events: batchEventItems,
        periodType: batchEventData.periodType,
        createdBy: currentUser?.uid || 'admin',
        createdAt: serverTimestamp(),
        status: 'active',
        expiresAt: Timestamp.fromDate(normalizedEndDate),
        isActive: true,
        type: 'batch'
      };

      console.log('Creating batch event with data:', {
        organizationCode: batchEvent.organizationCode,
        startDate: batchEvent.startDate,
        endDate: batchEvent.endDate,
        eventsCount: batchEvent.events.length,
        periodType: batchEvent.periodType,
        status: batchEvent.status
      });

      CustomToast.show('Saving batch event...', 'info');
      const docRef = await addDoc(collection(db, 'batchEvents'), batchEvent);
      console.log('Batch event created with ID:', docRef.id);

      // Log operation
      logOperation({
        type: 'create',
        contentType: 'batchEvent',
        contentId: docRef.id,
        contentTitle: `Batch Event (${enabledEvents.length} events)`,
        organizationCode: organizationCode,
        details: {
          startDate: startDate,
          endDate: endDate,
          eventCount: enabledEvents.length,
        },
      });

      // Send notifications to all users in the organization
      try {
        // Query users by organizationCode, schoolCode, or classCode to catch all users
        const usersQuery1 = query(
          collection(db, 'users'),
          where('organizationCode', '==', organizationCode)
        );
        const usersQuery2 = query(
          collection(db, 'users'),
          where('schoolCode', '==', organizationCode)
        );
        const usersQuery3 = query(
          collection(db, 'users'),
          where('classCode', '==', organizationCode)
        );

        const [snapshot1, snapshot2, snapshot3] = await Promise.all([
          getDocs(usersQuery1),
          getDocs(usersQuery2),
          getDocs(usersQuery3)
        ]);

        // Combine all users and deduplicate by user ID
        const allUsers = new Map<string, any>();
        [snapshot1, snapshot2, snapshot3].forEach(snapshot => {
          snapshot.forEach((doc) => {
            if (!allUsers.has(doc.id)) {
              allUsers.set(doc.id, { id: doc.id, data: doc.data() });
            }
          });
        });

        // Create individual notification documents per user
        // Use batch writes for efficiency (max 500 operations per batch)
        const batches: ReturnType<typeof writeBatch>[] = [writeBatch(db)];
        let currentBatchIndex = 0;
        let notificationCount = 0;
        const maxBatchSize = 500;

        const eventTitle = batchEventItems.length > 0
          ? `${batchEventItems.length} new event${batchEventItems.length > 1 ? 's' : ''}`
          : 'New batch events';
        const eventDescription = `New batch events from ${startDate.toLocaleDateString()} to ${endDate.toLocaleDateString()}`;

        for (const [userId, userInfo] of allUsers) {
          // Check for duplicate notification (check by eventId and recipientId)
          const existingNotificationsQuery = query(
            collection(db, 'notifications'),
            where('recipientId', '==', userId),
            where('type', '==', 'event'),
            where('eventId', '==', docRef.id),
            where('read', '==', false),
            limit(1)
          );
          const existingNotifications = await getDocs(existingNotificationsQuery);

          if (existingNotifications.empty) {
            // Check if current batch is full
            if (notificationCount > 0 && notificationCount % maxBatchSize === 0) {
              currentBatchIndex++;
              batches.push(writeBatch(db));
            }

            const notificationRef = doc(collection(db, 'notifications'));
            batches[currentBatchIndex].set(notificationRef, {
              recipientId: userId,
              senderId: currentUser?.uid || 'admin',
              eventId: docRef.id,
              title: `New Batch Event: ${eventTitle}`,
              body: eventDescription,
              type: 'event',
              timestamp: serverTimestamp(),
              read: false,
              processed: false, // Will be set to true when notification is shown
              organizationCode: organizationCode,
              eventDate: Timestamp.fromDate(startDate),
            });
            notificationCount++;
          }
        }

        // Commit all batches
        if (notificationCount > 0) {
          await Promise.all(batches.map(b => b.commit()));
          console.log(`Created ${notificationCount} event notifications for batch event`);
        }
      } catch (notifError) {
        console.error('Error sending batch event notifications:', notifError);
        // Don't fail event creation if notifications fail
      }

      // Reload data
      await loadData();
      setShowCreateEvent(false);
      CustomToast.show('✅ Batch event created successfully!', 'success');
    } catch (error) {
      console.error('Error creating batch event:', error);
      CustomToast.show('❌ Failed to create batch event', 'error');
    } finally {
      setLoading(false);
    }
  };

  // Handle batch event deletion
  const handleDeleteBatchEvent = async (batchEventId: string) => {
    try {
      // Get batch event data before deletion
      const batchEventDoc = await getDoc(doc(db, 'batchEvents', batchEventId));
      const batchEventData = batchEventDoc.data();

      // Instead of deleting, mark as deleted with status flag
      // This ensures it won't show on mobile app (which filters by status === 'active')
      await updateDoc(doc(db, 'batchEvents', batchEventId), {
        status: 'deleted',
        deletedAt: serverTimestamp(),
        deletedBy: currentUser?.uid || 'admin',
      });

      console.log('Batch event marked as deleted:', batchEventId);

      // Log operation
      if (batchEventData) {
        logOperation({
          type: 'delete',
          contentType: 'batchEvent',
          contentId: batchEventId,
          contentTitle: `Batch Event (${batchEventData.events?.length || 0} events)`,
          organizationCode: batchEventData.organizationCode || selectedOrganization,
          details: {
            startDate: batchEventData.startDate,
            endDate: batchEventData.endDate,
            eventCount: batchEventData.events?.length || 0,
          },
        });
      }

      // Reload data
      await loadData();
      CustomToast.show('Batch event deleted successfully!', 'success');
    } catch (error) {
      console.error('Error deleting batch event:', error);
      CustomToast.show('Failed to delete batch event', 'error');
    }
  };

  if (loading) {
    return (
      <div className="admin-theme" style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: '16px' }}>
        <div style={{ width: '36px', height: '36px', borderRadius: '50%', border: '3px solid rgba(59,156,255,.15)', borderTopColor: 'var(--brand)', animation: 'spin 0.8s linear infinite' }}></div>
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
        <p style={{ color: 'var(--muted)', fontSize: '14px' }}>Loading console…</p>
      </div>
    );
  }

  return (
    <div className="admin-theme">
      <div className="app">
      {/* ────────────── Sidebar ────────────── */}
      <aside className="side">
        <div className="side-logo">
          <div style={{ width: '30px', height: '30px', borderRadius: '8px', background: 'linear-gradient(135deg,#22cdee,#3b9cff)', display: 'grid', placeItems: 'center', fontWeight: 900, fontSize: '14px', color: '#06121f', flexShrink: 0 }}>P</div>
          PEAR
          <div className="tag">ADMIN</div>
          {/* PLACEHOLDER: sidebar-header old content starts below */}
        </div>
        {/* Sidebar navigation */}
        <div className="side-sec" style={{ flex: 1, overflowY: 'auto' }}>
          <div className="side-label">Main</div>
          <div
            className={`nav-item ${selectedTab === 'dashboard' ? 'active' : ''}`}
            onClick={() => setSelectedTab('dashboard')}
          >
            <svg viewBox="0 0 24 24"><path d="M3 13h4v7H3v-7Zm6-6h4v13H9V7Zm6 3h4v10h-4V10Z"/></svg>
            Dashboard
            {selectedTab === 'dashboard' ? null : null}
          </div>
          <div
            className={`nav-item ${selectedTab === 'organizations' ? 'active' : ''}`}
            onClick={() => setSelectedTab('organizations')}
          >
            <svg viewBox="0 0 24 24"><path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5"/></svg>
            Organizations
            <span className="count">{organizations.length}</span>
          </div>
          <div
            className={`nav-item ${selectedTab === 'users' ? 'active' : ''}`}
            onClick={() => setSelectedTab('users')}
          >
            <svg viewBox="0 0 24 24"><path d="M16 11c1.66 0 3-1.34 3-3s-1.34-3-3-3-3 1.34-3 3 1.34 3 3 3zm-8 0c1.66 0 3-1.34 3-3S9.66 5 8 5 5 6.34 5 8s1.34 3 3 3zm0 2c-2.33 0-7 1.17-7 3.5V19h14v-2.5c0-2.33-4.67-3.5-7-3.5zm8 0c-.29 0-.62.02-.97.05 1.16.84 1.97 1.97 1.97 3.45V19h6v-2.5c0-2.33-4.67-3.5-7-3.5z"/></svg>
            Users
            <span className="count">{allUsers.length}</span>
          </div>

          <div className="side-label">Content</div>
          <div
            className={`nav-item ${selectedTab === 'content' ? 'active' : ''}`}
            onClick={() => setSelectedTab('content')}
          >
            <svg viewBox="0 0 24 24"><path d="M20 4H4a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V6a2 2 0 0 0-2-2zm0 4-8 5-8-5V6l8 5 8-5v2z"/></svg>
            Content
          </div>
          <div
            className="nav-item"
            onClick={() => navigate('/reports')}
          >
            <svg viewBox="0 0 24 24"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8l-6-6zm2 16H8v-2h8v2zm0-4H8v-2h8v2zm-3-5V3.5L18.5 9H13z"/></svg>
            Reports
          </div>
          <div
            className="nav-item"
            onClick={() => navigate('/activity-tracker')}
          >
            <svg viewBox="0 0 24 24"><path d="M22 12h-4l-3 9L9 3l-3 9H2"/></svg>
            Activity Tracker
          </div>
          <div
            className="nav-item"
            onClick={() => navigate('/admin/console-admins')}
          >
            <svg viewBox="0 0 24 24"><path d="M16 11c1.66 0 3-1.34 3-3s-1.34-3-3-3-3 1.34-3 3 1.34 3 3 3zm-8 0c1.66 0 3-1.34 3-3S9.66 5 8 5 5 6.34 5 8s1.34 3 3 3zm0 2c-2.33 0-7 1.17-7 3.5V19h14v-2.5C15 14.17 10.33 13 8 13zm8 0c-.29 0-.62.02-.97.05C17.16 14.13 19 15.28 19 16.5V19h5v-2.5c0-2.33-4.67-3.5-7-3.5z"/></svg>
            Console admins
          </div>

          <div className="side-label">Fleet</div>
          <div
            className={`nav-item ${selectedTab === 'bus' ? 'active' : ''}`}
            onClick={() => setSelectedTab('bus')}
          >
            <svg viewBox="0 0 24 24"><path d="M4 16V6a2 2 0 0 1 2-2h12a2 2 0 0 1 2 2v10a1 1 0 0 1-1 1h-1a2 2 0 1 1-4 0H8a2 2 0 1 1-4 0H3a1 1 0 0 1-1-1Zm2-9v4h12V7Z"/></svg>
            Bus Tracking
          </div>
          <div
            className={`nav-item ${selectedTab === 'lorry' ? 'active' : ''}`}
            onClick={() => setSelectedTab('lorry')}
          >
            <svg viewBox="0 0 24 24"><path d="M2 6a1 1 0 0 1 1-1h11a1 1 0 0 1 1 1v3h3.2a1 1 0 0 1 .8.4l2.6 3.4a1 1 0 0 1 .2.6V17a1 1 0 0 1-1 1h-1a2.5 2.5 0 0 1-5 0H9a2.5 2.5 0 0 1-5 0H3a1 1 0 0 1-1-1V6Zm13 5h5l-2-2.6H15V11Z"/></svg>
            Lorry Fleets
          </div>
        </div>

        {/* Sidebar footer — user card */}
        <div className="side-foot">
          <div className="su-card">
            <div className="su-av">{(currentUser?.email || 'P')[0].toUpperCase()}</div>
            <div style={{ minWidth: 0 }}>
              <div className="su-name" style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {currentUser?.displayName || 'PEAR Admin'}
              </div>
              <div className="su-role">Super Admin</div>
            </div>
            <button
              title="Log out"
              onClick={logout}
              style={{ marginLeft: 'auto', background: 'none', border: 'none', cursor: 'pointer', color: 'var(--faint)', padding: '4px', borderRadius: '6px', display: 'grid', placeItems: 'center' }}
              onMouseEnter={(e) => (e.currentTarget.style.color = 'var(--red)')}
              onMouseLeave={(e) => (e.currentTarget.style.color = 'var(--faint)')}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M17 7l-1.41 1.41L18.17 11H8v2h10.17l-2.58 2.58L17 17l5-5zM4 5h8V3H4c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h8v-2H4V5z"/></svg>
            </button>
          </div>
        </div>
      </aside>

      {/* ────────────── Main ────────────── */}
      <div className="main">
        {/* Topbar */}
        <div className="topbar">
          <div className="tb-search">
            <svg viewBox="0 0 24 24"><path d="M21 21l-4.35-4.35M17 11A6 6 0 1 1 5 11a6 6 0 0 1 12 0z" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/></svg>
            <input
              type="text"
              placeholder="Search users, orgs, content…"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>

          <div className="tb-right">
            {/* Environment badge */}
            <div className="env">
              <div className="dot"></div>
              Production
            </div>

            {/* Org filter */}
            {!isFleetAdmin ? (
              <select
                value={selectedOrganization}
                onChange={(e) => setSelectedOrganization(e.target.value)}
                style={{ background: 'var(--surface)', border: '1px solid var(--line)', color: 'var(--text)', borderRadius: '10px', padding: '8px 12px', fontFamily: 'inherit', fontSize: '13px', fontWeight: 600, cursor: 'pointer', outline: 'none' }}
              >
                <option value="all">All orgs ({organizations.length})</option>
                {organizations.map(org => (
                  <option key={org.id} value={org.code}>{org.name}</option>
                ))}
              </select>
            ) : (
              <div style={{ fontSize: '13px', color: 'var(--cyan)', fontWeight: 700 }}>
                Fleet: {organizations.find(org => org.code === fleetOrgCode)?.name || fleetOrgCode}
              </div>
            )}

            {/* Refresh */}
            <button className="tb-icon" onClick={loadData} title="Refresh">
              <svg viewBox="0 0 24 24"><path d="M23 4v6h-6M1 20v-6h6"/><path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
            </button>

            {/* Notifications */}
            <button className="tb-icon" title="Notifications" style={{ position: 'relative' }}>
              <svg viewBox="0 0 24 24"><path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/><path d="M13.73 21a2 2 0 0 1-3.46 0" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/></svg>
              <div style={{ position: 'absolute', top: '7px', right: '7px', width: '7px', height: '7px', borderRadius: '50%', background: 'var(--red)', border: '1.5px solid var(--bg)' }}></div>
            </button>
          </div>
        </div>

        {/* Page header row */}
        <div style={{ padding: '22px 28px 0', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '12px' }}>
          <div>
            <h1 style={{ margin: 0, fontSize: '22px', fontWeight: 800, letterSpacing: '-.02em', color: 'var(--text)' }}>
              {selectedTab === 'dashboard' && 'Dashboard'}
              {selectedTab === 'organizations' && 'Organizations'}
              {selectedTab === 'users' && 'Users'}
              {selectedTab === 'content' && 'Content'}
              {selectedTab === 'bus' && 'Bus Tracking'}
              {selectedTab === 'lorry' && 'Lorry Fleets'}
            </h1>
            <div style={{ fontSize: '13px', color: 'var(--muted)', marginTop: '3px' }}>
              PEAR Admin Console
              {selectedOrganization !== 'all' && (
                <span style={{ color: 'var(--cyan)', fontWeight: 700 }}>
                  {' '}· {organizations.find(org => org.code === selectedOrganization)?.name || selectedOrganization}
                </span>
              )}
            </div>
          </div>
          <div style={{ display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap' }}>
            {selectedTab === 'organizations' && (
              <>
                <button
                  className="btn btn-line"
                  onClick={() => { setWizardInitialKind('FULL_FEATURE'); setShowAddOrganization(true); }}
                  title="Add new organization"
                >
                  <svg viewBox="0 0 24 24" fill="currentColor"><path d="M19 11H13V5h-2v6H5v2h6v6h2v-6h6z"/></svg>
                  Add Organization
                </button>
                {selectedOrganization !== 'all' && (
                  <button
                    className="btn btn-primary"
                    onClick={() => setFleetAdminCodeModal({ org: organizations.find(o => o.code === selectedOrganization)!, code: '', expiresAt: 0 })}
                    title="Generate temporary PIN for fleet admin sign-up"
                  >
                    <svg viewBox="0 0 24 24" fill="currentColor"><path d="M12 1 3 5v6c0 5.5 3.8 10.7 9 12 5.2-1.3 9-6.5 9-12V5l-9-4z"/></svg>
                    Temp PIN
                  </button>
                )}
              </>
            )}
          </div>
        </div>

        {/* Dashboard Tab */}
        {selectedTab === 'dashboard' && (
          <div className="dashboard-content">
            {/* Stats Cards */}
            <div className="stats-section">
              <div className="stat-card">
                <div className="stat-icon">
                  <FaSchool />
                </div>
                <div className="stat-content">
                  <h3 className="stat-number">{organizations.filter(org => org.hasActiveSubscription === true).length}</h3>
                  <p className="stat-label">Organizations</p>
                  <div className="stat-change positive">
                    <span>Active</span>
                  </div>
                </div>
              </div>

              <div className="stat-card">
                <div className="stat-icon">
                  <FaUsers />
                </div>
                <div className="stat-content">
                  <h3 className="stat-number">{users.length}</h3>
                  <p className="stat-label">Total Users</p>
                  <div className="stat-change positive">
                    <span>Registered</span>
                  </div>
                </div>
              </div>

              <div className="stat-card">
                <div className="stat-icon">
                  <FaNewspaper />
                </div>
                <div className="stat-content">
                  <h3 className="stat-number">{posts.length}</h3>
                  <p className="stat-label">Posts</p>
                  <div className="stat-change positive">
                    <span>Published</span>
                  </div>
                </div>
              </div>
            </div>
            {/* Operation History */}
            <div className="recent-activity">
              <h2>Operation History</h2>
              <div className="activity-list">
                {operationHistory.length === 0 ? (
                  <div className="empty-activity">
                    <p>No operations found for {selectedOrganization === 'all' ? 'any organization' : `organization ${selectedOrganization}`}</p>
                    <p style={{ fontSize: '14px', color: '#8487A2' }}>
                      Operations will appear here as you create, update, or delete content.
                    </p>
                  </div>
                ) : (
                  operationHistory.slice(0, 20).map((op: any) => {
                    const getContentIcon = () => {
                      switch (op.contentType) {
                        case 'post': return <FaNewspaper />;
                        case 'event': return <FaCalendarAlt />;
                        case 'batchEvent': return <FaCalendarAlt />;
                        case 'batchMenu': return <FaUtensils />;
                        case 'menu': return <FaUtensils />;
                        case 'diary': return <FaFileAlt />;
                        case 'timetable': return <FaCalendarAlt />;
                        default: return <FaFileAlt />;
                      }
                    };

                    const getTypeColor = () => {
                      switch (op.type) {
                        case 'create': return '#43BE83';
                        case 'delete': return '#EA8F95';
                        case 'update': return '#5041BC';
                        default: return '#8487A2';
                      }
                    };

                    const getTypeLabel = () => {
                      switch (op.type) {
                        case 'create': return 'Created';
                        case 'delete': return 'Deleted';
                        case 'update': return 'Updated';
                        default: return op.type;
                      }
                    };

                    const timestamp = op.timestamp?.toDate?.() || op.timestamp || new Date();

                    return (
                      <div key={op.id} className="activity-item">
                        <div className="activity-icon" style={{ backgroundColor: getTypeColor() }}>
                          {getContentIcon()}
                        </div>
                        <div className="activity-content">
                          <p className="activity-text">
                            <span style={{ fontWeight: '600', color: getTypeColor() }}>
                              {getTypeLabel()}
                            </span>
                            {' '}
                            <span style={{ textTransform: 'capitalize' }}>
                              {op.contentType === 'batchEvent' ? 'Batch Event' :
                                op.contentType === 'batchMenu' ? 'Batch Menu' :
                                  op.contentType === 'menu' ? 'Menu Item' :
                                    op.contentType}
                            </span>
                            : {op.contentTitle}
                          </p>
                          <p className="activity-time">
                            {new Date(timestamp).toLocaleString()}
                            {op.organizationCode && op.organizationCode !== 'all' && (
                              <span style={{ color: '#5041BC', marginLeft: '8px' }}>
                                • {op.organizationCode}
                              </span>
                            )}
                            {op.operatorName && (
                              <span style={{ color: '#8487A2', marginLeft: '8px' }}>
                                • by {op.operatorName}
                              </span>
                            )}
                          </p>
                          {op.details && (
                            <p style={{ fontSize: '12px', color: '#8487A2', marginTop: '4px' }}>
                              {op.details.itemCount && `${op.details.itemCount} items`}
                              {op.details.eventCount && `${op.details.eventCount} events`}
                              {op.details.studentName && `Student: ${op.details.studentName}`}
                            </p>
                          )}
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            </div>
          </div>
        )}

        {/* Bus-only Organizations Tab */}
        {selectedTab === 'bus' && (
          <div className="organizations-content">
            <div className="section-header">
              <h2>Bus tracking</h2>
              <button
                className="add-button"
                onClick={() => {
                  setShowAddOrganization(true);
                  setWizardInitialKind('BUS_TRACKING');
                  setWizardInitialLorryMode('SELF_TRACKING');
                }}
              >
                <FaPlus /> Add Bus Organization
              </button>
            </div>
            <p style={{ color: '#64748b', marginBottom: 20, fontSize: 14 }}>
              School bus tracking — bus drivers and parents only. Code suffix <strong>.bus</strong> (e.g. FLEET1.bus).
            </p>
            <div className="organizations-list">
              {organizations
                .filter(org => isBusTrackingOrg(org.productType))
                .map((org) => (
                <div key={org.id} className={`organization-card ${!org.hasActiveSubscription ? 'expired' : 'active'}`}>
                  <div className="org-info">
                    <div className="org-avatar">
                      <FaBus />
                    </div>
                    <div className="org-details">
                      <h3 className="org-name">{org.name || org.code || `Organization ${org.id}`}</h3>
                      <p className="org-code">Bus Code: {org.code || 'N/A'}</p>
                      {org.pendingActivation ? (
                        <div>
                          <div className="expired-badge" style={{ background: '#fef3c7', borderColor: '#fbbf24' }}>
                            <span style={{ color: '#92400e' }}>PENDING</span>
                          </div>
                          {!isFleetAdmin && <div><PendingResendButton orgCode={org.code || org.id} /></div>}
                        </div>
                      ) : org.subscriptionStatus === 'canceled' ? (
                        <div className="expired-badge" style={{ background: '#f1f5f9', borderColor: '#cbd5e1' }}>
                          <span style={{ color: '#475569' }}>CANCELED</span>
                        </div>
                      ) : org.cancelAtPeriodEnd ? (
                        <div className="expired-badge" style={{ background: '#fef2f2', borderColor: '#fca5a5' }}>
                          <span style={{ color: '#b91c1c' }}>CANCELING · follow up</span>
                        </div>
                      ) : (org.paymentFailures || 0) > 0 && org.hasActiveSubscription ? (
                        <div className="expired-badge" style={{ background: '#fef3c7', borderColor: '#f59e0b' }}>
                          <span style={{ color: '#b45309' }}>PAST DUE · follow up</span>
                        </div>
                      ) : org.isPilot ? (
                        <div className="active-badge" style={{ background: '#fff7ed', borderColor: '#fed7aa' }}>
                          <span style={{ color: '#c2410c' }}>PILOT</span>
                        </div>
                      ) : !org.hasActiveSubscription ? (
                        <div className="expired-badge">
                          <span>EXPIRED</span>
                        </div>
                      ) : (
                        <div className="active-badge">
                          <span>ACTIVE</span>
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="org-summary">
                    <div className="summary-row">
                      <span className="summary-label">Riders</span>
                      <span className="summary-value">{org.studentCount}</span>
                    </div>
                    <div className="summary-row">
                      <span className="summary-label">Drivers</span>
                      <span className="summary-value">{org.teacherCount}</span>
                    </div>
                    <div className="summary-row">
                      <span className="summary-label">Parents</span>
                      <span className="summary-value">{org.parentCount}</span>
                    </div>
                  </div>
                  <div className="org-actions">
                    <button className={`role-badge ${!org.hasActiveSubscription ? 'expired' : 'active'}`}>
                      Bus tracking
                    </button>
                    <div className="options-dropdown">
                      <button className="options-btn">
                        <FaEllipsisV />
                      </button>
                      <div className="dropdown-menu">
                        <div className="dropdown-details">
                          {org.phone && (
                            <div className="detail-item">
                              <span className="detail-label">Phone:</span>
                              <span className="detail-value">{org.phone}</span>
                            </div>
                          )}
                          {org.email && (
                            <div className="detail-item">
                              <span className="detail-label">Email:</span>
                              <span className="detail-value">{org.email}</span>
                            </div>
                          )}
                          {org.subscriptionStatus && (
                            <div className="detail-item">
                              <span className="detail-label">Subscription:</span>
                              <span className={`detail-value ${org.subscriptionStatus}`}>
                                {org.subscriptionStatus.charAt(0).toUpperCase() + org.subscriptionStatus.slice(1)}
                              </span>
                            </div>
                          )}
                          {org.addons && Object.keys(org.addons).length > 0 && (() => {
                            const addonLabels: Record<string, string> = { gps: 'GPS sensor', fuel: 'Fuel sensor', fees: 'School fees module' };
                            const verifiedCount = Object.values(org.addons).filter(a => a.status === 'connected' && (a as OrgAddon).verified).length;
                            return (
                              <>
                                <div className="detail-item" style={{ marginTop: 8, paddingTop: 8, borderTop: '1px dashed #e2e8f0', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                  <span className="detail-label" style={{ fontWeight: 700, color: '#374151' }}>Add-ons</span>
                                  {verifiedCount > 0 && <span style={{ fontSize: 10, background: '#dcfce7', color: '#16a34a', borderRadius: 8, padding: '2px 7px', fontWeight: 700 }}>{verifiedCount} live</span>}
                                </div>
                                {(Object.entries(org.addons) as [string, OrgAddon][]).filter(([key]) => key !== 'scores').map(([key, addon]) => (
                                  <div key={key} className="detail-item" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
                                    <span className="detail-label">{addonLabels[key] || key}:</span>
                                    <span style={{ fontSize: 11, fontWeight: 700, color: (addon.status === 'connected' && addon.verified) ? '#16a34a' : addon.wanted ? '#b45309' : '#94a3b8' }}>
                                      {(addon.status === 'connected' && addon.verified) ? '● Live' : addon.wanted ? 'On · not verified' : 'Off'}
                                    </span>
                                  </div>
                                ))}
                                <div className="detail-item" style={{ fontSize: 10.5, color: '#94a3b8', marginTop: 2 }}>
                                  Connect &amp; verify providers in organization settings below.
                                </div>
                              </>
                            );
                          })()}
                        </div>
                        <div className="dropdown-divider"></div>
                        {!isFleetAdmin && (
                        <button
                          className="dropdown-item"
                          onClick={async () => {
                            if (!org.code || !currentUser?.uid) return;
                            const code = String(Math.floor(1000 + Math.random() * 9000));
                            const now = Date.now();
                            const expiresAt = now + 15 * 60 * 1000;
                            try {
                              await setDoc(doc(db, 'fleet_admin_signup_codes', org.code), {
                                code,
                                orgCode: org.code,
                                generatedBy: currentUser.uid,
                                expiresAt: Timestamp.fromMillis(expiresAt),
                                createdAt: serverTimestamp(),
                              });
                              setFleetAdminCodeModal({ org, code, expiresAt });
                            } catch (e) {
                              console.warn('Failed to generate fleet admin signup code:', e);
                              CustomToast.show('Failed to generate code', 'error');
                            }
                          }}
                        >
                          Generate one-time code
                        </button>
                        )}
                        {/* Subscription tester controls — expire / restore for dev/QA */}
                        <div className="dropdown-divider" />
                        <button
                          className="dropdown-item"
                          title="Tester: set expiry to yesterday to trigger grace period"
                          onClick={async () => {
                            if (!org.code) return;
                            const yesterday = new Date(Date.now() - 24 * 60 * 60 * 1000);
                            const graceEnd = new Date(Date.now() + 7 * 24 * 60 * 60 * 1000);
                            try {
                              await updateDoc(doc(db, 'organizations', org.code), {
                                expirationDate: yesterday,
                                gracePeriodUntil: graceEnd,
                                subscriptionStatus: 'expired',
                                hasActiveSubscription: false,
                              });
                              CustomToast.show('⏰ Subscription set to grace period (expires yesterday)', 'info');
                            } catch (e) { CustomToast.show('Could not update subscription', 'error'); }
                          }}
                        >
                          ⏰ [Test] Trigger grace period
                        </button>
                        <button
                          className="dropdown-item"
                          title="Tester: set expiry to 8 days ago to trigger lockout"
                          onClick={async () => {
                            if (!org.code) return;
                            const expired = new Date(Date.now() - 8 * 24 * 60 * 60 * 1000);
                            const graceEnd = new Date(Date.now() - 1 * 24 * 60 * 60 * 1000);
                            try {
                              await updateDoc(doc(db, 'organizations', org.code), {
                                expirationDate: expired,
                                gracePeriodUntil: graceEnd,
                                subscriptionStatus: 'expired',
                                hasActiveSubscription: false,
                              });
                              CustomToast.show('🔒 Subscription set to locked (past grace)', 'info');
                            } catch (e) { CustomToast.show('Could not update subscription', 'error'); }
                          }}
                        >
                          🔒 [Test] Trigger lockout
                        </button>
                        <button
                          className="dropdown-item"
                          title="Tester: restore subscription for 30 days"
                          onClick={async () => {
                            if (!org.code) return;
                            const next30 = new Date(Date.now() + 30 * 24 * 60 * 60 * 1000);
                            try {
                              await updateDoc(doc(db, 'organizations', org.code), {
                                expirationDate: next30,
                                gracePeriodUntil: null,
                                subscriptionStatus: 'active',
                                hasActiveSubscription: true,
                                isActive: true,
                              });
                              CustomToast.show('✅ Subscription restored (30 days)', 'success');
                            } catch (e) { CustomToast.show('Could not restore subscription', 'error'); }
                          }}
                        >
                          ✅ [Test] Restore subscription
                        </button>
                        <div className="dropdown-divider" />
                        <button
                          className="dropdown-item"
                          onClick={() => navigate(`/admin/org/${encodeURIComponent(org.code)}/settings`)}
                        >
                          ⚙️ Open settings
                        </button>
                        <button
                          className="dropdown-item"
                          onClick={() => {
                            setEditingOrganization(org);
                            setShowEditOrganization(true);
                          }}
                        >
                          <FaEdit /> Edit
                        </button>
                        <button
                          className="dropdown-item delete"
                          onClick={async () => {
                            const confirmed = await CustomConfirm.show(
                              `Are you sure you want to delete ${org.name || org.code}? This will remove the fleet and all associated data.`,
                              'Delete Fleet Organization'
                            );
                            if (confirmed) {
                              handleDeleteOrganization(org.id);
                            }
                          }}
                        >
                          <FaTrash /> Delete
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            {organizations.filter(org => isBusTrackingOrg(org.productType)).length === 0 && (
              <div style={{ textAlign: 'center', padding: 40, color: '#64748b', background: '#f8fafc', borderRadius: 12, marginTop: 20 }}>
                <FaBus style={{ fontSize: 48, marginBottom: 16, opacity: 0.5 }} />
                <p style={{ fontSize: 16, marginBottom: 8 }}>No bus tracking organizations yet</p>
                <p style={{ fontSize: 14, marginBottom: 20 }}>Create one for school bus routes with drivers and parents on mobile.</p>
                <button
                  className="add-button"
                  onClick={() => {
                    setShowAddOrganization(true);
                    setWizardInitialKind('BUS_TRACKING');
                    setWizardInitialLorryMode('SELF_TRACKING');
                  }}
                >
                  <FaPlus /> Add Bus Organization
                </button>
              </div>
            )}
          </div>
        )}

        {/* Lorry Fleet Organizations Tab */}
        {selectedTab === 'lorry' && (
          <div className="organizations-content">
            <div className="section-header">
              <h2>Lorry fleets</h2>
              <button
                className="add-button"
                onClick={() => {
                  setShowAddOrganization(true);
                  setWizardInitialKind('LORRY');
                  setWizardInitialLorryMode('CARGO');
                }}
              >
                <FaPlus /> Add Lorry Organization
              </button>
            </div>
            <p style={{ color: '#64748b', marginBottom: 20, fontSize: 14 }}>
              Commercial lorry fleets — choose <strong>Personal tracking</strong> (owner + driver) or <strong>Cargo</strong> (clients + live maps). Codes use <strong>.fleet</strong> or <strong>.cargo</strong>.
            </p>
            <div className="organizations-list">
              {organizations
                .filter(org => isLorryOrg(org.productType))
                .map((org) => (
                <div key={org.id} className={`organization-card ${!org.hasActiveSubscription ? 'expired' : 'active'}`}>
                  <div className="org-info">
                    <div className="org-avatar">
                      <FaTruck />
                    </div>
                    <div className="org-details">
                      <h3 className="org-name">{org.name || org.code || `Organization ${org.id}`}</h3>
                      <p className="org-code">Fleet Code: {org.code || 'N/A'}</p>
                      {org.pendingActivation ? (
                        <div>
                          <div className="expired-badge" style={{ background: '#fef3c7', borderColor: '#fbbf24' }}>
                            <span style={{ color: '#92400e' }}>PENDING</span>
                          </div>
                          {!isFleetAdmin && <div><PendingResendButton orgCode={org.code || org.id} /></div>}
                        </div>
                      ) : org.subscriptionStatus === 'canceled' ? (
                        <div className="expired-badge" style={{ background: '#f1f5f9', borderColor: '#cbd5e1' }}>
                          <span style={{ color: '#475569' }}>CANCELED</span>
                        </div>
                      ) : org.cancelAtPeriodEnd ? (
                        <div className="expired-badge" style={{ background: '#fef2f2', borderColor: '#fca5a5' }}>
                          <span style={{ color: '#b91c1c' }}>CANCELING · follow up</span>
                        </div>
                      ) : (org.paymentFailures || 0) > 0 && org.hasActiveSubscription ? (
                        <div className="expired-badge" style={{ background: '#fef3c7', borderColor: '#f59e0b' }}>
                          <span style={{ color: '#b45309' }}>PAST DUE · follow up</span>
                        </div>
                      ) : org.isPilot ? (
                        <div className="active-badge" style={{ background: '#fff7ed', borderColor: '#fed7aa' }}>
                          <span style={{ color: '#c2410c' }}>PILOT</span>
                        </div>
                      ) : !org.hasActiveSubscription ? (
                        <div className="expired-badge">
                          <span>EXPIRED</span>
                        </div>
                      ) : (
                        <div className="active-badge">
                          <span>ACTIVE</span>
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="org-summary">
                    <div className="summary-row">
                      <span className="summary-label">
                        {isCargoOrg(org.productType) ? 'Deliveries' : 'Vehicles'}
                      </span>
                      <span className="summary-value">{org.studentCount}</span>
                    </div>
                    <div className="summary-row">
                      <span className="summary-label">Drivers</span>
                      <span className="summary-value">{org.teacherCount}</span>
                    </div>
                    <div className="summary-row">
                      <span className="summary-label">
                        {isCargoOrg(org.productType) ? 'Clients' : 'Owners'}
                      </span>
                      <span className="summary-value">{org.parentCount}</span>
                    </div>
                  </div>
                  <div className="org-actions">
                    <button className={`role-badge ${!org.hasActiveSubscription ? 'expired' : 'active'}`}>
                      {orgTypeBadgeLabel(org.productType)}
                    </button>
                    <div className="options-dropdown">
                      <button className="options-btn">
                        <FaEllipsisV />
                      </button>
                      <div className="dropdown-menu">
                        <div className="dropdown-details">
                          {org.phone && (
                            <div className="detail-item">
                              <span className="detail-label">Phone:</span>
                              <span className="detail-value">{org.phone}</span>
                            </div>
                          )}
                          {org.email && (
                            <div className="detail-item">
                              <span className="detail-label">Email:</span>
                              <span className="detail-value">{org.email}</span>
                            </div>
                          )}
                          {org.subscriptionStatus && (
                            <div className="detail-item">
                              <span className="detail-label">Subscription:</span>
                              <span className={`detail-value ${org.subscriptionStatus}`}>
                                {org.subscriptionStatus.charAt(0).toUpperCase() + org.subscriptionStatus.slice(1)}
                              </span>
                            </div>
                          )}
                          {org.addons && Object.keys(org.addons).length > 0 && (() => {
                            const addonLabels: Record<string, string> = { gps: 'GPS sensor', fuel: 'Fuel sensor', fees: 'School fees module' };
                            const verifiedCount = Object.values(org.addons).filter(a => a.status === 'connected' && (a as OrgAddon).verified).length;
                            return (
                              <>
                                <div className="detail-item" style={{ marginTop: 8, paddingTop: 8, borderTop: '1px dashed #e2e8f0', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                  <span className="detail-label" style={{ fontWeight: 700, color: '#374151' }}>Add-ons</span>
                                  {verifiedCount > 0 && <span style={{ fontSize: 10, background: '#dcfce7', color: '#16a34a', borderRadius: 8, padding: '2px 7px', fontWeight: 700 }}>{verifiedCount} live</span>}
                                </div>
                                {(Object.entries(org.addons) as [string, OrgAddon][]).filter(([key]) => key !== 'scores').map(([key, addon]) => (
                                  <div key={key} className="detail-item" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
                                    <span className="detail-label">{addonLabels[key] || key}:</span>
                                    <span style={{ fontSize: 11, fontWeight: 700, color: (addon.status === 'connected' && addon.verified) ? '#16a34a' : addon.wanted ? '#b45309' : '#94a3b8' }}>
                                      {(addon.status === 'connected' && addon.verified) ? '● Live' : addon.wanted ? 'On · not verified' : 'Off'}
                                    </span>
                                  </div>
                                ))}
                                <div className="detail-item" style={{ fontSize: 10.5, color: '#94a3b8', marginTop: 2 }}>
                                  Connect &amp; verify providers in organization settings below.
                                </div>
                              </>
                            );
                          })()}
                        </div>
                        <div className="dropdown-divider"></div>
                        {!isFleetAdmin && (
                        <button
                          className="dropdown-item"
                          onClick={async () => {
                            if (!org.code || !currentUser?.uid) return;
                            const code = String(Math.floor(1000 + Math.random() * 9000));
                            const now = Date.now();
                            const expiresAt = now + 15 * 60 * 1000;
                            try {
                              await setDoc(doc(db, 'fleet_admin_signup_codes', org.code), {
                                code,
                                orgCode: org.code,
                                generatedBy: currentUser.uid,
                                expiresAt: Timestamp.fromMillis(expiresAt),
                                createdAt: serverTimestamp(),
                              });
                              setFleetAdminCodeModal({ org, code, expiresAt });
                            } catch (e) {
                              console.warn('Failed to generate fleet admin signup code:', e);
                              CustomToast.show('Failed to generate code', 'error');
                            }
                          }}
                        >
                          Generate one-time code
                        </button>
                        )}
                        <div className="dropdown-divider" />
                        <button
                          className="dropdown-item"
                          onClick={() => navigate(`/admin/org/${encodeURIComponent(org.code)}/settings`)}
                        >
                          ⚙️ Open settings
                        </button>
                        <button
                          className="dropdown-item"
                          onClick={() => {
                            setEditingOrganization(org);
                            setShowEditOrganization(true);
                          }}
                        >
                          <FaEdit /> Edit
                        </button>
                        <button
                          className="dropdown-item delete"
                          onClick={async () => {
                            const confirmed = await CustomConfirm.show(
                              `Are you sure you want to delete ${org.name || org.code}? This will remove the fleet and all associated data.`,
                              'Delete Fleet Organization'
                            );
                            if (confirmed) {
                              handleDeleteOrganization(org.id);
                            }
                          }}
                        >
                          <FaTrash /> Delete
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            {organizations.filter(org => isLorryOrg(org.productType)).length === 0 && (
              <div style={{ textAlign: 'center', padding: 40, color: '#64748b', background: '#f8fafc', borderRadius: 12, marginTop: 20 }}>
                <FaTruck style={{ fontSize: 48, marginBottom: 16, opacity: 0.5 }} />
                <p style={{ fontSize: 16, marginBottom: 8 }}>No lorry fleet organizations yet</p>
                <p style={{ fontSize: 14, marginBottom: 20 }}>Create a personal-tracking or cargo fleet for commercial lorry operations.</p>
                <button
                  className="add-button"
                  onClick={() => {
                    setShowAddOrganization(true);
                    setWizardInitialKind('LORRY');
                    setWizardInitialLorryMode('CARGO');
                  }}
                >
                  <FaPlus /> Add Lorry Organization
                </button>
              </div>
            )}
          </div>
        )}

        {/* Organizations Tab */}
        {selectedTab === 'organizations' && (
          <div className="organizations-content">
            <div className="section-header">
              <h2>Organizations</h2>
              {!isFleetAdmin && (
                <button
                  className="add-button"
                  onClick={() => {
                    setWizardInitialKind('FULL_FEATURE');
                    setShowAddOrganization(true);
                  }}
                >
                  <FaPlus /> Add Organization
                </button>
              )}
            </div>

            {!isFleetAdmin && <ConsoleCancellations />}

            <div className="organizations-list">
              {(isFleetAdmin && fleetOrgCode
                ? organizations.filter(org => org.code === fleetOrgCode)
                : organizations
              ).map((org) => (
                <div key={org.id} className={`organization-card ${!org.hasActiveSubscription ? 'expired' : 'active'}`}>
                  <div className="org-info">
                    <div className="org-avatar">
                      <FaSchool />
                    </div>
                    <div className="org-details">
                      <h3 className="org-name">{org.name || org.code || `Organization ${org.id}`}</h3>
                      <p className="org-code">
                        {(isBusTrackingOrg(org.productType) || isLorryOrg(org.productType)) ? 'Fleet Code' : 'School Code'}: {org.code || 'N/A'}
                      </p>
                      {org.pendingActivation ? (
                        <div>
                          <div className="expired-badge" style={{ background: '#fef3c7', borderColor: '#fbbf24' }}>
                            <span style={{ color: '#92400e' }}>PENDING</span>
                          </div>
                          {!isFleetAdmin && <div><PendingResendButton orgCode={org.code || org.id} /></div>}
                        </div>
                      ) : org.subscriptionStatus === 'canceled' ? (
                        <div className="expired-badge" style={{ background: '#f1f5f9', borderColor: '#cbd5e1' }}>
                          <span style={{ color: '#475569' }}>CANCELED</span>
                        </div>
                      ) : org.cancelAtPeriodEnd ? (
                        <div className="expired-badge" style={{ background: '#fef2f2', borderColor: '#fca5a5' }}>
                          <span style={{ color: '#b91c1c' }}>CANCELING · follow up</span>
                        </div>
                      ) : (org.paymentFailures || 0) > 0 && org.hasActiveSubscription ? (
                        <div className="expired-badge" style={{ background: '#fef3c7', borderColor: '#f59e0b' }}>
                          <span style={{ color: '#b45309' }}>PAST DUE · follow up</span>
                        </div>
                      ) : org.isPilot ? (
                        <div className="active-badge" style={{ background: '#fff7ed', borderColor: '#fed7aa' }}>
                          <span style={{ color: '#c2410c' }}>PILOT</span>
                        </div>
                      ) : !org.hasActiveSubscription ? (
                        <div className="expired-badge">
                          <span>EXPIRED</span>
                        </div>
                      ) : (
                        <div className="active-badge">
                          <span>ACTIVE</span>
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="org-summary">
                    <div className="summary-row">
                      <span className="summary-label">
                        {isBusTrackingOrg(org.productType)
                          ? 'Riders'
                          : isLorryPersonalOrg(org.productType)
                            ? 'Vehicles'
                            : isCargoOrg(org.productType)
                              ? 'Deliveries'
                              : 'Students'}
                      </span>
                      <span className="summary-value">{org.studentCount}</span>
                    </div>
                    <div className="summary-row">
                      <span className="summary-label">
                        {isBusTrackingOrg(org.productType) || isLorryOrg(org.productType) ? 'Drivers' : 'Teachers'}
                      </span>
                      <span className="summary-value">{org.teacherCount}</span>
                    </div>
                    <div className="summary-row">
                      <span className="summary-label">
                        {isCargoOrg(org.productType)
                          ? 'Clients'
                          : isLorryPersonalOrg(org.productType)
                            ? 'Owners'
                            : isBusTrackingOrg(org.productType)
                              ? 'Parents'
                              : 'Parents'}
                      </span>
                      <span className="summary-value">{org.parentCount}</span>
                    </div>
                  </div>
                  <div className="org-actions">
                    <button className={`role-badge ${!org.hasActiveSubscription ? 'expired' : 'active'}`}>
                      {orgTypeBadgeLabel(org.productType) || org.type}
                    </button>
                    <div className="options-dropdown">
                      <button className="options-btn">
                        <FaEllipsisV />
                      </button>
                      <div className="dropdown-menu">
                        <div className="dropdown-details">
                          {org.phone && (
                            <div className="detail-item">
                              <span className="detail-label">Phone:</span>
                              <span className="detail-value">{org.phone}</span>
                            </div>
                          )}
                          {org.email && (
                            <div className="detail-item">
                              <span className="detail-label">Email:</span>
                              <span className="detail-value">{org.email}</span>
                            </div>
                          )}
                          {org.subscriptionPlan && (
                            <div className="detail-item">
                              <span className="detail-label">Plan:</span>
                              <span className="detail-value">{org.subscriptionPlan.charAt(0).toUpperCase() + org.subscriptionPlan.slice(1)}</span>
                            </div>
                          )}
                          {org.subscriptionStatus && (
                            <div className="detail-item">
                              <span className="detail-label">Subscription:</span>
                              <span className={`detail-value ${org.subscriptionStatus}`}>
                                {org.subscriptionStatus.charAt(0).toUpperCase() + org.subscriptionStatus.slice(1)}
                              </span>
                            </div>
                          )}
                          {org.addons && Object.keys(org.addons).length > 0 && (() => {
                            const addonLabels: Record<string, string> = { gps: 'GPS sensor', fuel: 'Fuel sensor', fees: 'School fees module' };
                            const verifiedCount = Object.values(org.addons).filter(a => a.status === 'connected' && (a as OrgAddon).verified).length;
                            return (
                              <>
                                <div className="detail-item" style={{ marginTop: 8, paddingTop: 8, borderTop: '1px dashed #e2e8f0', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                  <span className="detail-label" style={{ fontWeight: 700, color: '#374151' }}>Add-ons</span>
                                  {verifiedCount > 0 && <span style={{ fontSize: 10, background: '#dcfce7', color: '#16a34a', borderRadius: 8, padding: '2px 7px', fontWeight: 700 }}>{verifiedCount} live</span>}
                                </div>
                                {(Object.entries(org.addons) as [string, OrgAddon][]).filter(([key]) => key !== 'scores').map(([key, addon]) => (
                                  <div key={key} className="detail-item" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
                                    <span className="detail-label">{addonLabels[key] || key}:</span>
                                    <span style={{ fontSize: 11, fontWeight: 700, color: (addon.status === 'connected' && addon.verified) ? '#16a34a' : addon.wanted ? '#b45309' : '#94a3b8' }}>
                                      {(addon.status === 'connected' && addon.verified) ? '● Live' : addon.wanted ? 'On · not verified' : 'Off'}
                                    </span>
                                  </div>
                                ))}
                                <div className="detail-item" style={{ fontSize: 10.5, color: '#94a3b8', marginTop: 2 }}>
                                  Connect &amp; verify providers in organization settings below.
                                </div>
                              </>
                            );
                          })()}
                        </div>
                        <div className="dropdown-divider"></div>
                        <button
                          className="dropdown-item"
                          onClick={() => navigate(`/admin/org/${encodeURIComponent(org.code)}/settings`)}
                        >
                          ⚙️ Open settings
                        </button>
                        <button
                          className="dropdown-item"
                          onClick={() => {
                            setEditingOrganization(org);
                            setShowEditOrganization(true);
                          }}
                        >
                          <FaEdit /> Edit
                        </button>
                        {!isBusTrackingOrg(org.productType) && !isLorryOrg(org.productType) && (
                          <button
                            className="dropdown-item"
                            onClick={() => showClassroomsForOrganization(org)}
                          >
                            <FaDatabase /> View Classrooms
                          </button>
                        )}
                        {!isFleetAdmin && (
                        <button
                          className="dropdown-item delete"
                          onClick={async () => {
                            const confirmed = await CustomConfirm.show(
                              `Are you sure you want to delete ${org.name || org.code}? This will also delete all ${org.studentCount + org.teacherCount + org.parentCount} associated users.`,
                              'Delete Organization'
                            );
                            if (confirmed) {
                              handleDeleteOrganization(org.id);
                            }
                          }}
                        >
                          <FaTrash /> Delete
                        </button>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Users Tab */}
        {selectedTab === 'users' && (
          <div className="users-content">
            <div className="section-header">
              <h2>Users</h2>
              {/* ADDITIONAL ADMIN BUTTONS - COMMENTED OUT FOR PRODUCTION */}
              {/* <div style={{ marginTop: '20px', padding: '20px', backgroundColor: '#f8f9fa', borderRadius: '8px', border: '1px solid #dee2e6' }}>
                <h3 style={{ marginBottom: '15px', color: '#495057' }}>🔧 Admin Tools</h3>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px' }}>
                  <button 
                    onClick={handleMigrateParentSchoolCodes}
                    style={{
                      background: '#4CAF50',
                      color: 'white',
                      border: 'none',
                      padding: '8px 16px',
                      borderRadius: '8px',
                      fontSize: '14px',
                      fontWeight: '600',
                      cursor: 'pointer'
                    }}
                  >
                    🚀 Migrate Parent Codes
                  </button>
                  <button 
                    onClick={handleMigrateClassOrganizationCodes}
                    style={{
                      background: '#9C27B0',
                      color: 'white',
                      border: 'none',
                      padding: '8px 16px',
                      borderRadius: '8px',
                      fontSize: '14px',
                      fontWeight: '600',
                      cursor: 'pointer'
                    }}
                  >
                    🏫 Migrate Class Org Codes
                  </button>
                  <button 
                    onClick={handleMigrateConversationsAndNotifications}
                    style={{
                      background: '#FF5722',
                      color: 'white',
                      border: 'none',
                      padding: '8px 16px',
                      borderRadius: '8px',
                      fontSize: '14px',
                      fontWeight: '600',
                      cursor: 'pointer'
                    }}
                  >
                    💬 Migrate Conv & Notif
                  </button>
                  <button 
                    onClick={handleValidateStructure}
                    style={{
                      background: '#43BE83',
                      color: 'white',
                      border: 'none',
                      padding: '8px 16px',
                      borderRadius: '8px',
                      fontSize: '14px',
                      fontWeight: '600',
                      cursor: 'pointer'
                    }}
                  >
                    ✅ Validate Structure
                  </button>
                  <button 
                    onClick={handleCleanupOrphanedClasses}
                    style={{
                      background: '#FF6B6B',
                      color: 'white',
                      border: 'none',
                      padding: '8px 16px',
                      borderRadius: '8px',
                      fontSize: '14px',
                      fontWeight: '600',
                      cursor: 'pointer'
                    }}
                  >
                    🧹 Clean Orphaned Classes
                  </button>
                  <button 
                    onClick={async () => {
                      console.log('=== DEBUG PARENT DATA ===');
                      const parentUsers = users.filter(u => u.role === 'parent');
                      console.log('All parent users:', parentUsers.map(p => ({
                        id: p.id,
                        name: p.displayName,
                        schoolCode: p.schoolCode,
                        classCode: p.classCode,
                        children: p.children
                      })));
                      console.log('Selected organization:', selectedOrganization);
                      console.log('Available organizations:', organizations.map(o => ({ code: o.code, name: o.name })));
                      CustomToast.show(`Found ${parentUsers.length} parent users. Check console for details.`, 'info');
                    }}
                    style={{
                      background: '#FF9800',
                      color: 'white',
                      border: 'none',
                      padding: '8px 16px',
                      borderRadius: '8px',
                      fontSize: '14px',
                      fontWeight: '600',
                      cursor: 'pointer'
                    }}
                  >
                    🔍 Debug Parents
                  </button>
                  <button 
                    onClick={async () => {
                      console.log('=== DEBUG ALL USERS IN DATABASE ===');
                      try {
                        const allUsersSnapshot = await getDocs(collection(db, 'users'));
                        const allUsers = allUsersSnapshot.docs.map(doc => ({
                          id: doc.id,
                          ...doc.data()
                        })) as any[];
                        
                        const parentUsers = allUsers.filter(u => u.role === 'parent');
                        console.log('ALL users in database:', allUsers.map(u => ({
                          id: u.id,
                          name: u.displayName,
                          role: u.role,
                          schoolCode: u.schoolCode,
                          classCode: u.classCode
                        })));
                        console.log('Parent users in database:', parentUsers.map(p => ({
                          id: p.id,
                          name: p.displayName,
                          schoolCode: p.schoolCode,
                          classCode: p.classCode,
                          children: p.children
                        })));
                        
                        // Show detailed parent info
                        if (parentUsers.length > 0) {
                          const parent = parentUsers[0];
                          console.log('=== DETAILED PARENT INFO ===');
                          console.log('Parent ID:', parent.id);
                          console.log('Parent Name:', parent.displayName);
                          console.log('Parent SchoolCode:', parent.schoolCode);
                          console.log('Parent ClassCode:', parent.classCode);
                          console.log('Parent Children:', parent.children);
                          console.log('Selected Organization:', selectedOrganization);
                          console.log('Does schoolCode match organization?', parent.schoolCode === selectedOrganization);
                          console.log('Does classCode match organization?', parent.classCode === selectedOrganization);
                        }
                        console.log('Current selected organization:', selectedOrganization);
                        CustomToast.show(`Found ${parentUsers.length} parent users in database. Check console for details.`, 'info');
                      } catch (error) {
                        console.error('Error fetching all users:', error);
                        CustomToast.show('Error fetching users from database', 'error');
                      }
                    }}
                    style={{
                      background: '#E91E63',
                      color: 'white',
                      border: 'none',
                      padding: '8px 16px',
                      borderRadius: '8px',
                      fontSize: '14px',
                      fontWeight: '600',
                      cursor: 'pointer'
                    }}
                  >
                    🔥 Debug All Users
                  </button>
                  <button 
                    onClick={async () => {
                      console.log('=== DEBUG CLASSES COLLECTION ===');
                      try {
                        const classesSnapshot = await getDocs(collection(db, 'classes'));
                        const allClasses = classesSnapshot.docs.map(doc => ({
                          id: doc.id,
                          ...doc.data()
                        })) as any[];
                        
                        console.log('ALL classes in database:', allClasses.map(c => ({
                          id: c.id,
                          classCode: c.classCode,
                          joinCode: c.joinCode,
                          teacherId: c.teacherId,
                          organizationCode: c.organizationCode,
                          schoolCode: c.schoolCode
                        })));
                        
                        // Look for class with classCode test1234
                        const targetClass = allClasses.find(c => c.classCode === 'test1234' || c.joinCode === 'test1234');
                        if (targetClass) {
                          console.log('Found target class:', targetClass);
                          console.log('Target class details:', {
                            id: targetClass.id,
                            classCode: targetClass.classCode,
                            joinCode: targetClass.joinCode,
                            teacherId: targetClass.teacherId,
                            organizationCode: targetClass.organizationCode,
                            schoolCode: targetClass.schoolCode,
                            className: targetClass.className,
                            parents: targetClass.parents
                          });
                        } else {
                          console.log('No class found with classCode or joinCode: test1234');
                        }
                        
                        // Also look for any class that might match
                        console.log('All class codes found:', allClasses.map(c => ({
                          id: c.id,
                          classCode: c.classCode,
                          joinCode: c.joinCode,
                          organizationCode: c.organizationCode
                        })));
                        
                        // Check if we need to fix class organization codes
                        const classesWithoutOrgCode = allClasses.filter(c => !c.organizationCode);
                        if (classesWithoutOrgCode.length > 0) {
                          console.log('Classes missing organizationCode:', classesWithoutOrgCode.map(c => ({
                            id: c.id,
                            classCode: c.classCode,
                            joinCode: c.joinCode
                          })));
                        }
                        
                        CustomToast.show(`Found ${allClasses.length} classes in database. Check console for details.`, 'info');
                      } catch (error) {
                        console.error('Error fetching classes:', error);
                        CustomToast.show('Error fetching classes from database', 'error');
                      }
                    }}
                    style={{
                      background: '#673AB7',
                      color: 'white',
                      border: 'none',
                      padding: '8px 16px',
                      borderRadius: '8px',
                      fontSize: '14px',
                      fontWeight: '600',
                      cursor: 'pointer'
                    }}
                  >
                    🏫 Debug Classes
                  </button>
                  <button 
                    onClick={async () => {
                      console.log('=== SIMPLE PARENT FIX ===');
                      try {
                        // Get the teacher's schoolCode (MAX001)
                        const teacher = users.find(u => u.role === 'teacher');
                        if (!teacher) {
                          CustomToast.show('No teacher found', 'error');
                          return;
                        }
                        
                        const teacherSchoolCode = teacher.schoolCode; // Should be MAX001
                        console.log(`Teacher schoolCode: ${teacherSchoolCode}`);
                        
                        // Find parent with wrong schoolCode
                        const parent = users.find(u => u.role === 'parent' && u.schoolCode !== teacherSchoolCode);
                        if (!parent) {
                          CustomToast.show('No parent found with wrong schoolCode', 'info');
                          return;
                        }
                        
                        console.log(`Fixing parent ${parent.displayName}: schoolCode from ${parent.schoolCode} to ${teacherSchoolCode}`);
                        
                        // Update parent's schoolCode directly
                        await updateDoc(doc(db, 'users', parent.id), {
                          schoolCode: teacherSchoolCode,
                          updatedAt: serverTimestamp()
                        });
                        
                        CustomToast.show(`Fixed parent ${parent.displayName} schoolCode to ${teacherSchoolCode}`, 'success');
                        
                        // Reload data
                        await loadData();
                        
                      } catch (error) {
                        console.error('Error fixing parent:', error);
                        CustomToast.show('Error fixing parent', 'error');
                      }
                    }}
                    style={{
                      background: '#FF5722',
                      color: 'white',
                      border: 'none',
                      padding: '8px 16px',
                      borderRadius: '8px',
                      fontSize: '14px',
                      fontWeight: '600',
                      cursor: 'pointer'
                    }}
                  >
                    🎯 Simple Parent Fix
                  </button>
                </div>
              </div>
              */}
              {/* ADDITIONAL ADMIN BUTTONS - COMMENTED OUT FOR PRODUCTION */}
              {/* <button 
                onClick={handleRunMigration}
                style={{
                  background: '#5041BC',
                  color: 'white',
                  border: 'none',
                  padding: '8px 16px',
                  borderRadius: '8px',
                  fontSize: '14px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  marginLeft: '12px'
                }}
              >
                <FaDatabase style={{ marginRight: '8px' }} />
                Run Migration
              </button>
              <button 
                onClick={handleValidateStructure}
                style={{
                  background: '#43BE83',
                  color: 'white',
                  border: 'none',
                  padding: '8px 16px',
                  borderRadius: '8px',
                  fontSize: '14px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  marginLeft: '12px'
                }}
              >
                Validate Structure
              </button>
              <button 
                onClick={handleCleanupOrphanedClasses}
                style={{
                  background: '#FF6B6B',
                  color: 'white',
                  border: 'none',
                  padding: '8px 16px',
                  borderRadius: '8px',
                  fontSize: '14px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  marginLeft: '12px'
                }}
              >
                Clean Orphaned Classes
              </button>
              <button 
                onClick={handleMigrateClassOrganizationCodes}
                style={{
                  background: '#9C27B0',
                  color: 'white',
                  border: 'none',
                  padding: '8px 16px',
                  borderRadius: '8px',
                  fontSize: '14px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  marginLeft: '12px'
                }}
              >
                Migrate Class Org Codes
              </button>
              <button 
                onClick={handleMigrateConversationsAndNotifications}
                style={{
                  background: '#FF5722',
                  color: 'white',
                  border: 'none',
                  padding: '8px 16px',
                  borderRadius: '8px',
                  fontSize: '14px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  marginLeft: '12px'
                }}
              >
                Migrate Conv & Notif
              </button>
              <button 
                onClick={handleMigrateParentSchoolCodes}
                style={{
                  background: '#4CAF50',
                  color: 'white',
                  border: 'none',
                  padding: '8px 16px',
                  borderRadius: '8px',
                  fontSize: '14px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  marginLeft: '12px'
                }}
              >
                Migrate Parent Codes
              </button>
              <button 
                onClick={() => {
                  console.log('=== DEBUG CURRENT STATE ===');
                  console.log('Organizations:', organizations.map(org => ({ id: org.id, code: org.code, name: org.name })));
                  console.log('Users:', users.map(u => ({ name: u.displayName, role: u.role, schoolCode: u.schoolCode, classCode: u.classCode })));
                  console.log('Posts:', posts.map(p => ({ id: p.id, classId: p.classId, authorName: p.authorName })));
                  console.log('Deleted Organizations:', Array.from(deletedOrganizations));
                  console.log('Selected Organization:', selectedOrganization);
                  console.log('=== END DEBUG ===');
                }}
                style={{
                  background: '#F7941D',
                  color: 'white',
                  border: 'none',
                  padding: '8px 16px',
                  borderRadius: '8px',
                  fontSize: '14px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  marginLeft: '12px'
                }}
              >
                Debug State
              </button>
              <button 
                onClick={handleDeleteEverything}
                style={{
                  background: '#EA8F95',
                  color: 'white',
                  border: 'none',
                  padding: '8px 16px',
                  borderRadius: '8px',
                  fontSize: '14px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  marginLeft: '12px'
                }}
              >
                Delete Everything
              </button>
              
              <button 
                onClick={checkSubscriptionExpiration}
                style={{
                  background: '#4CAF50',
                  color: 'white',
                  border: 'none',
                  padding: '8px 16px',
                  borderRadius: '8px',
                  fontSize: '14px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  marginLeft: '12px'
                }}
              >
                Check Subscriptions
              </button>
              
              <button 
                onClick={async () => {
                  console.log('=== DEBUG: CHECKING ORGANIZATIONS ===');
                  const orgsQuery = query(collection(db, 'organizations'));
                  const orgsSnapshot = await getDocs(orgsQuery);
                  console.log('Raw organizations from DB:', orgsSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
                  console.log('Current organizations state:', organizations);
                  console.log('Selected organization:', selectedOrganization);
                  CustomToast.show(`Found ${orgsSnapshot.docs.length} organizations in DB`, 'info');
                }}
                style={{
                  background: '#FF9800',
                  color: 'white',
                  border: 'none',
                  padding: '8px 16px',
                  borderRadius: '8px',
                  fontSize: '14px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  marginLeft: '12px'
                }}
              >
                Debug Orgs
              </button>
              
              <button 
                onClick={async () => {
                  console.log('=== TESTING FIREBASE WRITE ===');
                  try {
                    const testDoc = await addDoc(collection(db, 'test'), {
                      test: true,
                      timestamp: new Date(),
                      message: 'Testing Firebase write access'
                    });
                    console.log('Test document added with ID:', testDoc.id);
                    CustomToast.show('Firebase write test successful!', 'success');
                  } catch (error) {
                    console.error('Firebase write test failed:', error);
                    CustomToast.show('Firebase write test failed!', 'error');
                  }
                }}
                style={{
                  background: '#9C27B0',
                  color: 'white',
                  border: 'none',
                  padding: '8px 16px',
                  borderRadius: '8px',
                  fontSize: '14px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  marginLeft: '12px'
                }}
              >
                Test Firebase
              </button>
              */}


            </div>

            <div className="filter-buttons">
              <button
                className={`filter-btn ${selectedFilter === 'All users' ? 'active' : ''}`}
                onClick={() => setSelectedFilter('All users')}
              >
                All users ({users.length})
              </button>
              <button
                className={`filter-btn ${selectedFilter === 'Teachers' ? 'active' : ''}`}
                onClick={() => setSelectedFilter('Teachers')}
              >
                Teachers ({getTeacherCount()})
              </button>
              <button
                className={`filter-btn ${selectedFilter === 'Parents' ? 'active' : ''}`}
                onClick={() => setSelectedFilter('Parents')}
              >
                Parents ({getParentCount()})
              </button>
              <button
                className={`filter-btn ${selectedFilter === 'Students' ? 'active' : ''}`}
                onClick={() => setSelectedFilter('Students')}
              >
                Students ({getStudentCount()})
              </button>
            </div>

            <div className="users-list">
              {getFilteredUsers().map((user) => (
                <div key={user.id} className="user-card">
                  <div className="user-avatar">
                    <FaUser />
                  </div>
                  <div className="user-info">
                    <h3 className="user-name">{user.displayName || 'Unknown User'}</h3>
                    <p className="user-email">{user.email}</p>
                    <p className="user-institution">
                      {(() => {
                        const userOrgCode = user.organizationCode || user.schoolCode || user.classCode || user.institution || user.associatedClassroomId;
                        return userOrgCode ? `Organization Code: ${userOrgCode}` : 'No Organization Code';
                      })()}
                    </p>
                    {user.role === 'parent' && user.children && user.children.length > 0 && (
                      <p className="user-children">
                        Children: {user.children.length} {user.children.length === 1 ? 'child' : 'children'}
                      </p>
                    )}
                    {user.role === 'student' && user.parentName && (
                      <p className="user-parent">
                        Parent: {user.parentName}
                      </p>
                    )}
                  </div>
                  <div className="user-stats">
                    <div className="stat">
                      <span className="stat-label">Role</span>
                      <span className="stat-value">{user.role}</span>
                    </div>
                    <div className="stat">
                      <span className="stat-label">
                        Organization Code
                      </span>
                      <span className="stat-value">
                        {(() => {
                          const userOrgCode = user.organizationCode || user.schoolCode || user.classCode || user.institution || user.associatedClassroomId;
                          return userOrgCode || 'N/A';
                        })()}
                      </span>
                    </div>
                    {user.role === 'parent' && user.children && user.children.length > 0 && (
                      <div className="stat">
                        <span className="stat-label">Children</span>
                        <span className="stat-value">{user.children.length}</span>
                      </div>
                    )}
                    {user.role === 'student' && user.parentName && (
                      <div className="stat">
                        <span className="stat-label">Parent</span>
                        <span className="stat-value">{user.parentName}</span>
                      </div>
                    )}
                  </div>
                  <div className="user-status">
                    <span
                      className="status-badge"
                      style={{ backgroundColor: getStatusColor(user.role) }}
                    >
                      {getStatusText(user.role)}
                    </span>
                  </div>
                  <div className="user-actions">
                    <button
                      className="action-btn more"
                      onClick={() => _showUserDetails(user)}
                      title="View user details"
                    >
                      <FaEllipsisV />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Content Tab */}
        {selectedTab === 'content' && (
          <div className="content-content">
            {/* Debug Info - Only show in development */}

            {/* Organization Selection Warning - Only show when no organization is selected and organizations exist */}
            {selectedOrganization === 'all' && organizations.length > 0 && (
              <div className="organization-warning">
                <div className="warning-icon">⚠️</div>
                <div className="warning-text">
                  <h3>Select an Organization</h3>
                  <p>Please select an organization from the dropdown above before creating content.</p>
                  <p>Available organizations: {organizations.map(org => org.name).join(', ')}</p>
                </div>
              </div>
            )}

            <div className="content-tabs">
              <button
                className={`content-tab ${selectedFilter === 'posts' ? 'active' : ''}`}
                onClick={() => setSelectedFilter('posts')}
              >
                <FaNewspaper /> Posts
              </button>
              <button
                className={`content-tab ${selectedFilter === 'events' ? 'active' : ''}`}
                onClick={() => setSelectedFilter('events')}
              >
                <FaCalendarAlt /> Events
              </button>
              <button
                className={`content-tab ${selectedFilter === 'timetable' ? 'active' : ''}`}
                onClick={() => setSelectedFilter('timetable')}
              >
                <FaCalendarAlt /> Timetable
              </button>
              <button
                className={`content-tab ${selectedFilter === 'menu' ? 'active' : ''}`}
                onClick={() => setSelectedFilter('menu')}
              >
                <FaUtensils /> Menu
              </button>
              <button
                className={`content-tab ${selectedFilter === 'diary' ? 'active' : ''}`}
                onClick={() => {
                  setSelectedFilter('diary');
                  // Load classrooms when diary tab is selected
                  if (selectedOrganization !== 'all') {
                    loadOrganizationClassrooms(selectedOrganization);
                  }
                }}
              >
                <FaFileAlt /> Diary Entries
              </button>
              <button
                className={`content-tab ${selectedFilter === 'cloudinary' ? 'active' : ''}`}
                onClick={() => setSelectedFilter('cloudinary')}
              >
                <FaCloudUploadAlt /> Image Upload
              </button>
            </div>

            <div className="content-actions">
              {selectedFilter === 'posts' && selectedOrganization !== 'all' && (
                <button
                  className="create-button"
                  onClick={() => setShowCreatePost(true)}
                >
                  <FaPlus /> Create Post
                </button>
              )}
              {selectedFilter === 'events' && selectedOrganization !== 'all' && (
                <button
                  className="create-button"
                  onClick={() => setShowCreateEvent(true)}
                >
                  <FaPlus /> Create Event
                </button>
              )}
              {selectedFilter === 'timetable' && selectedOrganization !== 'all' && (
                <button
                  className="create-button"
                  onClick={() => setShowTimetableManagementScreen(true)}
                >
                  <FaPlus /> Manage Timetable
                </button>
              )}
              {selectedFilter === 'menu' && (
                <div className="button-group">
                  {selectedOrganization !== 'all' && (
                    <button
                      className="create-button"
                      onClick={() => setShowCreateMenuItem(true)}
                    >
                      <FaPlus /> Add Menu Item/Items
                    </button>
                  )}
                  <button
                    className="create-button"
                    style={{ backgroundColor: '#EA8F95', marginLeft: selectedOrganization !== 'all' ? '12px' : '0' }}
                    onClick={async () => {
                      const itemCount = selectedOrganization === 'all' ? allMenuItems.length : menuItems.length;
                      const batchMenuCount = selectedOrganization === 'all' ? allBatchMenus.length : batchMenus.length;
                      const orgText = selectedOrganization === 'all' ? 'all organizations' : `organization ${selectedOrganization}`;
                      const confirmed = await CustomConfirm.show(
                        `Are you sure you want to delete ALL ${itemCount} menu item(s) and ${batchMenuCount} batch menu(s) from ${orgText}? This action cannot be undone.`,
                        'Delete All Menu Items & Batch Menus'
                      );
                      if (confirmed) {
                        handleDeleteAllMenuItems();
                      }
                    }}
                  >
                    <FaTrash /> Delete All Menu Items
                  </button>
                </div>
              )}
              {selectedFilter === 'diary' && selectedOrganization !== 'all' && (
                <div className="button-group">
                  <button
                    className="create-button"
                    onClick={async () => {
                      // Load classrooms if not already loaded
                      if (organizationClassrooms.length === 0) {
                        await loadOrganizationClassrooms(selectedOrganization);
                      }
                      setShowDiaryClassroomSelection(true);
                    }}
                  >
                    <FaPlus /> Add Diary Entry
                  </button>
                </div>
              )}
            </div>

            <div className="content-list">
              {selectedFilter === 'posts' && posts.map((post) => (
                <div key={post.id} className="content-card">
                  <div className="content-info">
                    <h3 className="content-title">{post.textContent.substring(0, 50)}...</h3>
                    <p className="content-author">By {post.authorName}</p>
                    <p className="content-time">{new Date(post.timestamp?.toDate()).toLocaleDateString()}</p>
                  </div>
                  <div className="content-actions">
                    <button className="action-btn edit">
                      <FaEdit />
                    </button>
                    <button
                      className="action-btn delete"
                      onClick={async () => {
                        const confirmed = await CustomConfirm.show(
                          'Are you sure you want to delete this post?',
                          'Delete Post'
                        );
                        if (confirmed) {
                          handleDeletePost(post.id);
                        }
                      }}
                    >
                      <FaTrash />
                    </button>
                  </div>
                </div>
              ))}

              {selectedFilter === 'events' && (
                <>
                  {events.map((event) => (
                    <div key={event.id} className="content-card">
                      <div className="content-info">
                        <h3 className="content-title">{event.title}</h3>
                        <p className="content-description">{event.description}</p>
                        <p className="content-time">{new Date(event.date?.toDate()).toLocaleDateString()}</p>
                      </div>
                      <div className="content-actions">
                        <button className="action-btn edit">
                          <FaEdit />
                        </button>
                        <button
                          className="action-btn delete"
                          onClick={async () => {
                            const confirmed = await CustomConfirm.show(
                              'Are you sure you want to delete this event?',
                              'Delete Event'
                            );
                            if (confirmed) {
                              handleDeleteEvent(event.id);
                            }
                          }}
                        >
                          <FaTrash />
                        </button>
                      </div>
                    </div>
                  ))}

                  {/* Batch Events */}
                  {batchEvents.map((batchEvent) => (
                    <div key={batchEvent.id} className="content-card batch-event-card">
                      <div className="content-info">
                        <h3 className="content-title">
                          Batch Event #{batchEvent.id.substring(0, 8)}
                        </h3>
                        <p className="content-category">
                          {batchEvent.periodType === 'week' ? 'Weekly' : 'Monthly'} events
                        </p>
                        <p className="content-description">
                          {batchEvent.events?.length || 0} events • {
                            (() => {
                              try {
                                const startDate = batchEvent.startDate?.toDate ? batchEvent.startDate.toDate() : (batchEvent.startDate instanceof Date ? batchEvent.startDate : new Date(batchEvent.startDate));
                                const endDate = batchEvent.endDate?.toDate ? batchEvent.endDate.toDate() : (batchEvent.endDate instanceof Date ? batchEvent.endDate : new Date(batchEvent.endDate));
                                return `${startDate.toLocaleDateString()} - ${endDate.toLocaleDateString()}`;
                              } catch (e) {
                                return 'Invalid date';
                              }
                            })()
                          }
                        </p>
                        {/* Batch Event Items */}
                        <div className="batch-items-list">
                          {batchEvent.events?.map((event: any, index: number) => (
                            <div key={index} className="batch-item">
                              <div className="batch-item-header">
                                <span className="batch-item-title">{event.title}</span>
                                {event.time && <span className="batch-item-time">{event.time}</span>}
                              </div>
                              {event.location && (
                                <span className="batch-item-location">{event.location}</span>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                      <div className="content-actions">
                        <button
                          className="action-btn delete"
                          onClick={async () => {
                            const confirmed = await CustomConfirm.show(
                              'Are you sure you want to delete this batch event? This will delete all events in the batch.',
                              'Delete Batch Event'
                            );
                            if (confirmed) {
                              handleDeleteBatchEvent(batchEvent.id);
                            }
                          }}
                        >
                          <FaTrash />
                        </button>
                      </div>
                    </div>
                  ))}
                </>
              )}

              {selectedFilter === 'menu' && (
                <>
                  {/* Individual Menu Items */}
                  {menuItems.map((item) => (
                    <div key={item.id} className="content-card">
                      <div className="content-info">
                        <h3 className="content-title">{item.name}</h3>
                        <p className="content-description">{item.description}</p>
                        <p className="content-category">{item.category}</p>
                      </div>
                      <div className="content-actions">
                        <button className="action-btn edit">
                          <FaEdit />
                        </button>
                        <button
                          className="action-btn delete"
                          onClick={async () => {
                            const confirmed = await CustomConfirm.show(
                              `Are you sure you want to delete "${item.name}" (${item.category})?`,
                              'Delete Menu Item'
                            );
                            if (confirmed) {
                              handleDeleteMenuItem(item.id, item.name, item.category);
                            }
                          }}
                        >
                          <FaTrash />
                        </button>
                      </div>
                    </div>
                  ))}

                  {/* Batch Menus */}
                  {batchMenus.map((batchMenu) => (
                    <div key={batchMenu.id} className="content-card batch-menu-card">
                      <div className="content-info">
                        <div className="batch-header">
                          <h3 className="content-title">
                            Batch Menu #{batchMenu.id.substring(0, 8)}
                          </h3>
                          <span className="batch-badge">BATCH</span>
                        </div>
                        <p className="content-description">
                          {batchMenu.periodType === 'week' ? 'Weekly' : 'Monthly'} menu
                        </p>
                        <p className="content-category">
                          {batchMenu.menuItems?.length || 0} items • {new Date(batchMenu.startDate?.toDate()).toLocaleDateString()} - {new Date(batchMenu.endDate?.toDate()).toLocaleDateString()}
                        </p>

                        {/* Batch Menu Items */}
                        <div className="batch-items">
                          {batchMenu.menuItems?.map((item: any, index: number) => (
                            <div key={index} className="batch-item">
                              <div className="batch-item-info">
                                <span className="batch-item-name">{item.name}</span>
                                <span className="batch-item-category">{item.category}</span>
                              </div>
                              {item.imageUrl && (
                                <div className="batch-item-image">
                                  <img
                                    src={item.imageUrl}
                                    alt={item.name}
                                    style={{ width: '40px', height: '40px', objectFit: 'cover', borderRadius: '4px' }}
                                  />
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                      <div className="content-actions">
                        <button className="action-btn edit">
                          <FaEdit />
                        </button>
                        <button
                          className="action-btn delete"
                          onClick={async () => {
                            const confirmed = await CustomConfirm.show(
                              'Are you sure you want to delete this batch menu? This will delete all items in the batch.',
                              'Delete Batch Menu'
                            );
                            if (confirmed) {
                              handleDeleteBatchMenu(batchMenu.id);
                            }
                          }}
                        >
                          <FaTrash />
                        </button>
                      </div>
                    </div>
                  ))}
                </>
              )}

              {selectedFilter === 'timetable' && (
                <>
                  {/* Timetable Management Card */}
                  <div className="content-card">
                    <div className="content-info">
                      <h3 className="content-title">📅 Timetable Management</h3>
                      <p className="content-description">
                        Manage class schedules and activities for all organizations
                      </p>
                      <p className="content-category">
                        Select an organization to view classrooms
                      </p>
                    </div>
                    <div className="content-actions">
                      <button
                        className="action-btn primary-large"
                        onClick={() => {
                          if (selectedOrganization === 'all') {
                            alert('Please select a specific organization first');
                          } else {
                            setShowTimetableManagementScreen(true);
                          }
                        }}
                        title="Manage Timetables"
                      >
                        <FaEdit /> Manage Timetables
                      </button>
                    </div>
                  </div>

                  {/* Holiday Management Card */}
                  <div className="content-card">
                    <div className="content-info">
                      <h3 className="content-title">🎉 Holiday & Break Management</h3>
                      <p className="content-description">
                        Add public holidays, school breaks, exam periods, and half days.
                        These dates will appear in the mobile app timetable.
                      </p>
                      <p className="content-category">
                        {holidays.length} holidays configured
                      </p>
                    </div>
                    <div className="content-actions">
                      <button
                        className="action-btn edit"
                        onClick={async () => {
                          if (selectedOrganization !== 'all') {
                            await loadHolidays(selectedOrganization);
                          }
                          setShowAddHolidayModal(true);
                        }}
                        title="Manage Holidays"
                      >
                        <FaPlus />
                      </button>
                    </div>
                  </div>

                  {/* Weekly Summary Card */}
                  <div className="content-card">
                    <div className="content-info">
                      <h3 className="content-title">📧 Weekly Summary Generator</h3>
                      <p className="content-description">
                        Generate and send weekly summaries to parents (every Friday & Saturday at 6 PM).
                        Includes timetable, events, tests, and key activities.
                      </p>
                      <p className="content-category">
                        Linked to each child's classroom
                      </p>
                    </div>
                    <div className="content-actions">
                      <button
                        className="action-btn edit"
                        onClick={async () => {
                          if (selectedOrganization !== 'all') {
                            await loadOrganizationClassrooms(selectedOrganization);
                            await loadHolidays(selectedOrganization);
                          }
                          setShowWeeklySummaryModal(true);
                        }}
                        title="Generate Weekly Summary"
                      >
                        <FaCalendarAlt />
                      </button>
                    </div>
                  </div>
                </>
              )}

              {selectedFilter === 'diary' && (
                <>
                  {diaryEntries.map((entry) => (
                    <div key={entry.id} className="content-card">
                      <div className="content-info">
                        <h3 className="content-title">
                          {entry.type === 'general' ? 'General Note' : `Personal Note for ${entry.studentName}`}
                        </h3>
                        <p className="content-description">{entry.content}</p>
                        <p className="content-category">
                          Mood: {entry.mood} • Teacher: {entry.teacherName} •
                          {new Date(entry.timestamp?.toDate?.() || entry.timestamp).toLocaleDateString()}
                        </p>
                        {entry.parentReplies && entry.parentReplies.length > 0 && (
                          <p className="content-category">
                            {entry.parentReplies.length} reply{entry.parentReplies.length !== 1 ? 'ies' : 'y'}
                          </p>
                        )}
                      </div>
                      <div className="content-actions">
                        <button
                          className="action-btn delete"
                          onClick={async () => {
                            const confirmed = await CustomConfirm.show(
                              'Are you sure you want to delete this diary entry? This will also delete all associated replies.',
                              'Delete Diary Entry'
                            );
                            if (confirmed) {
                              handleDeleteDiaryEntry(entry.id);
                            }
                          }}
                        >
                          <FaTrash />
                        </button>
                      </div>
                    </div>
                  ))}
                </>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Modals */}
      {showAddOrganization && (
        <CreateOrgWizard
          initialKind={wizardInitialKind}
          initialLorryMode={wizardInitialLorryMode}
          onClose={() => setShowAddOrganization(false)}
          onCreate={submitWizardOrg}
        />
      )}

      {showCreatePost && (
        <div className="modal-overlay">
          <div className="modal">
            <h2>Create Post</h2>
            <form onSubmit={(e) => {
              e.preventDefault();
              const formData = new FormData(e.currentTarget);
              handleCreatePost({
                textContent: formData.get('content') as string,
                imageUrl: formData.get('imageUrl') as string
              });
            }}>
              <textarea name="content" placeholder="Post content..." required />
              <input name="imageUrl" placeholder="Image URL (optional)" />
              <div className="modal-actions">
                <button type="button" onClick={() => setShowCreatePost(false)}>Cancel</button>
                <button type="submit">Create Post</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showCreateEvent && (
        <div className="modal-overlay">
          <div className="modal">
            <h2>Create Event</h2>

            {/* Mode Selection */}
            <div className="mode-selector">
              <button
                type="button"
                className={`mode-btn ${eventCreationMode === 'single' ? 'active' : ''}`}
                onClick={() => setEventCreationMode('single')}
              >
                Single Event
              </button>
              <button
                type="button"
                className={`mode-btn ${eventCreationMode === 'batch' ? 'active' : ''}`}
                onClick={() => setEventCreationMode('batch')}
              >
                Batch Events
              </button>
            </div>

            {/* Single Event Mode */}
            {eventCreationMode === 'single' && (
              <form onSubmit={(e) => {
                e.preventDefault();
                const formData = new FormData(e.currentTarget);
                const imageFile = (document.getElementById('eventImage') as HTMLInputElement)?.files?.[0];

                handleCreateEvent({
                  title: formData.get('title') as string,
                  description: formData.get('description') as string,
                  date: new Date(formData.get('date') as string),
                  time: formData.get('time') as string,
                  location: formData.get('location') as string,
                  imageUrl: formData.get('imageUrl') as string,
                  imageFile: imageFile
                });
              }}>
                <input name="title" placeholder="Event Title" required />
                <textarea name="description" placeholder="Event Description" required />
                <input name="date" type="date" required />
                <input name="time" type="time" placeholder="Event Time" />
                <input name="location" placeholder="Event Location" />

                <div style={{ marginBottom: '16px' }}>
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', color: '#303972' }}>
                    Event Image
                  </label>
                  <input
                    id="eventImage"
                    name="imageFile"
                    type="file"
                    accept="image/*"
                    style={{
                      width: '100%',
                      padding: '8px',
                      border: '1px solid #ddd',
                      borderRadius: '4px',
                      fontSize: '14px'
                    }}
                  />
                  <small style={{ color: '#666', fontSize: '12px' }}>
                    Upload an image or provide a URL below
                  </small>
                </div>

                <input name="imageUrl" placeholder="Event Image URL (optional)" />

                <div className="modal-actions">
                  <button type="button" onClick={() => setShowCreateEvent(false)}>Cancel</button>
                  <button type="submit">Create Event</button>
                </div>
              </form>
            )}

            {/* Batch Event Mode */}
            {eventCreationMode === 'batch' && (
              <div className="batch-event-form">
                {/* Period Type Selection */}
                <div className="form-group">
                  <label>Period Type</label>
                  <div className="period-selector">
                    <button
                      type="button"
                      className={`period-btn ${batchEventData.periodType === 'week' ? 'active' : ''}`}
                      onClick={() => setBatchEventData(prev => ({ ...prev, periodType: 'week' }))}
                    >
                      Week
                    </button>
                    <button
                      type="button"
                      className={`period-btn ${batchEventData.periodType === 'month' ? 'active' : ''}`}
                      onClick={() => setBatchEventData(prev => ({ ...prev, periodType: 'month' }))}
                    >
                      Month
                    </button>
                  </div>
                </div>

                {/* Date Selection */}
                <div className="form-group">
                  <label>Start Date (Weekdays Only)</label>
                  <input
                    type="date"
                    value={batchEventData.startDate}
                    onChange={(e) => {
                      const selectedDate = new Date(e.target.value);
                      const dayOfWeek = selectedDate.getDay();
                      if (dayOfWeek === 0 || dayOfWeek === 6) {
                        CustomToast.show('Please select a weekday (Monday-Friday)', 'error');
                        return;
                      }
                      setBatchEventData(prev => ({ ...prev, startDate: e.target.value }));
                    }}
                    min={formatDateForInput(getNextWeekday(new Date()))}
                  />
                  <small style={{ color: '#666', fontSize: '12px' }}>
                    Events are only available Monday-Friday
                  </small>
                </div>

                {/* Events */}
                <div className="form-group">
                  <label>Events (Enable at least 1)</label>
                  <div className="events-grid">
                    {batchEventData.events.map((event, index) => (
                      <div key={index} className="event-item-card">
                        <div className="item-header">
                          <label className="checkbox-label">
                            <input
                              type="checkbox"
                              checked={event.isEnabled}
                              onChange={(e) => {
                                const newEvents = [...batchEventData.events];
                                newEvents[index].isEnabled = e.target.checked;
                                setBatchEventData(prev => ({ ...prev, events: newEvents }));
                              }}
                            />
                            <span className="event-number">Event {index + 1}</span>
                          </label>
                        </div>
                        {event.isEnabled && (
                          <div className="item-fields">
                            <input
                              type="text"
                              placeholder="Event Title *"
                              value={event.title}
                              onChange={(e) => {
                                const newEvents = [...batchEventData.events];
                                newEvents[index].title = e.target.value;
                                setBatchEventData(prev => ({ ...prev, events: newEvents }));
                              }}
                            />
                            <textarea
                              placeholder="Description *"
                              value={event.description}
                              onChange={(e) => {
                                const newEvents = [...batchEventData.events];
                                newEvents[index].description = e.target.value;
                                setBatchEventData(prev => ({ ...prev, events: newEvents }));
                              }}
                            />
                            <input
                              type="time"
                              placeholder="Time"
                              value={event.time}
                              onChange={(e) => {
                                const newEvents = [...batchEventData.events];
                                newEvents[index].time = e.target.value;
                                setBatchEventData(prev => ({ ...prev, events: newEvents }));
                              }}
                            />
                            <input
                              type="text"
                              placeholder="Location"
                              value={event.location}
                              onChange={(e) => {
                                const newEvents = [...batchEventData.events];
                                newEvents[index].location = e.target.value;
                                setBatchEventData(prev => ({ ...prev, events: newEvents }));
                              }}
                            />

                            {/* Image upload */}
                            <div className="item-image-upload">
                              <label>Event Photo</label>
                              {event.imageFile ? (
                                <div className="image-preview">
                                  <img
                                    src={URL.createObjectURL(event.imageFile)}
                                    alt="Preview"
                                    style={{ width: '80px', height: '80px', objectFit: 'cover', borderRadius: '6px' }}
                                  />
                                  <button
                                    type="button"
                                    onClick={() => {
                                      const newEvents = [...batchEventData.events];
                                      newEvents[index].imageFile = null;
                                      setBatchEventData(prev => ({ ...prev, events: newEvents }));
                                    }}
                                    className="remove-image-btn"
                                    style={{ fontSize: '12px', padding: '4px 8px' }}
                                  >
                                    Remove
                                  </button>
                                </div>
                              ) : (
                                <div className="image-upload-placeholder">
                                  <input
                                    type="file"
                                    accept="image/*"
                                    onChange={(e) => {
                                      const file = e.target.files?.[0];
                                      if (file) {
                                        const newEvents = [...batchEventData.events];
                                        newEvents[index].imageFile = file;
                                        setBatchEventData(prev => ({ ...prev, events: newEvents }));
                                      }
                                    }}
                                    id={`batch-event-image-${index}`}
                                    style={{ display: 'none' }}
                                  />
                                  <label htmlFor={`batch-event-image-${index}`} className="upload-label">
                                    <FaImage style={{ fontSize: '16px', color: '#5041BC' }} />
                                    <span>Add photo</span>
                                  </label>
                                </div>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>

                <div className="modal-actions">
                  <button type="button" onClick={() => setShowCreateEvent(false)}>Cancel</button>
                  <button
                    type="button"
                    onClick={() => handleCreateBatchEvent(batchEventData)}
                    disabled={batchEventData.events.filter(event => event.isEnabled).length < 1}
                  >
                    Create Batch Events
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {showCreateMenuItem && (
        <div className="modal-overlay">
          <div className="modal">
            <h2>Add Menu Item/Items</h2>

            {/* Mode Selection */}
            <div className="mode-selector">
              <button
                type="button"
                className={`mode-btn ${menuCreationMode === 'single' ? 'active' : ''}`}
                onClick={() => setMenuCreationMode('single')}
              >
                Single Item
              </button>
              <button
                type="button"
                className={`mode-btn ${menuCreationMode === 'batch' ? 'active' : ''}`}
                onClick={() => setMenuCreationMode('batch')}
              >
                Batch Menu (2-4 items)
              </button>
            </div>

            {/* Single Item Mode */}
            {menuCreationMode === 'single' && (
              <div className="single-item-form">
                <div className="form-group">
                  <label>Item Name *</label>
                  <input
                    type="text"
                    value={singleMenuItem.name}
                    onChange={(e) => setSingleMenuItem(prev => ({ ...prev, name: e.target.value }))}
                    placeholder="e.g., Orange Juice"
                    required
                  />
                </div>

                <div className="form-group">
                  <label>Description *</label>
                  <textarea
                    value={singleMenuItem.description}
                    onChange={(e) => setSingleMenuItem(prev => ({ ...prev, description: e.target.value }))}
                    placeholder="Describe the menu item..."
                    required
                    rows={3}
                  />
                </div>

                <div className="form-group">
                  <label>Category *</label>
                  <select
                    value={singleMenuItem.category}
                    onChange={(e) => setSingleMenuItem(prev => ({ ...prev, category: e.target.value }))}
                    required
                  >
                    <option value="">Select Category</option>
                    <option value="drinks">Drinks</option>
                    <option value="break">Break</option>
                    <option value="lunch">Lunch</option>
                    <option value="snack">Snack</option>
                  </select>
                </div>

                <div className="form-group">
                  <label>Item Photo</label>
                  <div className="image-upload-section">
                    {singleMenuItem.imageFile ? (
                      <div className="image-preview">
                        <img
                          src={URL.createObjectURL(singleMenuItem.imageFile)}
                          alt="Preview"
                          style={{ width: '100px', height: '100px', objectFit: 'cover', borderRadius: '8px' }}
                        />
                        <div className="image-actions">
                          <button
                            type="button"
                            onClick={removeSingleItemImage}
                            className="remove-image-btn"
                          >
                            Remove
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div className="image-upload-placeholder">
                        <input
                          type="file"
                          accept="image/*"
                          onChange={handleSingleItemImageChange}
                          id="single-item-image"
                          style={{ display: 'none' }}
                        />
                        <label htmlFor="single-item-image" className="upload-label">
                          <FaImage style={{ fontSize: '24px', color: '#5041BC' }} />
                          <span>Click to upload photo</span>
                        </label>
                      </div>
                    )}
                  </div>
                </div>

                <div className="modal-actions">
                  <button type="button" onClick={() => setShowCreateMenuItem(false)}>Cancel</button>
                  <button type="button" onClick={handleCreateSingleMenuItem}>Add Menu Item</button>
                </div>
              </div>
            )}

            {/* Batch Menu Mode */}
            {menuCreationMode === 'batch' && (
              <div className="batch-menu-form">
                {/* Period Type Selection */}
                <div className="form-group">
                  <label>Period Type</label>
                  <div className="period-selector">
                    <button
                      type="button"
                      className={`period-btn ${batchMenuData.periodType === 'week' ? 'active' : ''}`}
                      onClick={() => setBatchMenuData(prev => ({ ...prev, periodType: 'week' }))}
                    >
                      Week
                    </button>
                    <button
                      type="button"
                      className={`period-btn ${batchMenuData.periodType === 'month' ? 'active' : ''}`}
                      onClick={() => setBatchMenuData(prev => ({ ...prev, periodType: 'month' }))}
                    >
                      Month
                    </button>
                  </div>
                </div>

                {/* Date Selection */}
                <div className="form-group">
                  <label>Start Date (Weekdays Only)</label>
                  <input
                    type="date"
                    value={batchMenuData.startDate}
                    onChange={(e) => {
                      const selectedDate = new Date(e.target.value);
                      const dayOfWeek = selectedDate.getDay();
                      if (dayOfWeek === 0 || dayOfWeek === 6) {
                        CustomToast.show('Please select a weekday (Monday-Friday)', 'error');
                        return;
                      }
                      setBatchMenuData(prev => ({ ...prev, startDate: e.target.value }));
                    }}
                    min={formatDateForInput(getNextWeekday(new Date()))}
                  />
                  <small style={{ color: '#666', fontSize: '12px' }}>
                    Menu items are only available Monday-Friday
                  </small>
                </div>

                {/* Menu Items */}
                <div className="form-group">
                  <label>Menu Items (Select 2-4 categories)</label>
                  <div className="menu-items-grid">
                    {batchMenuData.menuItems.map((item, index) => (
                      <div key={item.category} className="menu-item-card">
                        <div className="item-header">
                          <label className="checkbox-label">
                            <input
                              type="checkbox"
                              checked={item.isEnabled}
                              onChange={(e) => {
                                const newMenuItems = [...batchMenuData.menuItems];
                                newMenuItems[index].isEnabled = e.target.checked;
                                setBatchMenuData(prev => ({ ...prev, menuItems: newMenuItems }));
                              }}
                            />
                            <span className="category-name">{item.category.charAt(0).toUpperCase() + item.category.slice(1)}</span>
                          </label>
                        </div>
                        {item.isEnabled && (
                          <div className="item-fields">
                            <input
                              type="text"
                              placeholder="Item Name"
                              value={item.name}
                              onChange={(e) => {
                                const newMenuItems = [...batchMenuData.menuItems];
                                newMenuItems[index].name = e.target.value;
                                setBatchMenuData(prev => ({ ...prev, menuItems: newMenuItems }));
                              }}
                            />
                            <textarea
                              placeholder="Description"
                              value={item.description}
                              onChange={(e) => {
                                const newMenuItems = [...batchMenuData.menuItems];
                                newMenuItems[index].description = e.target.value;
                                setBatchMenuData(prev => ({ ...prev, menuItems: newMenuItems }));
                              }}
                            />

                            {/* Image upload for batch items */}
                            <div className="item-image-upload">
                              <label>Item Photo</label>
                              {item.imageFile ? (
                                <div className="image-preview">
                                  <img
                                    src={URL.createObjectURL(item.imageFile)}
                                    alt="Preview"
                                    style={{ width: '80px', height: '80px', objectFit: 'cover', borderRadius: '6px' }}
                                  />
                                  <button
                                    type="button"
                                    onClick={() => removeBatchItemImage(index)}
                                    className="remove-image-btn"
                                    style={{ fontSize: '12px', padding: '4px 8px' }}
                                  >
                                    Remove
                                  </button>
                                </div>
                              ) : (
                                <div className="image-upload-placeholder">
                                  <input
                                    type="file"
                                    accept="image/*"
                                    onChange={(e) => handleBatchItemImageChange(index, e)}
                                    id={`batch-item-image-${index}`}
                                    style={{ display: 'none' }}
                                  />
                                  <label htmlFor={`batch-item-image-${index}`} className="upload-label">
                                    <FaImage style={{ fontSize: '16px', color: '#5041BC' }} />
                                    <span>Add photo</span>
                                  </label>
                                </div>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>

                <div className="modal-actions">
                  <button type="button" onClick={() => setShowCreateMenuItem(false)}>Cancel</button>
                  <button
                    type="button"
                    onClick={() => handleCreateBatchMenu(batchMenuData)}
                    disabled={batchMenuData.menuItems.filter(item => item.isEnabled).length < 2}
                  >
                    Create Batch Menu
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {showEditOrganization && editingOrganization && (
        <div className="modal-overlay">
          <div className="modal">
            <h2>Edit Organization</h2>
            <form key={editingOrganization.id} onSubmit={(e) => {
              e.preventDefault();
              const formData = new FormData(e.currentTarget);
              const updates = {
                name: formData.get('name') as string,
                address: formData.get('address') as string,
                phone: formData.get('phone') as string,
                email: formData.get('email') as string,
                expirationDate: formData.get('expirationDate') ? new Date(formData.get('expirationDate') as string) : null,
                isActive: formData.get('isActive') === 'true',
                subscriptionPlan: formData.get('subscriptionPlan') as 'premium' | 'enterprise',
                maxStudents: parseInt(formData.get('maxStudents') as string) || 100,
                maxTeachers: parseInt(formData.get('maxTeachers') as string) || 10,
                maxParents: parseInt(formData.get('maxParents') as string) || 200,
                subscriptionStatus: formData.get('subscriptionStatus') as 'active' | 'expired' | 'suspended'
              };

              // Show warning if setting to inactive
              if (editingOrganization.isActive && updates.isActive === false) {
                const userCount = users.filter(user =>
                  user.schoolCode === editingOrganization.code || user.classCode === editingOrganization.code
                ).length;

                if (!window.confirm(`Setting this organization as inactive will log out ${userCount} users and prevent them from logging back in. Are you sure you want to continue?`)) {
                  return;
                }
              }

              handleEditOrganization(editingOrganization.id, updates);
              setShowEditOrganization(false);
              setEditingOrganization(null);
            }}>
              <input
                name="name"
                placeholder="Organization Name"
                defaultValue={editingOrganization.name}
                required
              />
              <input
                name="address"
                placeholder="Address"
                defaultValue={editingOrganization.address}
              />
              <input
                name="phone"
                placeholder="Phone"
                defaultValue={editingOrganization.phone}
              />
              <input
                name="email"
                placeholder="Email"
                defaultValue={editingOrganization.email}
              />
              <input
                name="expirationDate"
                type="date"
                placeholder="Expiration Date"
                defaultValue={editingOrganization.expirationDate ?
                  new Date(editingOrganization.expirationDate.toDate()).toISOString().split('T')[0] :
                  ''
                }
              />
              <select name="isActive" defaultValue={editingOrganization.isActive?.toString() || 'true'}>
                <option value="true">Active</option>
                <option value="false">Inactive</option>
              </select>
              <select name="subscriptionPlan" defaultValue={editingOrganization.subscriptionPlan === 'enterprise' ? 'enterprise' : 'premium'}>
                <option value="premium">Premium</option>
                <option value="enterprise">Enterprise</option>
              </select>
              <select name="subscriptionStatus" defaultValue={editingOrganization.subscriptionStatus || 'active'}>
                <option value="active">Active</option>
                <option value="expired">Expired</option>
                <option value="suspended">Suspended</option>
              </select>
              <input
                name="maxStudents"
                type="number"
                placeholder="Max Students"
                defaultValue={editingOrganization.maxStudents?.toString() || '100'}
              />
              <input
                name="maxTeachers"
                type="number"
                placeholder="Max Teachers"
                defaultValue={editingOrganization.maxTeachers?.toString() || '10'}
              />
              <input
                name="maxParents"
                type="number"
                placeholder="Max Parents"
                defaultValue={editingOrganization.maxParents?.toString() || '200'}
              />

              {(isBusTrackingOrg(editingOrganization.productType) || isLorryOrg(editingOrganization.productType)) && (
                <p style={{ marginTop: 16, fontSize: 13, color: '#666' }}>
                  {isBusTrackingOrg(editingOrganization.productType) ? (
                    <><FaBus style={{ marginRight: 6 }} /> {orgTypeBadgeLabel(editingOrganization.productType)} — roles: {fleetRolesForProductType(editingOrganization.productType)}</>
                  ) : isLorryPersonalOrg(editingOrganization.productType) ? (
                    <><FaTruck style={{ marginRight: 6 }} /> {orgTypeBadgeLabel(editingOrganization.productType)} — roles: {fleetRolesForProductType(editingOrganization.productType)}</>
                  ) : (
                    <><FaTruck style={{ marginRight: 6 }} /> {orgTypeBadgeLabel(editingOrganization.productType)} — roles: {fleetRolesForProductType(editingOrganization.productType)}</>
                  )}
                </p>
              )}
              {editingOrganization.productType === 'LORRY' && (
                <p style={{ marginTop: 8, fontSize: 12, color: '#888' }}>
                  Legacy cargo org (.lorry). New cargo orgs use the .cargo suffix.
                </p>
              )}

              <div className="modal-actions">
                <button type="button" onClick={() => {
                  setShowEditOrganization(false);
                  setEditingOrganization(null);
                }}>Cancel</button>
                <button type="submit">Update Organization</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Fleet admin one-time code modal */}
      {fleetAdminCodeModal && (
        <div className="modal-overlay" onClick={() => setFleetAdminCodeModal(null)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 400 }}>
            <div className="modal-header">
              <h2>One-time signup code</h2>
              <button className="modal-close" onClick={() => setFleetAdminCodeModal(null)}>×</button>
            </div>
            <div className="modal-body">
              <p style={{ fontSize: 14, color: '#64748b', marginBottom: 16 }}>
                Share this code with the person who will create the fleet admin account. They enter it on the login page (Create account).
              </p>
              <p style={{ fontSize: 13, marginBottom: 8 }}><strong>Organization:</strong> {fleetAdminCodeModal.org.name || fleetAdminCodeModal.org.code}</p>
              <div style={{ padding: 16, background: '#f0fdf4', borderRadius: 10, border: '1px solid #bbf7d0', marginBottom: 12 }}>
                <div style={{ fontSize: 11, fontWeight: 600, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 6 }}>Code</div>
                <div style={{ fontSize: 24, fontWeight: 700, fontFamily: 'monospace', letterSpacing: '0.2em', color: '#16a34a' }}>{fleetAdminCodeModal.code}</div>
                <div style={{ fontSize: 11, color: '#64748b', marginTop: 6 }}>Valid for 15 minutes</div>
              </div>
              <button className="add-button" onClick={() => setFleetAdminCodeModal(null)}>Close</button>
            </div>
          </div>
        </div>
      )}

      {/* Classrooms Modal */}
      {showClassroomsModal && selectedOrgForClassrooms && (
        <div className="modal-overlay" onClick={closeClassroomsModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{selectedOrgForClassrooms.name} - Classrooms</h2>
              <button className="modal-close" onClick={closeClassroomsModal}>
                ×
              </button>
            </div>

            <div className="modal-body">
              {loadingClassrooms ? (
                <div className="loading-container">
                  <div className="loading-spinner"></div>
                  <p>Loading classrooms...</p>
                </div>
              ) : organizationClassrooms.length === 0 ? (
                <div className="empty-state">
                  <FaSchool style={{ fontSize: '48px', color: '#ccc', marginBottom: '16px' }} />
                  <h3>No Classrooms Found</h3>
                  <p>This organization doesn't have any classrooms yet.</p>
                </div>
              ) : (
                <div className="classrooms-list">
                  <div className="classrooms-summary">
                    <div className="summary-item">
                      <span className="summary-label">Total Classrooms:</span>
                      <span className="summary-value">{organizationClassrooms.length}</span>
                    </div>
                    <div className="summary-item">
                      <span className="summary-label">Total Students:</span>
                      <span className="summary-value">
                        {organizationClassrooms.reduce((sum, classroom) => sum + classroom.studentCount, 0)}
                      </span>
                    </div>
                    <div className="summary-item">
                      <span className="summary-label">Total Parents:</span>
                      <span className="summary-value">
                        {organizationClassrooms.reduce((sum, classroom) => sum + classroom.parentCount, 0)}
                      </span>
                    </div>
                  </div>

                  {organizationClassrooms.map((classroom) => (
                    <div key={classroom.id} className="classroom-card">
                      <div className="classroom-header">
                        <div className="classroom-info">
                          <h3 className="classroom-name">{classroom.className}</h3>
                          <p className="classroom-code">Class Code: {classroom.classCode}</p>
                        </div>
                        <div className="classroom-status">
                          <span className={`status-badge ${classroom.isActive ? 'active' : 'inactive'}`}>
                            {classroom.isActive ? 'Active' : 'Inactive'}
                          </span>
                        </div>
                      </div>

                      <div className="classroom-teacher">
                        <h4>Teacher</h4>
                        <div className="teacher-info">
                          <div className="teacher-detail">
                            <span className="detail-label">Name:</span>
                            <span className="detail-value">{classroom.teacherName}</span>
                          </div>
                          <div className="teacher-detail">
                            <span className="detail-label">Email:</span>
                            <span className="detail-value">{classroom.teacherEmail}</span>
                          </div>
                          <div className="teacher-detail">
                            <span className="detail-label">Phone:</span>
                            <span className="detail-value">{classroom.teacherPhone}</span>
                          </div>
                        </div>
                      </div>

                      <div className="classroom-stats">
                        <div className="stat-item">
                          <span className="stat-label">Parents:</span>
                          <span className="stat-value">{classroom.parentCount}</span>
                        </div>
                        <div className="stat-item">
                          <span className="stat-label">Students:</span>
                          <span className="stat-value">{classroom.studentCount}</span>
                        </div>
                        <div className="stat-item">
                          <span className="stat-label">Created:</span>
                          <span className="stat-value">
                            {classroom.createdAt ? new Date(classroom.createdAt.toDate()).toLocaleDateString() : 'Unknown'}
                          </span>
                        </div>
                      </div>

                      {classroom.parents && classroom.parents.length > 0 && (
                        <div className="classroom-parents">
                          <h4>Parents & Children</h4>
                          <div className="parents-list">
                            {classroom.parents.map((parent: any, index: number) => (
                              <div key={index} className="parent-item">
                                <div className="parent-info">
                                  <span className="parent-name">{parent.parentName}</span>
                                  <span className="parent-email">{parent.parentEmail}</span>
                                </div>
                                <div className="children-info">
                                  <span className="children-count">
                                    {parent.children ? parent.children.length : 0} children
                                  </span>
                                  {parent.children && parent.children.length > 0 && (
                                    <span className="children-names">
                                      {parent.children.join(', ')}
                                    </span>
                                  )}
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="modal-footer">
              <button className="modal-button secondary" onClick={closeClassroomsModal}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Timetable Modal - Enhanced with Year Group Grouping */}
      {showTimetableModal && (
        <div className="modal-overlay">
          <div className="modal large-modal">
            <div className="modal-header">
              <h2>📅 Timetable Management</h2>
              <button
                className="modal-close"
                onClick={() => setShowTimetableModal(false)}
              >
                ×
              </button>
            </div>
            <p style={{ color: '#8487A2', marginBottom: '20px' }}>Select a classroom or year group to manage timetables:</p>

            {selectedOrganization === 'all' ? (
              <div className="warning-message">
                <p>Please select an organization first to view classrooms.</p>
              </div>
            ) : (
              <div className="classroom-selection">
                {loadingClassrooms ? (
                  <div className="loading-message">
                    <p>Loading classrooms...</p>
                  </div>
                ) : organizationClassrooms.length === 0 ? (
                  <div className="no-classrooms-message">
                    <p>No classrooms found, try again</p>
                  </div>
                ) : (
                  <div className="year-group-container">
                    {/* Group classrooms by year group */}
                    {(() => {
                      // Group classrooms by yearGroup
                      const grouped: { [key: string]: any[] } = {};
                      organizationClassrooms.forEach(classroom => {
                        const yg = classroom.yearGroup || 'Unknown Year Group';
                        if (!grouped[yg]) grouped[yg] = [];
                        grouped[yg].push(classroom);
                      });

                      // Sort year groups: Year 1, Year 2, etc. Unknown last
                      const sortedKeys = Object.keys(grouped).sort((a, b) => {
                        if (a === 'Unknown Year Group') return 1;
                        if (b === 'Unknown Year Group') return -1;
                        const numA = parseInt(a.match(/\d+/)?.[0] || '99');
                        const numB = parseInt(b.match(/\d+/)?.[0] || '99');
                        return numA - numB;
                      });

                      return sortedKeys.map(yearGroup => (
                        <div key={yearGroup} className="year-group-section">
                          <div className="year-group-header">
                            <h3>📚 {yearGroup}</h3>
                            <span className="classroom-count">{grouped[yearGroup].length} classroom{grouped[yearGroup].length !== 1 ? 's' : ''}</span>
                            {yearGroup !== 'Unknown Year Group' && (
                              <button
                                className="upload-yeargroup-btn"
                                onClick={() => {
                                  setTimetableUploadTarget({ type: 'yearGroup', value: yearGroup, classrooms: grouped[yearGroup], name: yearGroup });
                                  setShowTimetableEditor(true);
                                }}
                              >
                                📅 Create Timetable for {yearGroup}
                              </button>
                            )}
                          </div>
                          <div className="classroom-list">
                            {grouped[yearGroup].map((classroom) => (
                              <div key={classroom.id} className="classroom-option">
                                <div className="classroom-details">
                                  <h4>{classroom.className}</h4>
                                  <p>Class Code: {classroom.classCode}</p>
                                  <p>Teacher: {classroom.teacherName}</p>
                                  <p>Students: {classroom.studentCount}</p>
                                </div>
                                <button
                                  className="select-classroom-btn"
                                  onClick={() => {
                                    setTimetableUploadTarget({ type: 'classroom', value: classroom.id, classrooms: [classroom], name: classroom.className });
                                    setShowTimetableEditor(true);
                                  }}
                                >
                                  📅 Create Timetable
                                </button>
                              </div>
                            ))}
                          </div>
                        </div>
                      ));
                    })()}
                  </div>
                )}
              </div>
            )}

            <div className="modal-footer">
              <button
                className="modal-button secondary"
                onClick={() => setShowTimetableModal(false)}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Timetable CSV Upload Modal */}
      {showTimetableUploadModal && timetableUploadTarget && (
        <div className="modal-overlay">
          <div className="modal large-modal">
            <div className="modal-header">
              <h2>📊 Upload Timetable</h2>
              <button
                className="modal-close"
                onClick={() => {
                  setShowTimetableUploadModal(false);
                  setTimetableCSVData([]);
                  setTimetableUploadPreview(null);
                }}
              >
                ×
              </button>
            </div>

            <div className="modal-content">
              <div className="upload-target-info">
                <h3>
                  {timetableUploadTarget.type === 'yearGroup'
                    ? `📚 Uploading for: ${timetableUploadTarget.value}`
                    : `🏫 Uploading for: ${timetableUploadTarget.classrooms[0]?.className}`}
                </h3>
                <p style={{ color: '#8487A2' }}>
                  {timetableUploadTarget.type === 'yearGroup'
                    ? `This timetable will be applied to ${timetableUploadTarget.classrooms.length} classroom(s) in ${timetableUploadTarget.value}`
                    : `This timetable will be applied to ${timetableUploadTarget.classrooms[0]?.className}`}
                </p>
              </div>

              {/* Term Selection */}
              <div className="form-group">
                <label>Select Term *</label>
                <select
                  value={selectedTerm}
                  onChange={(e) => setSelectedTerm(e.target.value)}
                  style={{ width: '100%', padding: '12px', borderRadius: '8px', border: '1px solid #EBE8FF' }}
                >
                  <option value="">Select a term...</option>
                  <option value="Term 1 2025">Term 1 2025</option>
                  <option value="Term 2 2025">Term 2 2025</option>
                  <option value="Term 3 2025">Term 3 2025</option>
                  <option value="Term 1 2026">Term 1 2026</option>
                </select>
              </div>

              {/* Google Sheets Template - Full Guide with Formulas */}
              <div style={{ marginTop: '20px', maxHeight: '600px', overflowY: 'auto', border: '1px solid #EBE8FF', borderRadius: '12px' }}>
                <GoogleSheetsTemplateSection />
              </div>

              {/* CSV File Upload */}
              <div className="csv-upload-section" style={{ marginTop: '20px' }}>
                <input
                  type="file"
                  accept=".csv"
                  id="timetable-csv-upload"
                  style={{ display: 'none' }}
                  onChange={async (e) => {
                    const file = e.target.files?.[0];
                    if (!file) return;

                    try {
                      const text = await file.text();
                      const lines = text.split('\n').filter(line => line.trim());
                      const headers = lines[0].split(',').map(h => h.trim().toLowerCase());

                      const data = lines.slice(1).map(line => {
                        const values = line.split(',').map(v => v.trim());
                        const row: any = {};
                        headers.forEach((h, i) => {
                          row[h] = values[i] || '';
                        });
                        return row;
                      }).filter(row => row.day && row.starttime && row.subject);

                      // Transform to schedule structure
                      const scheduleMap = new Map<string, any[]>();
                      data.forEach(row => {
                        const day = row.day;
                        if (!scheduleMap.has(day)) {
                          scheduleMap.set(day, []);
                        }
                        scheduleMap.get(day)?.push({
                          startTime: row.starttime,
                          endTime: row.endtime,
                          subject: row.subject,
                          teacher: row.teacher || null,
                          room: row.room || null,
                          type: row.type || 'lesson',
                        });
                      });

                      const schedule = Array.from(scheduleMap.entries()).map(([day, slots]) => ({
                        day,
                        slots: slots.sort((a, b) => a.startTime.localeCompare(b.startTime)),
                      }));

                      setTimetableCSVData(data);
                      setTimetableUploadPreview({ schedule, rowCount: data.length });
                      CustomToast.show(`Loaded ${data.length} time slots from CSV`, 'success');
                    } catch (error) {
                      console.error('Error parsing CSV:', error);
                      CustomToast.show('Error parsing CSV file. Please check the format.', 'error');
                    }
                  }}
                />
                <label
                  htmlFor="timetable-csv-upload"
                  className="csv-upload-label"
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    padding: '40px',
                    border: '2px dashed #5041BC',
                    borderRadius: '12px',
                    cursor: 'pointer',
                    background: '#FAFAFA',
                    transition: 'all 0.3s ease'
                  }}
                >
                  <FaCloudUploadAlt size={48} color="#5041BC" />
                  <p style={{ marginTop: '10px', fontWeight: '600', color: '#5041BC' }}>Click to upload CSV file</p>
                  <span style={{ fontSize: '12px', color: '#8487A2' }}>or drag and drop</span>
                </label>
              </div>

              {/* Preview */}
              {timetableUploadPreview && (
                <div className="timetable-preview" style={{ marginTop: '20px' }}>
                  <h4 style={{ color: '#303972', marginBottom: '15px' }}>✅ Preview ({timetableUploadPreview.rowCount} time slots loaded)</h4>
                  <div style={{ maxHeight: '200px', overflowY: 'auto', background: '#F5F3FF', padding: '15px', borderRadius: '8px' }}>
                    {timetableUploadPreview.schedule.map((daySchedule: any) => (
                      <div key={daySchedule.day} style={{ marginBottom: '10px' }}>
                        <strong style={{ color: '#5041BC' }}>{daySchedule.day}</strong>
                        <ul style={{ marginLeft: '20px', fontSize: '13px' }}>
                          {daySchedule.slots.slice(0, 3).map((slot: any, idx: number) => (
                            <li key={idx}>{slot.startTime}-{slot.endTime}: {slot.subject} {slot.teacher ? `(${slot.teacher})` : ''}</li>
                          ))}
                          {daySchedule.slots.length > 3 && <li>...and {daySchedule.slots.length - 3} more</li>}
                        </ul>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <div className="modal-footer">
              <button
                className="modal-button secondary"
                onClick={() => {
                  setShowTimetableUploadModal(false);
                  setTimetableCSVData([]);
                  setTimetableUploadPreview(null);
                }}
              >
                Cancel
              </button>
              <button
                className="modal-button primary"
                disabled={!timetableUploadPreview || !selectedTerm || isUploadingTimetable}
                onClick={async () => {
                  if (!timetableUploadPreview || !selectedTerm || !timetableUploadTarget) return;

                  setIsUploadingTimetable(true);
                  try {
                    const batch = writeBatch(db);

                    for (const classroom of timetableUploadTarget.classrooms) {
                      // Archive existing active timetables for this classroom
                      const existingQuery = query(
                        collection(db, 'timetables'),
                        where('classId', '==', classroom.id),
                        where('status', '==', 'active')
                      );
                      const existingDocs = await getDocs(existingQuery);
                      existingDocs.docs.forEach(doc => {
                        batch.update(doc.ref, { status: 'archived', archivedAt: serverTimestamp() });
                      });

                      // Create new timetable
                      const newTimetableRef = doc(collection(db, 'timetables'));
                      batch.set(newTimetableRef, {
                        classId: classroom.id,
                        classCode: classroom.classCode,
                        className: classroom.className,
                        yearGroup: classroom.yearGroup || 'Unknown',
                        organizationCode: classroom.organizationCode || selectedOrganization,
                        term: selectedTerm,
                        status: 'active',
                        schedule: timetableUploadPreview.schedule,
                        uploadedAt: serverTimestamp(),
                        uploadedBy: currentUser?.uid || 'admin',
                        uploadedByName: currentUser?.displayName || 'Admin',
                      });
                    }

                    await batch.commit();

                    // Log the operation
                    logOperation({
                      type: 'create',
                      contentType: 'timetable',
                      contentId: timetableUploadTarget.value,
                      contentTitle: timetableUploadTarget.type === 'yearGroup'
                        ? `Timetable for ${timetableUploadTarget.value}`
                        : `Timetable for ${timetableUploadTarget.classrooms[0]?.className}`,
                      organizationCode: selectedOrganization,
                      details: {
                        term: selectedTerm,
                        classroomCount: timetableUploadTarget.classrooms.length,
                        slotCount: timetableUploadPreview.rowCount,
                      }
                    });

                    CustomToast.show(`Timetable uploaded to ${timetableUploadTarget.classrooms.length} classroom(s)!`, 'success');
                    setShowTimetableUploadModal(false);
                    setTimetableCSVData([]);
                    setTimetableUploadPreview(null);
                    setSelectedTerm('');
                  } catch (error) {
                    console.error('Error uploading timetable:', error);
                    CustomToast.show('Failed to upload timetable', 'error');
                  } finally {
                    setIsUploadingTimetable(false);
                  }
                }}
              >
                {isUploadingTimetable ? 'Uploading...' : `Upload to ${timetableUploadTarget.classrooms.length} Classroom(s)`}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Timetable Management Modal */}
      {showTimetableManagement && selectedClassroom && (
        <div className="modal-overlay">
          <div className="modal large-modal">
            <div className="modal-header">
              <h2>Timetable Management - {selectedClassroom.className}</h2>
              <button
                className="modal-close"
                onClick={() => setShowTimetableManagement(false)}
              >
                ×
              </button>
            </div>

            <div className="modal-content">
              <div className="classroom-info">
                <h3>Class Information</h3>
                <p><strong>Class Code:</strong> {selectedClassroom.classCode}</p>
                <p><strong>Teacher:</strong> {selectedClassroom.teacherName}</p>
                <p><strong>Students:</strong> {selectedClassroom.studentCount}</p>
                <p><strong>Parents:</strong> {selectedClassroom.parentCount}</p>
              </div>

              <div className="events-section">
                <h3>Class Schedule</h3>
                {loadingEvents ? (
                  <div className="loading-message">
                    <p>Loading events...</p>
                  </div>
                ) : classroomEvents.length === 0 ? (
                  <div className="no-events">
                    <p>No events scheduled for this class.</p>
                  </div>
                ) : (
                  <div className="events-list">
                    {classroomEvents.map((event) => (
                      <div key={event.id} className="event-item">
                        <div className="event-header">
                          <h4>{event.title}</h4>

                        </div>
                        <div className="event-details">
                          <p><strong>Time:</strong> {event.startTime?.toLocaleString()} - {event.endTime?.toLocaleString()}</p>
                          {event.description && <p><strong>Description:</strong> {event.description}</p>}
                          {event.room && <p><strong>Room:</strong> {event.room}</p>}
                          {event.location && <p><strong>Location:</strong> {event.location}</p>}
                        </div>
                        <div className="event-actions">
                          <button
                            className="btn-small secondary"
                            onClick={() => editEvent(event)}
                          >
                            Edit
                          </button>
                          <button
                            className="btn-small danger"
                            onClick={() => deleteEvent(event.id)}
                          >
                            Delete
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="add-event-section">
                <h3>Add New Event</h3>
                <button
                  className="btn-primary"
                  onClick={() => {
                    setShowAddEventModal(true);
                    resetNewEvent();
                  }}
                >
                  Add New Event
                </button>
              </div>
            </div>

            <div className="modal-footer">
              <button
                className="modal-button secondary"
                onClick={() => setShowTimetableManagement(false)}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add Event Modal */}
      {showAddEventModal && selectedClassroom && (
        <div className="modal-overlay">
          <div className="modal large-modal">
            <div className="modal-header">
              <h2>Add New Event - {selectedClassroom.className}</h2>
              <button
                className="modal-close"
                onClick={() => setShowAddEventModal(false)}
              >
                ×
              </button>
            </div>

            <div className="modal-content">
              {/* Mode Selection */}
              <div className="mode-selection">
                <h3>Select Mode</h3>
                <div className="mode-buttons">
                  <button
                    className={`mode-btn ${addEventMode === 'single' ? 'active' : ''}`}
                    onClick={() => setAddEventMode('single')}
                  >
                    📅 Single Event
                  </button>
                  <button
                    className={`mode-btn ${addEventMode === 'bulk' ? 'active' : ''}`}
                    onClick={() => setAddEventMode('bulk')}
                  >
                    📊 Bulk Schedule
                  </button>
                  <button
                    className={`mode-btn ${addEventMode === 'template' ? 'active' : ''}`}
                    onClick={() => setAddEventMode('template')}
                  >
                    📋 Template
                  </button>
                </div>
              </div>

              {/* Event Details Form */}
              <div className="event-form">
                <div className="form-row">
                  <div className="form-group">
                    <label>Event Title *</label>
                    <input
                      type="text"
                      value={newEvent.title}
                      onChange={(e) => setNewEvent(prev => ({ ...prev, title: e.target.value }))}
                      placeholder="e.g., Math Class, Science Lab"
                    />
                  </div>

                </div>

                <div className="form-group">
                  <label>Description</label>
                  <textarea
                    value={newEvent.description}
                    onChange={(e) => setNewEvent(prev => ({ ...prev, description: e.target.value }))}
                    placeholder="Event description..."
                    rows={3}
                  />
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label>Start Time</label>
                    <input
                      type="time"
                      value={newEvent.startTime}
                      onChange={(e) => setNewEvent(prev => ({ ...prev, startTime: e.target.value }))}
                    />
                  </div>
                  <div className="form-group">
                    <label>End Time</label>
                    <input
                      type="time"
                      value={newEvent.endTime}
                      onChange={(e) => setNewEvent(prev => ({ ...prev, endTime: e.target.value }))}
                    />
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label>Room</label>
                    <input
                      type="text"
                      value={newEvent.room}
                      onChange={(e) => setNewEvent(prev => ({ ...prev, room: e.target.value }))}
                      placeholder="e.g., Room 101"
                    />
                  </div>
                  <div className="form-group">
                    <label>Location</label>
                    <input
                      type="text"
                      value={newEvent.location}
                      onChange={(e) => setNewEvent(prev => ({ ...prev, location: e.target.value }))}
                      placeholder="e.g., Main Building"
                    />
                  </div>
                </div>

                {/* Single Event Mode */}
                {addEventMode === 'single' && (
                  <div className="form-group">
                    <label>Date</label>
                    <input
                      type="date"
                      value={newEvent.startDate}
                      onChange={(e) => setNewEvent(prev => ({
                        ...prev,
                        startDate: e.target.value,
                        endDate: e.target.value
                      }))}
                    />
                  </div>
                )}

                {/* Bulk Schedule Mode */}
                {addEventMode === 'bulk' && (
                  <div className="bulk-schedule-section">
                    <h4>Bulk Schedule Settings</h4>

                    <div className="form-row">
                      <div className="form-group">
                        <label>Start Date</label>
                        <input
                          type="date"
                          value={newEvent.startDate}
                          onChange={(e) => setNewEvent(prev => ({ ...prev, startDate: e.target.value }))}
                        />
                      </div>
                      <div className="form-group">
                        <label>End Date</label>
                        <input
                          type="date"
                          value={newEvent.endDate}
                          onChange={(e) => setNewEvent(prev => ({ ...prev, endDate: e.target.value }))}
                        />
                      </div>
                    </div>

                    <div className="form-group">
                      <label>Repeat Pattern</label>
                      <select
                        value={newEvent.repeatPattern}
                        onChange={(e) => setNewEvent(prev => ({
                          ...prev,
                          repeatPattern: e.target.value as 'daily' | 'weekly' | 'monthly'
                        }))}
                      >
                        <option value="daily">Daily</option>
                        <option value="weekly">Weekly</option>
                        <option value="monthly">Monthly</option>
                      </select>
                    </div>

                    <div className="form-group">
                      <label>Select Days</label>
                      <div className="day-selector">
                        {['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'].map(day => (
                          <button
                            key={day}
                            type="button"
                            className={`day-btn ${newEvent.selectedDays.includes(day) ? 'selected' : ''}`}
                            onClick={() => toggleDaySelection(day)}
                          >
                            {day.charAt(0).toUpperCase() + day.slice(1, 3)}
                          </button>
                        ))}
                      </div>
                    </div>

                    <div className="bulk-preview">
                      <h5>Preview</h5>
                      <p>This will create events for:</p>
                      <ul>
                        {newEvent.selectedDays.map(day => (
                          <li key={day}>
                            {day.charAt(0).toUpperCase() + day.slice(1)}s from {newEvent.startDate} to {newEvent.endDate}
                          </li>
                        ))}
                      </ul>
                      {newEvent.selectedDays.length > 0 && newEvent.startDate && newEvent.endDate && (
                        <div className="preview-count">
                          <strong>Estimated events to create: {(() => {
                            const startDate = new Date(newEvent.startDate);
                            const endDate = new Date(newEvent.endDate);
                            const dates = generateDatesForPattern(startDate, endDate, newEvent.selectedDays, newEvent.repeatPattern);
                            return dates.length;
                          })()}</strong>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Template Mode */}
                {addEventMode === 'template' && (
                  <div className="template-section">
                    <h4>Save as Template</h4>
                    <div className="form-group">
                      <label>Template Name</label>
                      <input
                        type="text"
                        value={newEvent.templateName}
                        onChange={(e) => setNewEvent(prev => ({ ...prev, templateName: e.target.value }))}
                        placeholder="e.g., Standard School Day"
                      />
                    </div>
                    <p className="template-info">
                      Save this event as a template for quick reuse. Templates can be applied to any classroom.
                    </p>
                  </div>
                )}
              </div>
            </div>

            <div className="modal-footer">
              <button
                className="modal-button secondary"
                onClick={() => setShowAddEventModal(false)}
              >
                Cancel
              </button>
              <button
                className="modal-button primary"
                disabled={isCreatingEvents}
                onClick={() => {
                  if (addEventMode === 'single') {
                    createSingleEvent();
                  } else if (addEventMode === 'bulk') {
                    createBulkEvents();
                  } else {
                    // TODO: Implement template saving
                    CustomToast.show('Template functionality coming soon!', 'info');
                  }
                }}
              >
                {isCreatingEvents ? (
                  <>
                    <span className="loading-spinner"></span>
                    {addEventMode === 'single' ? 'Creating Event...' :
                      addEventMode === 'bulk' ? 'Creating Events...' : 'Saving Template...'}
                  </>
                ) : (
                  addEventMode === 'single' ? 'Create Event' :
                    addEventMode === 'bulk' ? 'Create Bulk Events' : 'Save Template'
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Diary Classroom Selection Modal */}
      {showDiaryClassroomSelection && (
        <div className="modal-overlay">
          <div className="modal">
            <h2>Select Classroom for Diary Entry</h2>
            <div className="classroom-list">
              {isLoadingClasses ? (
                <div style={{ textAlign: 'center', padding: '40px' }}>
                  <div className="loading-spinner"></div>
                  <p style={{ marginTop: '20px', color: '#666' }}>Loading classrooms...</p>
                </div>
              ) : organizationClassrooms.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
                  <p>No classrooms found for this organization.</p>
                  <p style={{ fontSize: '14px', marginTop: '10px' }}>
                    Make sure the organization has active classrooms.
                  </p>
                </div>
              ) : (
                organizationClassrooms.map((classroom) => (
                  <div key={classroom.id} className="classroom-item">
                    <div className="classroom-info">
                      <h3>{classroom.className}</h3>
                      <p>Teacher: {classroom.teacherName}</p>
                      <p>{classroom.parentCount} parents • {classroom.studentCount} students</p>
                    </div>
                    <button
                      className="select-button"
                      onClick={() => {
                        setShowDiaryClassroomSelection(false);
                        setShowCreateDiaryEntry(true);
                        // Store selected classroom info for diary entry creation
                        localStorage.setItem('selectedDiaryClassroom', JSON.stringify(classroom));
                      }}
                    >
                      Select
                    </button>
                  </div>
                ))
              )}
            </div>
            <div className="modal-actions">
              <button onClick={() => setShowDiaryClassroomSelection(false)}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* Create Diary Entry Modal */}
      {showCreateDiaryEntry && (
        <div className="modal-overlay">
          <div className="modal">
            <h2>Create Diary Entry</h2>
            <form onSubmit={(e) => {
              e.preventDefault();
              const formData = new FormData(e.currentTarget);
              const selectedClassroom = JSON.parse(localStorage.getItem('selectedDiaryClassroom') || '{}');
              handleCreateDiaryEntry({
                content: formData.get('content') as string,
                mood: formData.get('mood') as string,
                type: formData.get('type') as string,
                studentName: formData.get('studentName') as string || undefined,
                classroomId: selectedClassroom.id,
                classroomName: selectedClassroom.className,
              });
            }}>
              <div className="selected-classroom">
                <strong>Selected Classroom:</strong> {JSON.parse(localStorage.getItem('selectedDiaryClassroom') || '{}').className}
              </div>

              <select name="type" required onChange={(e) => {
                const studentNameField = document.getElementById('studentNameField');
                if (studentNameField) {
                  studentNameField.style.display = e.target.value === 'personal' ? 'block' : 'none';
                }
              }}>
                <option value="">Select Entry Type</option>
                <option value="general">General Note</option>
                <option value="personal">Personal Note</option>
              </select>

              <div id="studentNameField" style={{ display: 'none' }}>
                <input name="studentName" placeholder="Student Name (for personal notes)" />
              </div>

              <textarea name="content" placeholder="Diary entry content..." required rows={4} />

              <select name="mood" required>
                <option value="">Select Mood</option>
                <option value="happy">Happy 😊</option>
                <option value="tired">Tired 😴</option>
                <option value="excited">Excited 🎉</option>
                <option value="nap_time">Nap Time 💤</option>
                <option value="energetic">Energetic ⚡</option>
                <option value="calm">Calm 😌</option>
              </select>

              <div className="modal-actions">
                <button type="button" onClick={() => {
                  setShowCreateDiaryEntry(false);
                  localStorage.removeItem('selectedDiaryClassroom');
                }}>Cancel</button>
                <button type="submit">Create Entry</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Holiday Management Modal */}
      {showAddHolidayModal && (
        <div className="modal-overlay">
          <div className="modal">
            <div className="modal-header">
              <h2>🎉 Holiday & Break Management</h2>
              <button
                className="modal-close"
                onClick={() => setShowAddHolidayModal(false)}
              >
                ×
              </button>
            </div>

            <div className="modal-content">
              {/* Existing Holidays */}
              <div className="holiday-list">
                <h3 style={{ marginBottom: '15px', color: '#303972' }}>Current Holidays</h3>
                {holidays.length === 0 ? (
                  <p style={{ color: '#8487A2', fontStyle: 'italic' }}>No holidays configured yet.</p>
                ) : (
                  holidays.map((holiday, index) => (
                    <div key={index} className="holiday-card">
                      <div className="holiday-date">{holiday.date}</div>
                      <div className="holiday-info">
                        <h4>{holiday.name}</h4>
                        <span className="holiday-type">{holiday.type.replace('_', ' ')}</span>
                      </div>
                      <button
                        className="action-btn delete"
                        onClick={async () => {
                          const updatedHolidays = holidays.filter((_, i) => i !== index);
                          setHolidays(updatedHolidays);

                          // Save to Firebase
                          try {
                            const calendarRef = doc(db, 'academic_calendar', `${selectedOrganization}_${new Date().getFullYear()}`);
                            await setDoc(calendarRef, {
                              organizationCode: selectedOrganization,
                              year: new Date().getFullYear(),
                              holidays: updatedHolidays,
                              updatedAt: serverTimestamp(),
                            }, { merge: true });
                            CustomToast.show('Holiday removed', 'success');
                          } catch (error) {
                            console.error('Error removing holiday:', error);
                          }
                        }}
                      >
                        <FaTrash />
                      </button>
                    </div>
                  ))
                )}
              </div>

              {/* Add New Holiday */}
              <div style={{ marginTop: '24px', padding: '20px', background: '#F5F3FF', borderRadius: '12px' }}>
                <h4 style={{ color: '#5041BC', marginBottom: '15px' }}>Add New Holiday</h4>
                <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
                  <input
                    type="date"
                    value={newHoliday.date}
                    onChange={(e) => setNewHoliday(prev => ({ ...prev, date: e.target.value }))}
                    style={{ flex: 1, minWidth: '150px', padding: '10px', borderRadius: '8px', border: '1px solid #EBE8FF' }}
                  />
                  <input
                    type="text"
                    placeholder="Holiday Name (e.g., Madaraka Day)"
                    value={newHoliday.name}
                    onChange={(e) => setNewHoliday(prev => ({ ...prev, name: e.target.value }))}
                    style={{ flex: 2, minWidth: '200px', padding: '10px', borderRadius: '8px', border: '1px solid #EBE8FF' }}
                  />
                  <select
                    value={newHoliday.type}
                    onChange={(e) => setNewHoliday(prev => ({ ...prev, type: e.target.value }))}
                    style={{ flex: 1, minWidth: '150px', padding: '10px', borderRadius: '8px', border: '1px solid #EBE8FF' }}
                  >
                    <option value="public_holiday">Public Holiday</option>
                    <option value="school_break">School Break</option>
                    <option value="exam_period">Exam Period</option>
                    <option value="half_day">Half Day</option>
                  </select>
                </div>
                <button
                  className="modal-button primary"
                  style={{ marginTop: '15px' }}
                  onClick={async () => {
                    if (!newHoliday.date || !newHoliday.name) {
                      CustomToast.show('Please fill in date and name', 'error');
                      return;
                    }

                    const updatedHolidays = [...holidays, newHoliday];
                    setHolidays(updatedHolidays);
                    setNewHoliday({ date: '', name: '', type: 'public_holiday' });

                    // Save to Firebase
                    try {
                      const calendarRef = doc(db, 'academic_calendar', `${selectedOrganization}_${new Date().getFullYear()}`);
                      await setDoc(calendarRef, {
                        organizationCode: selectedOrganization,
                        year: new Date().getFullYear(),
                        holidays: updatedHolidays,
                        updatedAt: serverTimestamp(),
                      }, { merge: true });
                      CustomToast.show('Holiday added successfully!', 'success');
                    } catch (error) {
                      console.error('Error saving holiday:', error);
                      CustomToast.show('Failed to save holiday', 'error');
                    }
                  }}
                >
                  <FaPlus /> Add Holiday
                </button>
              </div>
            </div>

            <div className="modal-footer">
              <button
                className="modal-button secondary"
                onClick={() => setShowAddHolidayModal(false)}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Weekly Summary Modal */}
      {showWeeklySummaryModal && (
        <div className="modal-overlay">
          <div className="modal large-modal">
            <div className="modal-header">
              <h2>📧 Weekly Summary Generator</h2>
              <button
                className="modal-close"
                onClick={() => setShowWeeklySummaryModal(false)}
              >
                ×
              </button>
            </div>

            <div className="modal-content">
              <div style={{ background: '#F5F3FF', padding: '20px', borderRadius: '12px', marginBottom: '20px' }}>
                <h3 style={{ color: '#5041BC', marginBottom: '10px' }}>ℹ️ How Weekly Summaries Work</h3>
                <ul style={{ color: '#303972', fontSize: '14px', lineHeight: '1.8' }}>
                  <li><strong>Friday & Saturday at 6 PM:</strong> Summaries are sent to all parents</li>
                  <li><strong>Per-Child:</strong> Parents with multiple children receive separate summaries</li>
                  <li><strong>Content:</strong> Timetable, upcoming events, tests, homework, key activities</li>
                  <li><strong>Classroom-Specific:</strong> Each child gets their classroom's summary</li>
                </ul>
              </div>

              {/* Generate Now Button */}
              <div style={{ textAlign: 'center', marginBottom: '20px' }}>
                <button
                  className="modal-button primary"
                  style={{ padding: '16px 32px', fontSize: '16px' }}
                  disabled={isGeneratingWeeklySummary || selectedOrganization === 'all'}
                  onClick={async () => {
                    if (selectedOrganization === 'all') {
                      CustomToast.show('Please select an organization first', 'error');
                      return;
                    }

                    setIsGeneratingWeeklySummary(true);
                    try {
                      // Get current week dates
                      const now = new Date();
                      const weekStart = new Date(now);
                      weekStart.setDate(now.getDate() - now.getDay() + 1); // Monday
                      const weekEnd = new Date(weekStart);
                      weekEnd.setDate(weekStart.getDate() + 4); // Friday

                      const weekStartStr = weekStart.toISOString().split('T')[0];
                      const weekEndStr = weekEnd.toISOString().split('T')[0];

                      // For each classroom in the organization
                      for (const classroom of organizationClassrooms) {
                        // Get timetable for this classroom
                        const timetableQuery = query(
                          collection(db, 'timetables'),
                          where('classId', '==', classroom.id),
                          where('status', '==', 'active'),
                          limit(1)
                        );
                        const timetableSnap = await getDocs(timetableQuery);
                        const timetable = timetableSnap.docs[0]?.data() || null;

                        // Get events for this week
                        const eventsQuery = query(
                          collection(db, 'calendar_events'),
                          where('classroomId', '==', classroom.id)
                        );
                        const eventsSnap = await getDocs(eventsQuery);
                        const weekEvents = eventsSnap.docs.map(d => d.data()).filter((e: any) => {
                          const eventDate = e.startTime?.toDate?.()?.toISOString?.()?.split('T')[0];
                          return eventDate && eventDate >= weekStartStr && eventDate <= weekEndStr;
                        });

                        // Create weekly summary document
                        const summaryRef = doc(collection(db, 'weekly_summaries'));
                        await setDoc(summaryRef, {
                          organizationCode: selectedOrganization,
                          classId: classroom.id,
                          className: classroom.className,
                          yearGroup: classroom.yearGroup || 'Unknown',
                          weekStarting: weekStartStr,
                          weekEnding: weekEndStr,
                          generatedAt: serverTimestamp(),
                          generatedBy: currentUser?.uid || 'admin',
                          content: {
                            timetable: timetable?.schedule || [],
                            keyEvents: weekEvents.map((e: any) => ({
                              date: e.startTime?.toDate?.()?.toISOString?.()?.split('T')[0],
                              title: e.title,
                              description: e.description || '',
                            })),
                            holidays: holidays.filter(h => h.date >= weekStartStr && h.date <= weekEndStr),
                          },
                          status: 'generated',
                        });

                        // Create notification for each parent in this classroom
                        for (const parent of classroom.parents || []) {
                          const notificationRef = doc(collection(db, 'notifications'));
                          await setDoc(notificationRef, {
                            recipientId: parent.parentId,
                            type: 'weekly_summary',
                            title: `📅 Weekly Summary - ${classroom.className}`,
                            body: `Your child's weekly summary for ${weekStartStr} to ${weekEndStr} is ready.`,
                            timestamp: serverTimestamp(),
                            read: false,
                            data: {
                              classId: classroom.id,
                              className: classroom.className,
                              weekStarting: weekStartStr,
                              weekEnding: weekEndStr,
                            }
                          });
                        }
                      }

                      CustomToast.show(`Weekly summaries generated for ${organizationClassrooms.length} classroom(s)!`, 'success');
                    } catch (error) {
                      console.error('Error generating weekly summaries:', error);
                      CustomToast.show('Failed to generate weekly summaries', 'error');
                    } finally {
                      setIsGeneratingWeeklySummary(false);
                    }
                  }}
                >
                  {isGeneratingWeeklySummary ? 'Generating...' : '🚀 Generate & Send Weekly Summaries Now'}
                </button>
                {selectedOrganization === 'all' && (
                  <p style={{ color: '#EA8F95', marginTop: '10px', fontSize: '13px' }}>
                    Please select an organization first
                  </p>
                )}
              </div>

              {/* Preview Section */}
              <div className="weekly-summary-preview">
                <h4 style={{ color: '#303972', marginBottom: '15px' }}>📋 Summary Preview (Example)</h4>
                <div className="summary-section">
                  <h4>📅 This Week's Timetable</h4>
                  <p style={{ fontSize: '13px', color: '#8487A2' }}>Full timetable for Monday-Friday</p>
                </div>
                <div className="summary-section">
                  <h4>📌 Key Events</h4>
                  <ul>
                    <li>Wednesday: Sports Day (10:00 AM)</li>
                    <li>Friday: Math Test (Period 3)</li>
                  </ul>
                </div>
                <div className="summary-section">
                  <h4>🎉 Holidays</h4>
                  <ul>
                    <li>No school holidays this week</li>
                  </ul>
                </div>
              </div>
            </div>

            <div className="modal-footer">
              <button
                className="modal-button secondary"
                onClick={() => setShowWeeklySummaryModal(false)}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Week/Term Selection Modal for Timetable Editor */}
      {showTimetableEditor && timetableUploadTarget && !selectedTerm && (
        <div className="modal-overlay">
          <div className="modal" style={{ maxWidth: '500px' }}>
            <div className="modal-header">
              <h2>📅 Select Week & Term</h2>
              <button
                className="modal-close"
                onClick={() => {
                  setShowTimetableEditor(false);
                  setTimetableUploadTarget(null);
                }}
              >
                ×
              </button>
            </div>

            <div className="modal-content">
              <p style={{ color: '#8487A2', marginBottom: '20px' }}>
                Creating timetable for: <strong>{timetableUploadTarget.name || timetableUploadTarget.value}</strong>
              </p>

              <div className="form-group">
                <label>Term *</label>
                <select
                  value={selectedTerm}
                  onChange={(e) => setSelectedTerm(e.target.value)}
                  style={{ width: '100%', padding: '12px', borderRadius: '8px', border: '1px solid #EBE8FF' }}
                >
                  <option value="">Select a term...</option>
                  <option value="Term 1 2025">Term 1 2025</option>
                  <option value="Term 2 2025">Term 2 2025</option>
                  <option value="Term 3 2025">Term 3 2025</option>
                  <option value="Term 1 2026">Term 1 2026</option>
                  <option value="Term 2 2026">Term 2 2026</option>
                  <option value="Term 3 2026">Term 3 2026</option>
                </select>
              </div>

              <div className="form-group">
                <label>Week Number *</label>
                <select
                  value={selectedWeekNumber}
                  onChange={(e) => setSelectedWeekNumber(parseInt(e.target.value))}
                  style={{ width: '100%', padding: '12px', borderRadius: '8px', border: '1px solid #EBE8FF' }}
                >
                  {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map(week => (
                    <option key={week} value={week}>Week {week}</option>
                  ))}
                </select>
              </div>
            </div>

            <div className="modal-footer">
              <button
                className="modal-button secondary"
                onClick={() => {
                  setShowTimetableEditor(false);
                  setTimetableUploadTarget(null);
                }}
              >
                Cancel
              </button>
              <button
                className="modal-button primary"
                disabled={!selectedTerm}
                onClick={() => {
                  // Proceed to editor - selectedTerm is now set
                  // Force re-render by updating state
                  setSelectedTerm(selectedTerm || 'Term 1 2026');
                }}
              >
                Open Editor
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Visual Timetable Editor */}
      {showTimetableEditor && timetableUploadTarget && selectedTerm && (
        <TimetableEditor
          targetType={timetableUploadTarget.type}
          targetId={timetableUploadTarget.value}
          targetName={timetableUploadTarget.name || timetableUploadTarget.value}
          term={selectedTerm}
          weekNumber={selectedWeekNumber}
          weekStartDate={(() => {
            // Calculate week start date based on term and week number
            const termDates: Record<string, string> = {
              'Term 1 2025': '2025-01-06',
              'Term 2 2025': '2025-04-28',
              'Term 3 2025': '2025-09-01',
              'Term 1 2026': '2026-01-05',
              'Term 2 2026': '2026-04-27',
              'Term 3 2026': '2026-08-31',
            };
            const termStart = new Date(termDates[selectedTerm] || '2026-01-05');
            termStart.setDate(termStart.getDate() + (selectedWeekNumber - 1) * 7);
            return termStart.toISOString().split('T')[0];
          })()}
          onClose={() => {
            setShowTimetableEditor(false);
            setTimetableUploadTarget(null);
            setSelectedTerm('');
            setSelectedWeekNumber(1);
          }}
          onSave={async () => {
            // Optional: refresh data or show success
          }}
        />
      )}

      {/* Full-screen Timetable Management - Now a full page */}
      {showTimetableManagementScreen && selectedOrganization !== 'all' && (
        <div className="timetable-management-fullpage">
          <TimetableManagementScreen
            organizationCode={selectedOrganization}
            onClose={() => setShowTimetableManagementScreen(false)}
          />
        </div>
      )}
    </div>
    </div>
  );
};

export default Dashboard;
